import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Expandable } from "@/components/Expandable";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { toast } from "sonner";
import { createPortal } from "react-dom";
import { LearningHero } from "@/components/LearningCatalog";
import AssignedFocus from "@/components/AssignedFocus";
import ProgressRing from "@/components/ProgressRing";
import { GripVertical } from "lucide-react";
import "./LearningHub.css";
import {
    BookOpen,
    Plus,
    Trash2,
    Pencil,
    MoreHorizontal,
    Paperclip,
    Link as LinkIcon,
    PlayCircle,
    GraduationCap,
    FileText,
    Headphones,
    Book,
    Globe,
    ExternalLink,
    UserPlus,
    X,
    Save,
    Loader2,
    Upload as UploadIcon,
    UploadCloud,
    FolderPlus,
    Folder as FolderIcon,
    ChevronDown,
    Edit3,
    CheckCircle2,
    Circle,
    Activity,
    SkipForward,
    Users,
    ChevronLeft,
    ChevronRight,
    Check,
    Megaphone,
    Code,
    Compass,
    Lightbulb,
    Rocket,
    Target,
    Brain,
    Palette,
    Briefcase,
    Layers,
    Sparkles,
    Search as SearchIcon,
    Filter,
    Send,
    Link2,
    SmilePlus,
    MessageCircle,
    FolderInput,
    Heart,
    Trophy,
    LineChart,
} from "lucide-react";

export const TYPE_ICON = {
    video: PlayCircle,
    course: GraduationCap,
    article: FileText,
    doc: FileText,
    podcast: Headphones,
    book: Book,
    other: Globe,
};
const TYPE_LABEL = {
    video: "Video",
    course: "Course",
    article: "Article",
    doc: "Doc",
    podcast: "Podcast",
    book: "Book",
    other: "Link",
};
const TYPES = ["article", "video", "course", "doc", "podcast", "book", "other"];

// Reaction emoji map — short keys map to glyph + label so the API stays
// stable and we can swap emoji rendering later without a migration.
// Order intentionally puts the everyday reactions (like/dislike/love) first.
const REACTIONS = [
    { key: "like", glyph: "👍", label: "Like" },
    { key: "dislike", glyph: "👎", label: "Dislike" },
    { key: "love", glyph: "❤️", label: "Love" },
    { key: "fire", glyph: "🔥", label: "Fire" },
    { key: "useful", glyph: "💡", label: "Useful" },
    { key: "gamechanger", glyph: "🎯", label: "Game-changer" },
    { key: "funny", glyph: "😂", label: "Funny" },
    { key: "mindblown", glyph: "🤯", label: "Mind-blown" },
];

// Pre-computed empty count map so its identity is stable across renders
// (otherwise the ReactionBar's effect dep would change every render).
const EMPTY_REACTION_COUNTS = Object.fromEntries(REACTIONS.map((r) => [r.key, 0]));

// Short date label for read receipts ("Mar 4", "Today", "Yesterday").
const formatShortDate = (iso) => {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        const now = new Date();
        const sameDay = d.toDateString() === now.toDateString();
        if (sameDay) return "Today";
        const y = new Date(now); y.setDate(now.getDate() - 1);
        if (d.toDateString() === y.toDateString()) return "Yesterday";
        return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch { return null; }
};

// ---------- ReactionBar — emoji toggle row ----------
// Visually clean by default: only reactions that have at least 1 vote are
// shown inline as pills (with count). Adding a new reaction goes through a
// "+ smiley" picker button which opens a small popover with all 8 glyphs.
// Active state (the user has reacted) gets a teal ring; optimistic updates
// with rollback on failure.
const ReactionBar = ({ resourceId, reactions, myReactions, onChanged }) => {
    const [counts, setCounts] = useState(reactions || EMPTY_REACTION_COUNTS);
    const [mine, setMine] = useState(new Set(myReactions || []));
    const [pickerOpen, setPickerOpen] = useState(false);
    const pickerRef = useRef(null);
    useEffect(() => { setCounts(reactions || EMPTY_REACTION_COUNTS); }, [reactions]);
    useEffect(() => { setMine(new Set(myReactions || [])); }, [myReactions]);
    // Click-outside to close the picker.
    useEffect(() => {
        if (!pickerOpen) return undefined;
        const onDoc = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [pickerOpen]);

    const toggle = async (e, key) => {
        e.preventDefault();
        e.stopPropagation();
        const wasMine = mine.has(key);
        // Optimistic
        const nextCounts = { ...counts, [key]: (counts[key] || 0) + (wasMine ? -1 : 1) };
        const nextMine = new Set(mine);
        if (wasMine) nextMine.delete(key); else nextMine.add(key);
        setCounts(nextCounts);
        setMine(nextMine);
        setPickerOpen(false);
        try {
            const r = await api.post(`/learning/resources/${resourceId}/react`, { emoji: key });
            setCounts(r.data?.reactions || nextCounts);
            setMine(new Set(r.data?.my_reactions || Array.from(nextMine)));
            onChanged?.(r.data);
        } catch (err) {
            // Rollback
            setCounts(counts);
            setMine(mine);
            toast.error(err?.response?.data?.detail || "Couldn't save reaction");
        }
    };

    // Only reactions with at least 1 vote (or that the user has personally
    // added) show inline — collapses the row from 8 emoji to 0-3 most days.
    const visible = REACTIONS.filter(({ key }) => (counts[key] || 0) > 0 || mine.has(key));

    return (
        <div
            className="inline-flex items-center gap-1 flex-wrap relative"
            data-testid={`reactions-${resourceId}`}
        >
            {visible.map(({ key, glyph, label }) => {
                const isMine = mine.has(key);
                const count = counts[key] || 0;
                return (
                    <button
                        key={key}
                        type="button"
                        onClick={(e) => toggle(e, key)}
                        data-testid={`react-${key}-${resourceId}`}
                        aria-label={`${label} — ${count} reactions`}
                        title={`${label}${isMine ? " · you reacted" : ""}${count > 0 ? ` · ${count}` : ""}`}
                        className={`group inline-flex items-center gap-1 h-6 px-1.5 rounded-full text-[11px] font-semibold transition-all ${
                            isMine
                                ? "bg-teal-50 ring-1 ring-[#2EB4AC]/40 text-[#1F7F79]"
                                : "bg-slate-50 hover:bg-slate-100 text-slate-600 ring-1 ring-slate-200"
                        }`}
                    >
                        <span className={`text-[12px] leading-none transition-transform ${isMine ? "scale-110" : "group-hover:scale-110"}`}>{glyph}</span>
                        {count > 0 && <span className="tabular-nums leading-none">{count}</span>}
                    </button>
                );
            })}
            {/* "+ add reaction" picker — keeps the default state spotless */}
            <div ref={pickerRef} className="relative inline-flex">
                <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPickerOpen((v) => !v); }}
                    data-testid={`react-add-${resourceId}`}
                    aria-label="Add reaction"
                    title="Add reaction"
                    className={`inline-flex items-center justify-center h-6 w-6 rounded-full text-slate-400 hover:text-[#1F7F79] hover:bg-teal-50 transition-colors ${pickerOpen ? "bg-teal-50 text-[#1F7F79]" : ""}`}
                >
                    <SmilePlus className="h-3.5 w-3.5" />
                </button>
                {pickerOpen && (
                    <div
                        className="absolute bottom-full left-0 mb-1.5 z-20 bg-white rounded-xl shadow-lg ring-1 ring-slate-200 px-1.5 py-1 flex items-center gap-0.5"
                        data-testid={`react-picker-${resourceId}`}
                    >
                        {REACTIONS.map(({ key, glyph, label }) => {
                            const isMine = mine.has(key);
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={(e) => toggle(e, key)}
                                    data-testid={`react-pick-${key}-${resourceId}`}
                                    title={label}
                                    aria-label={label}
                                    className={`h-8 w-8 rounded-lg text-[18px] leading-none inline-flex items-center justify-center transition-all hover:bg-slate-100 hover:scale-110 ${isMine ? "bg-teal-50 ring-1 ring-[#2EB4AC]/40" : ""}`}
                                >
                                    {glyph}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

// ---------- ReceiptsButton — owner-only popover showing who opened / completed ----------
const ReceiptsButton = ({ resourceId, assignedCount }) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const popRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        api.get(`/learning/resources/${resourceId}/receipts`)
            .then((r) => setData(r.data))
            .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load receipts"))
            .finally(() => setLoading(false));
    }, [open, resourceId]);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => { if (!popRef.current?.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    if (assignedCount <= 0) return null;
    return (
        <div className="relative inline-block" ref={popRef}>
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
                data-testid={`receipts-toggle-${resourceId}`}
                className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-[#2EB4AC]"
            >
                <CheckCircle2 className="h-3 w-3" />
                Receipts
            </button>
            {open && (
                <div
                    className="absolute z-40 right-0 mt-2 w-72 rounded-xl border border-slate-200 bg-white shadow-xl p-3"
                    data-testid={`receipts-popover-${resourceId}`}
                >
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold">Read receipts</div>
                        {data && (
                            <div className="text-[11px] text-slate-500 tabular-nums">
                                {data.summary.completed}/{data.summary.total} done · {data.summary.opened}/{data.summary.total} opened
                            </div>
                        )}
                    </div>
                    {loading && <div className="text-xs text-slate-500 py-3">Loading…</div>}
                    {!loading && data && data.assignees.length === 0 && (
                        <div className="text-xs text-slate-500 py-3">No assignees yet.</div>
                    )}
                    {!loading && data && data.assignees.length > 0 && (
                        <ul className="space-y-2 max-h-72 overflow-y-auto">
                            {data.assignees.map((a) => {
                                const opened = formatShortDate(a.first_opened_at);
                                const completed = formatShortDate(a.completed_at);
                                const dotCls = a.status === "completed"
                                    ? "bg-emerald-500"
                                    : (opened ? "bg-amber-400" : "bg-slate-300");
                                return (
                                    <li key={a.user_id} className="flex items-center gap-2.5">
                                        <span className={`h-2 w-2 rounded-full shrink-0 ${dotCls}`} />
                                        <span className="flex-1 min-w-0">
                                            <span className="block text-sm font-medium text-slate-900 truncate">{a.name}</span>
                                            <span className="block text-[11px] text-slate-500 truncate">
                                                {completed
                                                    ? `Completed · ${completed}`
                                                    : opened
                                                        ? `Opened · ${opened}${a.opened_count > 1 ? ` · ${a.opened_count}×` : ""}`
                                                        : "Not opened yet"}
                                            </span>
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
};

// Fire-and-forget "I opened this resource" ping. Used by ResourceCard's
// Open link to drive the read-receipt feed. Failures are silent so we
// never block the user's click.
const pingOpen = (resourceId) => {
    try { api.post(`/learning/resources/${resourceId}/open`).catch(() => {}); } catch { /* no-op */ }
};


// Pure helper — picks the 4 most recently added resources in the last 7
// days, falling back to the 4 newest overall when the week is empty. Kept
// at module scope so React's purity linter doesn't flag `Date.now()` use
// inside a `useMemo` body in the component below.
const computeWhatsNew = (resources) => {
    if (!resources || !resources.length) return { items: [], window: "week" };
    const sorted = [...resources].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const recent = sorted.filter((r) => {
        const t = Date.parse(r.created_at || "");
        return Number.isFinite(t) && t >= weekAgo;
    });
    if (recent.length > 0) return { items: recent.slice(0, 4), window: "week" };
    return { items: sorted.slice(0, 4), window: "recent" };
};

const STATUS_OPTIONS = [
    { v: "not_started", label: "Not started", icon: Circle, cls: "bg-slate-100 text-slate-700 border-slate-200" },
    { v: "in_progress", label: "In progress", icon: Activity, cls: "bg-blue-100 text-blue-800 border-blue-200" },
    { v: "completed",   label: "Completed",   icon: CheckCircle2, cls: "bg-green-100 text-green-800 border-green-200" },
    { v: "skipped",     label: "Skipped",     icon: SkipForward, cls: "bg-purple-100 text-purple-800 border-purple-200" },
];

import LearningMindMap from "@/pages/LearningMindMap";
// Valid sub-nav slugs. Kept in this order so a bare `/learning` still
// lands the user on the personalised Team tab.
const LH_TABS = ["team", "library", "assigned", "mine"];

// ---------- New sub-nav components (edX-inspired redesign) ----------

/** Sticky tab bar under the page title. Four tabs:
 *   • My Team's Workspace  ("team")            — the personalised default.
 *   • All Department Library ("all")           — full org catalog.
 *   • Assigned to me ("assigned")              — inbox of assignments.
 *   • My Contributions ("contributions")       — things I own.
 */
const LearningTabs = ({ tab, setTab, counts }) => {
    // Kept for internal state routing but no longer rendered as a tab bar —
    // the two sub-items live in the sidebar. The Assigned + Mine views
    // roll up into the Team tab (which now shows assignments + own +
    // department peers as one "My Team's Library" surface).
    void tab; void setTab; void counts;
    return null;
};

// ---------- HTML-mockup color mapping helpers ----------
// The HTML template ("odyssey-learning-hub-sidenav-v8") assigns each
// department a base color from the {teal, amber, coral, blue, violet}
// palette. We reproduce that mapping in code so real department names
// from the DB map to the same colors as the mockup. Everything else
// falls back to a stable hash so unknown departments still look good.
const LH_DEPT_COLOR_HINTS = [
    { match: /amazon/i,        color: "teal"   },
    { match: /flipkart/i,      color: "teal"   },
    { match: /zepto/i,         color: "violet" },
    { match: /blinkit|emerging|quick.?commerce/i, color: "violet" },
    { match: /d2c|direct.?to.?consumer/i,         color: "blue"   },
    { match: /supply.?chain/i, color: "amber"  },
    { match: /logistics|warehous/i, color: "amber" },
    { match: /meta|facebook/i, color: "coral"  },
    { match: /google|youtube|seo/i, color: "coral" },
    { match: /offline|retail/i, color: "coral" },
    { match: /customer|support|experience/i, color: "blue" },
    { match: /firmware|engineering|tech/i, color: "violet" },
    { match: /finance|account/i, color: "amber" },
    { match: /hr|people|talent/i, color: "coral" },
];
const LH_DEPT_COLOR_CYCLE = ["teal", "amber", "coral", "blue", "violet"];
const _lhHashString = (s = "") => { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0; return Math.abs(h); };
const lhDeptColor = (name = "") => {
    for (const hint of LH_DEPT_COLOR_HINTS) if (hint.match.test(name)) return hint.color;
    return LH_DEPT_COLOR_CYCLE[_lhHashString(name) % LH_DEPT_COLOR_CYCLE.length];
};
// Short name = friendly nickname matching the mockup design. First try
// explicit hint matches (e.g. "Amazon Marketing operations New" → "Amazon"),
// then fall back to a suffix-stripping heuristic for any dept the mockup
// didn't anticipate.
const LH_DEPT_SHORTHAND = [
    { match: /amazon/i,                             short: "Amazon" },
    { match: /flipkart/i,                           short: "Flipkart" },
    { match: /zepto/i,                              short: "Zepto" },
    { match: /blinkit|emerging|quick.?commerce/i,   short: "Blinkit & Emerging" },
    { match: /d2c|direct.?to.?consumer/i,           short: "D2C Platform" },
    { match: /supply.?chain/i,                      short: "Supply Chain" },
    { match: /logistics|warehous/i,                 short: "Logistics & Warehousing" },
    { match: /meta|facebook/i,                      short: "Meta Marketing" },
    { match: /google|youtube|seo/i,                 short: "Google, YouTube & SEO" },
    { match: /offline|retail/i,                     short: "Offline Marketing" },
    { match: /customer|support|experience/i,        short: "Customer Experience" },
    { match: /firmware|engineering|tech/i,          short: "Engineering" },
    { match: /finance|account/i,                    short: "Finance" },
    { match: /hr|people|talent/i,                   short: "People" },
];
const lhDeptShort = (name = "") => {
    if (!name) return "";
    for (const hint of LH_DEPT_SHORTHAND) if (hint.match.test(name)) return hint.short;
    // Fallback — strip known long-form suffixes.
    const stripped = name
        .replace(/\s*Marketplace\s+Growth\s*&\s*Intelligence.*$/i, "")
        .replace(/\s*Operations\s*&\s*(Business\s+)?Intelligence.*$/i, "")
        .replace(/\s*Growth\s*&\s*Intelligence.*$/i, "")
        .trim();
    return stripped || name;
};

// Icon glyph for a resource type — mirrors the mockup's `fmtIcon` map.
const LH_FMT_ICON = { video: "▶", course: "▶", article: "▤", doc: "▤", podcast: "▤", book: "▤", quiz: "✓", interactive: "⌘", other: "▤" };
// Map real backend resource `type` to the mockup's Format bucket for
// composition-chip coloring purposes.
const lhFmtBucket = (type = "") => {
    const t = String(type).toLowerCase();
    if (t === "video") return "Video";
    if (t === "quiz") return "Quiz";
    if (t === "interactive") return "Interactive";
    return "Document";
};

/** Pixel-perfect port of the HTML mockup's `.page-header` block:
 * peach → mint gradient with two blurred orbs, breadcrumb eyebrow,
 * `Fraunces`-style H1 (mapped to Outfit), description, and a horizontal
 * row of `.ph-stat-chip` stats. Adds a floating Shelf / Mind Map toggle
 * to the top-right corner. */
const LearningPageHeader = ({ tab, deptName, stats, viewMode, onViewMode }) => {
    const isTeam = tab === "team";
    const crumb = isTeam ? "My Team's Library" : "All Library";
    const desc = isTeam
        ? (deptName
            ? `Everything the ${deptName} team has contributed — your own team's collections, front and center.`
            : "Your own team's collections and contributions, front and center.")
        : "Every collection contributed across Odyssey, browsable by department.";
    return (
        <div className="page-header" data-testid={`learning-page-header-${tab}`}>
            <div className="ph-orb o1" /><div className="ph-orb o2" />
            {onViewMode && (
                <div className="ph-view-toggle" data-testid="learning-view-toggle">
                    <button
                        type="button"
                        onClick={() => onViewMode("shelf")}
                        className={viewMode === "shelf" ? "active" : ""}
                        data-testid="learning-view-shelf"
                    >Shelf</button>
                    <button
                        type="button"
                        onClick={() => onViewMode("mindmap")}
                        className={viewMode === "mindmap" ? "active" : ""}
                        data-testid="learning-view-mindmap"
                    >Mind Map</button>
                </div>
            )}
            <div className="ph-inner">
                <div className="ph-eyebrow"><span>Learning Hub</span><span className="sep">/</span><span>{crumb}</span></div>
                <h1>{crumb}</h1>
                <p>{desc}</p>
                <div className="ph-stats">
                    {(stats || []).map((s, i) => (
                        <div key={i} className="ph-stat-chip" data-testid={`page-stat-${s.key}`}>
                            <span className="val mono">{s.value}</span> {s.label}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

/** Retired the previous MyTeamHero / LibraryHero heroes — replaced by
 * LearningPageHeader (above). Keep the exports for legacy callers. */
const MyTeamHero = () => null;
const LibraryHero = () => null;

/** RETIRED — replaced by LearningPageHeader (defined above). */
const _MyTeamHero_retired = ({ deptName, teamCollectionCount, teamResourceCount, assignedCount }) => (
    <section
        data-testid="learning-team-hero"
        className="relative overflow-hidden rounded-2xl text-white shadow-md"
        style={{ background: "linear-gradient(120deg, #06333a 0%, #0a2540 55%, #1e1b4b 100%)" }}
    >
        <div className="absolute -top-16 -right-16 h-72 w-72 rounded-full opacity-40 blur-3xl" style={{ background: "radial-gradient(circle, rgba(46,180,172,0.6), transparent 70%)" }} />
        <div className="relative p-7 sm:p-9">
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-teal-200/90 flex items-center gap-2">
                <Users className="h-3.5 w-3.5" /> {deptName || "Your team"} · Your workspace
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2 leading-tight">
                Learn with <span className="bg-gradient-to-r from-emerald-300 to-teal-300 bg-clip-text text-transparent">your team&apos;s</span> curated knowledge
            </h2>
            <p className="text-sm text-teal-100/85 mt-2 max-w-2xl">
                Everything shared, curated, or created by teammates on {deptName || "your team"} — one place to catch up, jump into an assignment, and see what your peers are learning.
            </p>
            <div className="mt-6 flex flex-wrap gap-6 sm:gap-10">
                <div>
                    <div className="font-heading text-2xl sm:text-3xl font-extrabold">{teamCollectionCount}</div>
                    <div className="text-[11px] uppercase tracking-wider text-teal-100/70 mt-0.5">Team collections</div>
                </div>
                <div>
                    <div className="font-heading text-2xl sm:text-3xl font-extrabold">{teamResourceCount}</div>
                    <div className="text-[11px] uppercase tracking-wider text-teal-100/70 mt-0.5">Team resources</div>
                </div>
                <div>
                    <div className="font-heading text-2xl sm:text-3xl font-extrabold">{assignedCount}</div>
                    <div className="text-[11px] uppercase tracking-wider text-teal-100/70 mt-0.5">Assigned to you</div>
                </div>
            </div>
        </div>
    </section>
);

/** Continue-learning strip. */
const ContinueLearningStrip = ({ items, onOpen }) => {
    if (!items || items.length === 0) return null;
    return (
        <section data-testid="learning-continue" className="space-y-3">
            <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#25908A] font-semibold">Continue</div>
                <h3 className="font-heading text-xl font-bold text-slate-900 tracking-tight mt-1 flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-[#2EB4AC]" /> Continue where you left off
                </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.slice(0, 3).map((r) => {
                    const pct = Math.max(0, Math.min(100, Number(r.progress?.percent) || 0));
                    const Ic = TYPE_ICON[r.type] || TYPE_ICON.other;
                    return (
                        <button
                            key={r.id}
                            type="button"
                            data-testid={`continue-item-${r.id}`}
                            onClick={() => onOpen?.(r)}
                            className="text-left bg-white border border-slate-200 hover:border-[#2EB4AC] hover:shadow-md rounded-xl p-4 transition-all group"
                        >
                            <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-lg bg-teal-50 text-[#25908A] flex items-center justify-center shrink-0">
                                    <Ic className="h-5 w-5" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="font-semibold text-sm text-slate-900 line-clamp-1 group-hover:text-[#25908A]">{r.name || r.title || "Untitled"}</div>
                                    <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-1">
                                        {TYPE_LABEL[r.type] || "Resource"} · {pct}% done
                                    </div>
                                </div>
                            </div>
                            <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-[#2EB4AC] to-[#25908A] transition-all" style={{ width: `${pct}%` }} />
                            </div>
                        </button>
                    );
                })}
            </div>
        </section>
    );
};

const CARD_ACCENTS = [
    { bar: "#2EB4AC", tint: "#e6f7f5", fg: "#25908A" },
    { bar: "#f4a261", tint: "#fef3e5", fg: "#c46a1a" },
    { bar: "#e76f51", tint: "#fdece7", fg: "#b74a30" },
    { bar: "#7b8cff", tint: "#ecefff", fg: "#4b56b3" },
    { bar: "#c084fc", tint: "#f5ecff", fg: "#8a3ec4" },
    { bar: "#f472b6", tint: "#feecf4", fg: "#c53d80" },
    { bar: "#38bdf8", tint: "#e6f4fc", fg: "#1a80ba" },
];
const accentFor = (id) => {
    let h = 0;
    const s = String(id || "");
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
    return CARD_ACCENTS[Math.abs(h) % CARD_ACCENTS.length];
};

/** Team-tab collection card. Draggable + reorderable to match the mockup's
 * rearrange affordance — dragging it onto another card slots it before/
 * after; the parent persists the new order via `PUT /collections/reorder`. */
const TargetCollectionCard = ({ collection, resourceIndex, onOpen, onReorder }) => {
    const [mark, setMark] = useState(null); // "before" | "after" | null
    // Prefer the user-picked cover_color; fall back to a stable hash of
    // the department name so legacy collections still look themed.
    const color = collection.cover_color || lhDeptColor(collection.owner_department_name || "");
    const coverImg = (collection.cover_image || "").trim();
    const own = resourceIndex[collection.id] || { resources: 0, formats: [] };
    // Convert real backend types to the mockup's composition-icon buckets.
    const bucketSet = new Set();
    for (const t of own.formats) bucketSet.add(lhFmtBucket(t));
    const buckets = Array.from(bucketSet).slice(0, 3);
    const bucketIcon = { Video: "▶", Document: "▤", Quiz: "✓", Interactive: "⌘" };
    return (
        <div
            className={`card ${mark ? `reorder-${mark}` : ""}`}
            data-testid={`target-card-${collection.id}`}
            onClick={() => onOpen?.(collection)}
            draggable={!!collection.can_manage || true}
            onDragStart={(e) => {
                e.dataTransfer.setData("application/x-lh-collection", collection.id);
                e.dataTransfer.effectAllowed = "move";
            }}
            onDragOver={(e) => {
                if (!e.dataTransfer.types.includes("application/x-lh-collection")) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                const rect = e.currentTarget.getBoundingClientRect();
                const next = (e.clientX - rect.left) < rect.width / 2 ? "before" : "after";
                if (next !== mark) setMark(next);
            }}
            onDragLeave={() => setMark(null)}
            onDrop={(e) => {
                const draggedId = e.dataTransfer.getData("application/x-lh-collection");
                const pos = mark || "after";
                setMark(null);
                if (!draggedId || draggedId === collection.id) return;
                e.preventDefault();
                e.stopPropagation();
                onReorder?.(draggedId, collection.id, pos);
            }}
        >
            {coverImg ? (
                <div
                    className="card-cover"
                    style={{ backgroundImage: `url(${coverImg})` }}
                    data-testid={`card-cover-${collection.id}`}
                />
            ) : (
                <div className="card-accent" style={{ background: `var(--${color})` }} />
            )}
            <div className="card-inner">
                <div className="c-icon" style={{ background: `var(--${color}-tint)`, color: `var(--${color})` }}>◆</div>
                <div className="c-name">{collection.name || "Untitled collection"}</div>
                {buckets.length > 0 && (
                    <div className="c-composition">
                        {buckets.map((b, i) => (
                            <span key={i} style={{ background: `var(--${color}-tint)`, color: `var(--${color})` }}>{bucketIcon[b]}</span>
                        ))}
                    </div>
                )}
                <div className="c-meta-row">
                    <span className="c-meta">{collection.folder_count || 0} folder{(collection.folder_count || 0) === 1 ? "" : "s"}</span>
                    <span className="c-meta">{own.resources} resource{own.resources !== 1 ? "s" : ""}</span>
                </div>
            </div>
        </div>
    );
};

/** Pixel-perfect port of the HTML mockup's `.glib-card` — the
 * discover-mode collection card. Dept-tinted pill in the top-left,
 * bookmark button in the top-right, big title, footer with duration
 * + "Updated …" stamp. */
const DiscoverCollectionCard = ({ collection, resourceIndex, saved, onOpen, onToggleSave }) => {
    const deptName = collection.owner_department_name || "";
    const color = collection.cover_color || lhDeptColor(deptName);
    const coverImg = (collection.cover_image || "").trim();
    const own = resourceIndex[collection.id] || { resources: 0, formats: [], totalMinutes: 0 };
    const dur = own.totalMinutes || 0;
    const fmtDur = (m) => (m < 60 ? `${m}m` : (m % 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${Math.floor(m / 60)}h`));
    // "Updated …" — prefer created_at; if missing show em-dash.
    let updatedLabel = "—";
    const iso = collection.updated_at || collection.created_at;
    if (iso) {
        try {
            const d = new Date(iso);
            updatedLabel = `Updated ${d.toLocaleString(undefined, { month: "short", year: "2-digit" })}`;
        } catch { /* ignore */ }
    }
    // Title = drop everything before the last "|" (mockup convention).
    const title = String(collection.name || "Untitled").split("|").pop().trim();
    return (
        <div
            className="glib-card"
            data-testid={`discover-card-${collection.id}`}
            onClick={() => onOpen?.(collection)}
        >
            {coverImg && (
                <div
                    className="glib-cover"
                    style={{ backgroundImage: `url(${coverImg})` }}
                />
            )}
            <div className="glib-top">
                <span
                    className="glib-pill"
                    style={{ background: `var(--${color}-tint)`, color: `var(--${color})` }}
                    title={deptName}
                >
                    {lhDeptShort(deptName) || "Odyssey"}
                </span>
                <div className="glib-top-right">
                    <button
                        type="button"
                        className={`glib-bookmark ${saved ? "saved" : ""}`}
                        data-testid={`discover-bookmark-${collection.id}`}
                        onClick={(e) => { e.stopPropagation(); onToggleSave?.(collection.id); }}
                        aria-label="Bookmark"
                    >{saved ? "📑" : "🔖"}</button>
                </div>
            </div>
            <div className="glib-title">{title}</div>
            <div className="glib-footer">
                <span className="glib-clock">◷ {dur > 0 ? fmtDur(dur) : `${own.resources} resource${own.resources !== 1 ? "s" : ""}`}</span>
                <span>{updatedLabel}</span>
            </div>
        </div>
    );
};

// Suggested prompt pills for the Discover search — quick starting
// points that map to a search query on the collection catalog.
const LH_PROMPT_PILLS = [
    { label: "New hire fundamentals", query: "fundamental" },
    { label: "Onboarding basics",     query: "basic" },
    { label: "Support playbooks",     query: "playbook" },
    { label: "Fulfilment operations", query: "fulfilment" },
    { label: "Seller panel guides",   query: "seller" },
];

/** Discover view (All Library landing) — pixel-perfect from the mockup.
 * Big centered headline + subtitle, futuristic prompt-search card,
 * hero stats, and a Programs-style card grid with a sticky left rail
 * of accordion filters (Department / Type / Level / Skill / Duration),
 * a `Sort by` dropdown, and pagination. */
const DiscoverView = ({ collections, resourceIndex, savedIds, onOpen, onToggleSave, query, setQuery, activeDept, setActiveDept, catalogResources }) => {
    const [typeFilter, setTypeFilter] = useState("");
    const [levelFilter, setLevelFilter] = useState("");
    const [durationFilter, setDurationFilter] = useState(""); // "short" | "medium" | "long" | ""
    const [skillFilter, setSkillFilter] = useState("");
    const [sortBy, setSortBy] = useState("popular");
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 12;
    // Track which filter accordion is expanded — starts all open on the
    // web viewport, controllable per-section.
    const [openSection, setOpenSection] = useState({ dept: true, type: true, level: true, skill: false, duration: false });
    const toggleSection = (k) => setOpenSection((s) => ({ ...s, [k]: !s[k] }));
    // Departments that actually have at least one collection.
    const depts = useMemo(() => {
        const seen = new Map();
        for (const c of collections) {
            const key = c.owner_department_id || `__name__${c.owner_department_name || ""}`;
            if (!seen.has(key) && (c.owner_department_name || "").trim()) {
                seen.set(key, { id: key, name: c.owner_department_name, color: lhDeptColor(c.owner_department_name) });
            }
        }
        return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [collections]);
    // Unique skills across all collections for the Skill filter.
    const skills = useMemo(() => {
        const set = new Set();
        for (const c of collections) (c.skills || []).forEach((s) => s && set.add(s));
        return Array.from(set).sort();
    }, [collections]);

    // Duration bucketing: sum minutes per collection, then classify.
    const bucketOf = (mins) => {
        if (mins < 60) return "short";       // < 1h
        if (mins < 240) return "medium";     // 1–4h
        return "long";                       // 4h+
    };
    const q = query.trim().toLowerCase();
    const filtered = useMemo(() => {
        let arr = collections;
        if (activeDept !== "__all__") {
            arr = arr.filter((c) => (c.owner_department_id || `__name__${c.owner_department_name || ""}`) === activeDept);
        }
        if (typeFilter || levelFilter) {
            arr = arr.filter((c) => {
                const inCol = (catalogResources || []).filter((r) => r.collection_id === c.id);
                return inCol.some((r) =>
                    (!typeFilter || r.type === typeFilter) &&
                    (!levelFilter || r.level === levelFilter)
                );
            });
        }
        if (skillFilter) {
            arr = arr.filter((c) => (c.skills || []).some((s) => s === skillFilter));
        }
        if (durationFilter) {
            arr = arr.filter((c) => bucketOf(resourceIndex[c.id]?.totalMinutes || 0) === durationFilter);
        }
        if (q) arr = arr.filter((c) =>
            (c.name || "").toLowerCase().includes(q) ||
            (c.description || "").toLowerCase().includes(q) ||
            (c.owner_department_name || "").toLowerCase().includes(q) ||
            (c.skills || []).some((s) => String(s).toLowerCase().includes(q))
        );
        // Sort
        const arr2 = arr.slice();
        if (sortBy === "recent") arr2.sort((a, b) => String(b.updated_at || b.created_at || "").localeCompare(String(a.updated_at || a.created_at || "")));
        else if (sortBy === "az") arr2.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        else if (sortBy === "longest") arr2.sort((a, b) => (resourceIndex[b.id]?.totalMinutes || 0) - (resourceIndex[a.id]?.totalMinutes || 0));
        else /* popular */ arr2.sort((a, b) => (resourceIndex[b.id]?.resources || 0) - (resourceIndex[a.id]?.resources || 0));
        return arr2;
    }, [collections, activeDept, q, typeFilter, levelFilter, catalogResources, skillFilter, durationFilter, sortBy, resourceIndex]);

    // Reset to page 1 whenever any filter changes.
    useEffect(() => { setPage(1); }, [activeDept, typeFilter, levelFilter, skillFilter, durationFilter, q, sortBy]);
    const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const totalRes = filtered.reduce((s, c) => s + (resourceIndex[c.id]?.resources || 0), 0);
    const deptCount = new Set(filtered.map((c) => c.owner_department_id)).size;

    const [inputVal, setInputVal] = useState(query);
    useEffect(() => { setInputVal(query); }, [query]);
    const runSearch = () => setQuery(inputVal.trim().toLowerCase());

    const clearAll = () => {
        setTypeFilter(""); setLevelFilter(""); setSkillFilter(""); setDurationFilter("");
        setActiveDept("__all__"); setQuery(""); setInputVal("");
    };
    const anyActive = typeFilter || levelFilter || skillFilter || durationFilter || activeDept !== "__all__" || q;

    return (
        <div className="discover" data-testid="learning-discover">
            <h1>Level up your team&apos;s skills</h1>
            <div className="discover-sub">Leverage curated courses and internal knowledge to onboard, upskill, and train your team.</div>

            <div className="prompt-card" data-testid="discover-prompt-card">
                <div className="prompt-row">
                    <span className="pr-label">I want to</span>
                    <input
                        data-testid="discover-prompt-input"
                        placeholder="learn Amazon Advertising fundamentals..."
                        value={inputVal}
                        onChange={(e) => setInputVal(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
                    />
                    <button type="button" className="prompt-send" data-testid="discover-prompt-send" onClick={runSearch}>➤</button>
                </div>
                <div className="prompt-pills">
                    {LH_PROMPT_PILLS.map((p) => (
                        <span
                            key={p.query}
                            className="prompt-pill"
                            data-testid={`discover-pill-${p.query}`}
                            onClick={() => { setActiveDept("__all__"); setQuery(p.query); }}
                        >{p.label} ›</span>
                    ))}
                </div>
            </div>

            <div className="hero-stats">
                <div className="ph-stat-chip"><span className="val mono">{filtered.length}</span> collection{filtered.length === 1 ? "" : "s"}</div>
                <div className="ph-stat-chip"><span className="val mono">{deptCount}</span> department{deptCount === 1 ? "" : "s"}</div>
                <div className="ph-stat-chip"><span className="val mono">{totalRes}</span> resource{totalRes === 1 ? "" : "s"}</div>
            </div>

            {/* Two-column layout: accordion filters left, All Library grid right. */}
            <div className="lh-programs-layout">
                <aside className="lh-programs-filters" data-testid="discover-filters">
                    <LhAccordion
                        title="Department"
                        icon="🏛"
                        open={openSection.dept}
                        onToggle={() => toggleSection("dept")}
                    >
                        <div className="lh-side-chips">
                            <button
                                type="button"
                                onClick={() => setActiveDept("__all__")}
                                className={`lh-chip lh-chip-sm ${activeDept === "__all__" ? "selected" : ""}`}
                                data-testid="discover-dtab-all"
                            >All departments</button>
                            {depts.map((d) => (
                                <button
                                    key={d.id}
                                    type="button"
                                    onClick={() => setActiveDept(d.id)}
                                    className={`lh-chip lh-chip-sm ${activeDept === d.id ? "selected" : ""}`}
                                    data-testid={`discover-dtab-${d.id}`}
                                    title={d.name}
                                >
                                    <span className="dt-dot" style={{ background: activeDept === d.id ? "#fff" : `var(--${d.color})` }} />
                                    {lhDeptShort(d.name)}
                                </button>
                            ))}
                        </div>
                    </LhAccordion>
                    <LhAccordion title="Type" icon="▤" open={openSection.type} onToggle={() => toggleSection("type")}>
                        <div className="lh-side-chips">
                            <button
                                type="button"
                                onClick={() => setTypeFilter("")}
                                className={`lh-chip lh-chip-sm ${!typeFilter ? "selected" : ""}`}
                                data-testid="discover-filter-type-all"
                            >All</button>
                            {TYPES.map((t) => (
                                <button
                                    key={t}
                                    type="button"
                                    onClick={() => setTypeFilter(t)}
                                    className={`lh-chip lh-chip-sm ${typeFilter === t ? "selected" : ""}`}
                                    data-testid={`discover-filter-type-${t}`}
                                >{LH_FMT_ICON[t] || "▤"} {TYPE_LABEL[t] || t}</button>
                            ))}
                        </div>
                    </LhAccordion>
                    <LhAccordion title="Level" icon="≡" open={openSection.level} onToggle={() => toggleSection("level")}>
                        <div className="lh-side-chips">
                            {[
                                { key: "",             label: "All" },
                                { key: "beginner",     label: "● Beginner" },
                                { key: "intermediate", label: "●● Intermediate" },
                                { key: "advanced",     label: "●●● Advanced" },
                            ].map((opt) => (
                                <button
                                    key={opt.key || "all"}
                                    type="button"
                                    onClick={() => setLevelFilter(opt.key)}
                                    className={`lh-chip lh-chip-sm ${levelFilter === opt.key ? "selected" : ""}`}
                                    data-testid={`discover-filter-level-${opt.key || "all"}`}
                                >{opt.label}</button>
                            ))}
                        </div>
                    </LhAccordion>
                    {skills.length > 0 && (
                        <LhAccordion title="Skill" icon="✧" open={openSection.skill} onToggle={() => toggleSection("skill")}>
                            <div className="lh-side-chips">
                                <button
                                    type="button"
                                    onClick={() => setSkillFilter("")}
                                    className={`lh-chip lh-chip-sm ${!skillFilter ? "selected" : ""}`}
                                >All</button>
                                {skills.slice(0, 20).map((s) => (
                                    <button
                                        key={s}
                                        type="button"
                                        onClick={() => setSkillFilter(s)}
                                        className={`lh-chip lh-chip-sm ${skillFilter === s ? "selected" : ""}`}
                                    >{s}</button>
                                ))}
                            </div>
                        </LhAccordion>
                    )}
                    <LhAccordion title="Duration" icon="◷" open={openSection.duration} onToggle={() => toggleSection("duration")}>
                        <div className="lh-side-chips">
                            {[
                                { key: "",       label: "All" },
                                { key: "short",  label: "Under 1 hour" },
                                { key: "medium", label: "1 – 4 hours" },
                                { key: "long",   label: "4+ hours" },
                            ].map((opt) => (
                                <button
                                    key={opt.key || "all"}
                                    type="button"
                                    onClick={() => setDurationFilter(opt.key)}
                                    className={`lh-chip lh-chip-sm ${durationFilter === opt.key ? "selected" : ""}`}
                                >{opt.label}</button>
                            ))}
                        </div>
                    </LhAccordion>
                    {anyActive && (
                        <button type="button" className="lh-side-clear" onClick={clearAll} data-testid="discover-filter-clear">Clear filters ×</button>
                    )}
                </aside>

                <div className="lh-programs-main">
                    <div className="lh-programs-head">
                        <h2>All Library</h2>
                        <div className="lh-sort-wrap">
                            <span className="lh-sort-label">↕ Sort by:</span>
                            <select
                                className="lh-sort-select"
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                data-testid="discover-sort"
                            >
                                <option value="popular">Most Popular</option>
                                <option value="recent">Recently Updated</option>
                                <option value="longest">Longest first</option>
                                <option value="az">A → Z</option>
                            </select>
                        </div>
                    </div>
                    <div className="glib-grid" data-testid="discover-grid">
                        {pageItems.length === 0 ? (
                            <div style={{ gridColumn: "1/-1" }}>
                                <div className="empty-state">
                                    <div className="empty-icon">◆</div>
                                    <p>{q ? `Nothing matches "${q}".` : "No collections match these filters."}</p>
                                </div>
                            </div>
                        ) : (
                            pageItems.map((c) => (
                                <DiscoverCollectionCard
                                    key={c.id}
                                    collection={c}
                                    resourceIndex={resourceIndex}
                                    saved={savedIds.has(c.id)}
                                    onOpen={onOpen}
                                    onToggleSave={onToggleSave}
                                />
                            ))
                        )}
                    </div>
                    {pageCount > 1 && (
                        <LhPagination page={page} pageCount={pageCount} onChange={setPage} />
                    )}
                </div>
            </div>
        </div>
    );
};

/** Accordion filter card — mockup-styled expand/collapse panel. */
const LhAccordion = ({ title, icon, open, onToggle, children }) => (
    <div className={`lh-acc ${open ? "open" : ""}`}>
        <button type="button" className="lh-acc-head" onClick={onToggle}>
            <span className="lh-acc-icon">{icon}</span>
            <span className="lh-acc-title">{title}</span>
            <span className="lh-acc-caret">›</span>
        </button>
        {open && <div className="lh-acc-body">{children}</div>}
    </div>
);

/** Numeric pagination — matches the mockup: `1 2 3 4 5 … 28` with prev/
 * next buttons. Windowed around the current page. */
const LhPagination = ({ page, pageCount, onChange }) => {
    const items = [];
    // Show first + last + a window of 3 around current, connected with "…"
    const push = (n) => items.push(n);
    push(1);
    const start = Math.max(2, page - 1);
    const end = Math.min(pageCount - 1, page + 1);
    if (start > 2) push("…-left");
    for (let n = start; n <= end; n++) push(n);
    if (end < pageCount - 1) push("…-right");
    if (pageCount > 1) push(pageCount);
    return (
        <div className="lh-pagination" data-testid="discover-pagination">
            <button
                type="button"
                className="lh-pg-arrow"
                onClick={() => onChange(Math.max(1, page - 1))}
                disabled={page === 1}
                data-testid="pagination-prev"
            >‹</button>
            {items.map((n, i) => (typeof n === "number" ? (
                <button
                    key={n}
                    type="button"
                    className={`lh-pg-num ${n === page ? "active" : ""}`}
                    onClick={() => onChange(n)}
                    data-testid={`pagination-${n}`}
                >{n}</button>
            ) : (
                <span key={`${n}-${i}`} className="lh-pg-ellipsis">…</span>
            )))}
            <button
                type="button"
                className="lh-pg-arrow next"
                onClick={() => onChange(Math.min(pageCount, page + 1))}
                disabled={page === pageCount}
                data-testid="pagination-next"
            >›</button>
        </div>
    );
};

/** Team-tab body — pixel-perfect from the mockup.
 * Toolbar with root breadcrumb + filter + "+ New" dropdown, then a
 * `Collections` section label, then a `.grid` of `.card`s. */
const TargetLibraryBody = ({ tab, collections, catalogResources, onOpenCollection, onNewCollection, onAddResource, onReorderCollections }) => {
    const [query, setQuery] = useState("");
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);
    useEffect(() => {
        const close = () => setMenuOpen(false);
        document.addEventListener("click", close);
        return () => document.removeEventListener("click", close);
    }, []);

    // Build per-collection index of resource-count + type-buckets so the
    // card can render the composition chips without re-fetching.
    const resourceIndex = useMemo(() => {
        const idx = {};
        for (const r of catalogResources || []) {
            const cid = r.collection_id;
            if (!cid) continue;
            if (!idx[cid]) idx[cid] = { resources: 0, formats: [], seen: new Set() };
            idx[cid].resources += 1;
            const t = r.type || "other";
            if (!idx[cid].seen.has(t)) { idx[cid].seen.add(t); idx[cid].formats.push(t); }
        }
        return idx;
    }, [catalogResources]);

    const q = query.trim().toLowerCase();
    const filtered = useMemo(() => {
        if (!q) return collections;
        return collections.filter((c) =>
            (c.name || "").toLowerCase().includes(q) ||
            (c.description || "").toLowerCase().includes(q) ||
            (c.owner_department_name || "").toLowerCase().includes(q)
        );
    }, [collections, q]);
    const rootLabel = tab === "team" ? "My Team's Library" : "All Library";

    return (
        <>
            <div className="toolbar" data-testid="target-toolbar">
                <div className="crumbs" data-testid="target-crumbs">
                    <span className="crumb current">{rootLabel}</span>
                </div>
                <div className="search-mini" data-testid="target-filter-wrap">
                    ⌕
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Filter this view..."
                        data-testid="target-filter-input"
                    />
                </div>
                <div className="tb-actions" ref={menuRef}>
                    <button
                        type="button"
                        className="btn btn-primary"
                        data-testid="target-new-btn"
                        onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
                    >＋ New</button>
                    <div className={`new-menu ${menuOpen ? "open" : ""}`} onClick={(e) => e.stopPropagation()}>
                        <div
                            className="new-menu-item"
                            data-testid="target-new-collection"
                            onClick={() => { setMenuOpen(false); onNewCollection?.(); }}
                        ><span className="nmi-icon">◆</span> New collection</div>
                        <div className="new-menu-divider" />
                        <div
                            className="new-menu-item"
                            data-testid="target-new-resource"
                            onClick={() => { setMenuOpen(false); onAddResource?.(); }}
                        ><span className="nmi-icon">▤</span> Add resource</div>
                    </div>
                </div>
            </div>
            <div className="section-label">Collections</div>
            {filtered.length === 0 ? (
                <div className="empty-state" data-testid="target-empty">
                    <div className="empty-icon">◆</div>
                    <p>{q
                        ? `No collections match "${q}".`
                        : (tab === "team" ? "No team collections here yet. Start your team's library with its first collection." : "No collections in the org yet.")}
                    </p>
                    {!q && (
                        <button type="button" className="btn btn-primary" onClick={onNewCollection} data-testid="target-empty-new">
                            ＋ New collection
                        </button>
                    )}
                </div>
            ) : (
                <div className="grid" data-testid="target-collections-grid">
                    {filtered.map((c) => (
                        <TargetCollectionCard
                            key={c.id}
                            collection={c}
                            resourceIndex={resourceIndex}
                            onOpen={onOpenCollection}
                            onReorder={onReorderCollections}
                        />
                    ))}
                </div>
            )}
        </>
    );
};


/** Library-tab hero — dark navy → indigo gradient, big headline with
 * emerald "every", embedded search input. Same visual grammar as the
 * mockup: kicker, headline with gradient emphasis, description,
 * hero-embedded search pill. */
// The dark-navy Library hero has been retired; source removed to keep
// the file lean. See git history for the previous implementation.

/** "Browse by department" subject-tile grid. Icon + name + tally.
 * Clicking a tile deep-links to the department shelf page. */
const DEPT_ICON = [BookOpen, Sparkles, Users, GraduationCap, Compass, Briefcase, LineChart, Rocket, Trophy, Palette, Megaphone];
const DEPT_BG = ["bg-teal-50 text-[#25908A]", "bg-indigo-50 text-indigo-600", "bg-amber-50 text-amber-600", "bg-pink-50 text-pink-600", "bg-emerald-50 text-emerald-600", "bg-blue-50 text-blue-600", "bg-fuchsia-50 text-fuchsia-600", "bg-orange-50 text-orange-600"];

const DepartmentBrowseGrid = ({ myDept, otherDepts, catalogResources, onOpen }) => {
    // Merge my dept (if any) as the first tile, then all others.
    const tiles = [];
    if (myDept?.name) tiles.push({ id: myDept.id, name: myDept.name, count: myDept.items.length, isMine: true });
    for (const g of otherDepts || []) {
        if (g.id === "__none__") continue;
        tiles.push({ id: g.id, name: g.label, count: g.items.length, isMine: false });
    }
    if (tiles.length === 0) return null;
    // Per-dept resource count from catalog
    const resByDept = {};
    for (const r of catalogResources || []) {
        const k = r.owner_department_id || r.collection_department_id;
        if (!k) continue;
        resByDept[k] = (resByDept[k] || 0) + 1;
    }
    return (
        <section data-testid="learning-browse-grid" className="space-y-3">
            <div>
                <h3 className="font-heading text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                    <Sparkles className="h-6 w-6 text-[#2EB4AC]" /> Browse by department
                </h3>
                <p className="text-sm text-slate-500 mt-1">Every collection filed by owning department. Click a tile to open the full shelf.</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {tiles.map((t, i) => {
                    const Ic = DEPT_ICON[i % DEPT_ICON.length];
                    const bg = DEPT_BG[i % DEPT_BG.length];
                    return (
                        <button
                            key={t.id || `t-${i}`}
                            type="button"
                            data-testid={`dept-tile-${t.id || "none"}`}
                            onClick={() => onOpen?.(t.id)}
                            className="text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-[#2EB4AC] hover:shadow-lg hover:-translate-y-0.5 transition-all group"
                        >
                            <div className={`h-10 w-10 rounded-lg ${bg} flex items-center justify-center mb-3 group-hover:scale-105 transition-transform`}>
                                <Ic className="h-5 w-5" />
                            </div>
                            <div className="font-heading font-bold text-slate-900 text-base line-clamp-1">{t.name}</div>
                            <div className="text-xs text-slate-500 mt-1">
                                {resByDept[t.id] || 0} resources · {t.count} track{t.count !== 1 ? "s" : ""}
                                {t.isMine && <span className="ml-1 text-[#25908A] font-semibold">· your team</span>}
                            </div>
                        </button>
                    );
                })}
            </div>
        </section>
    );
};

/** "Trending across Odyssey" — type filter pills + sort dropdown + card
 * grid. Sorted by aggregate reactions or recency. */
const TRENDING_TYPES = [
    { k: "",        label: "All types" },
    { k: "video",   label: "Video" },
    { k: "article", label: "Article" },
    { k: "course",  label: "Course" },
    { k: "podcast", label: "Podcast" },
    { k: "book",    label: "Book" },
];

const TrendingSection = ({ resources, onOpen }) => {
    const [type, setType] = useState("");
    const [sort, setSort] = useState("reactions");
    const filtered = useMemo(() => {
        let arr = resources || [];
        if (type) arr = arr.filter((r) => (r.type || "other") === type);
        const scored = arr.map((r) => {
            const rxs = r.reactions || {};
            const rxTotal = Object.values(rxs).reduce((n, v) => n + (Array.isArray(v) ? v.length : (Number(v) || 0)), 0);
            return { ...r, __rx: rxTotal };
        });
        if (sort === "reactions") scored.sort((a, b) => b.__rx - a.__rx);
        if (sort === "newest") scored.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        if (sort === "alpha") scored.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        return scored.slice(0, 8);
    }, [resources, type, sort]);
    if (!resources || resources.length === 0) return null;
    return (
        <section data-testid="learning-trending" className="space-y-3">
            <div>
                <h3 className="font-heading text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                    <Sparkles className="h-6 w-6 text-red-500" /> Trending across Odyssey
                </h3>
                <p className="text-sm text-slate-500 mt-1">Most-reacted resources this month</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                {TRENDING_TYPES.map((t) => (
                    <button
                        key={t.k || "all"}
                        type="button"
                        onClick={() => setType(t.k)}
                        data-testid={`trending-filter-${t.k || "all"}`}
                        className={`h-8 px-3.5 rounded-full text-xs font-semibold border transition-colors ${
                            type === t.k
                                ? "bg-slate-900 text-white border-slate-900"
                                : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                        }`}
                    >
                        {t.k && (TYPE_ICON[t.k] ? React.createElement(TYPE_ICON[t.k], { className: "h-3 w-3 inline mr-1" }) : null)}
                        {t.label}
                    </button>
                ))}
                <span className="text-xs text-slate-500 ml-auto">{resources.length} resources</span>
                <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    data-testid="trending-sort"
                    className="h-8 bg-white border border-slate-200 rounded-lg px-2 text-xs text-slate-700"
                >
                    <option value="reactions">Sort: Most reacted</option>
                    <option value="newest">Sort: Newest</option>
                    <option value="alpha">Sort: A → Z</option>
                </select>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {filtered.map((r) => {
                    const Ic = TYPE_ICON[r.type] || TYPE_ICON.other;
                    return (
                        <button
                            key={r.id}
                            type="button"
                            data-testid={`trending-card-${r.id}`}
                            onClick={() => onOpen?.(r)}
                            className="group text-left bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-[#2EB4AC] hover:shadow-lg hover:-translate-y-0.5 transition-all"
                        >
                            <div className={`h-24 flex items-center justify-center relative ${
                                r.type === "video" ? "bg-gradient-to-br from-emerald-100 to-emerald-200"
                                : r.type === "article" ? "bg-gradient-to-br from-blue-100 to-indigo-200"
                                : r.type === "course" ? "bg-gradient-to-br from-amber-100 to-yellow-200"
                                : r.type === "podcast" ? "bg-gradient-to-br from-pink-100 to-fuchsia-200"
                                : "bg-gradient-to-br from-slate-100 to-slate-200"
                            }`}>
                                <Ic className="h-10 w-10 text-slate-700 opacity-80" />
                                <span className="absolute top-2 left-2 bg-white/90 text-slate-800 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider">
                                    {TYPE_LABEL[r.type] || "Other"}
                                </span>
                            </div>
                            <div className="p-3.5">
                                <div className="font-heading font-bold text-sm text-slate-900 line-clamp-2 group-hover:text-[#25908A] leading-snug">
                                    {r.name || "Untitled"}
                                </div>
                                <div className="text-[11px] text-slate-500 mt-1.5 truncate">{r.uploaded_by_name || r.owner_name || "—"}</div>
                                <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
                                    <span className="truncate">{r.owner_department_name || ""}</span>
                                    <span className="font-semibold text-slate-700 flex items-center gap-1 shrink-0">
                                        <Heart className="h-3 w-3 text-red-500 fill-red-500" /> {r.__rx || 0}
                                    </span>
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>
        </section>
    );
};




/** Inline nested view — pixel-perfect port of the mockup's inside-a-
 * collection layout. Renders the peach → mint page header (breadcrumb
 * = "My Team's Library / Collection / Folder…"), a toolbar with
 * navigable crumbs + filter + `＋ New` menu, then a `Folders` grid and
 * a `Resources` grid using the mockup's `.card` styling. Backed by the
 * real backend endpoints (`/learning/collections/{cid}/resources` +
 * `/collections/{cid}/folders`); clicking a resource opens its URL and
 * pings the "opened" endpoint so `learning_status` gets recorded. */
/** Folder card for the inline nested view — mockup `.card` styling plus
 * a hover-revealed `⋮` menu (rename / delete). Also a drop target: a
 * resource dragged from another folder (or root) can be dropped here to
 * move it into this folder via `PUT /learning/resources/{rid}` with a
 * new `folder_id`. */
const InsideFolderCard = ({ folder, color, canManage, onOpen, onRename, onDelete, onAddSubfolder, onMove, onDropResource, onDropFolder }) => {
    const [menu, setMenu] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    useEffect(() => {
        const close = () => setMenu(false);
        document.addEventListener("click", close);
        return () => document.removeEventListener("click", close);
    }, []);
    return (
        <div
            className={`card ${dragOver ? "drop-target" : ""}`}
            data-testid={`inside-folder-${folder.id}`}
            onClick={onOpen}
            draggable={canManage}
            onDragStart={(e) => {
                if (!canManage) return;
                e.stopPropagation();
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("application/x-lh-folder", folder.id);
            }}
            onDragOver={(e) => {
                const t = e.dataTransfer.types;
                if (t.includes("application/x-lh-resource") || t.includes("application/x-lh-folder")) {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    if (!dragOver) setDragOver(true);
                }
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
                setDragOver(false);
                const rid = e.dataTransfer.getData("application/x-lh-resource");
                const fid = e.dataTransfer.getData("application/x-lh-folder");
                if (!rid && !fid) return;
                e.preventDefault();
                if (rid) onDropResource?.(rid, folder.id);
                if (fid && fid !== folder.id) onDropFolder?.(fid, folder.id);
            }}
        >
            <div className="card-accent" style={{ background: `var(--${color})` }} />
            {canManage && (
                <>
                    <button
                        type="button"
                        className="card-menu-btn"
                        onClick={(e) => { e.stopPropagation(); setMenu((v) => !v); }}
                        data-testid={`folder-menu-${folder.id}`}
                        aria-label="Folder actions"
                    >⋮</button>
                    <div
                        className={`card-dropdown ${menu ? "open" : ""}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div data-testid={`folder-add-subfolder-${folder.id}`} onClick={() => { setMenu(false); onAddSubfolder?.(folder); }}>Add subfolder</div>
                        <div data-testid={`folder-rename-${folder.id}`} onClick={() => { setMenu(false); onRename(); }}>Rename</div>
                        <div data-testid={`folder-move-${folder.id}`} onClick={() => { setMenu(false); onMove?.(folder); }}>Move to…</div>
                        <div className="danger" data-testid={`folder-delete-${folder.id}`} onClick={() => { setMenu(false); onDelete(); }}>Delete</div>
                    </div>
                </>
            )}
            <div className="card-inner">
                <div className="c-icon" style={{ background: `var(--${color}-tint)`, color: `var(--${color})` }}>▢</div>
                <div className="c-name">{folder.name}</div>
                <div className="c-meta-row">
                    <span className="c-meta">Folder</span>
                    {(folder.subfolder_count || 0) > 0 && (
                        <span className="c-meta">{folder.subfolder_count} subfolder{folder.subfolder_count === 1 ? "" : "s"}</span>
                    )}
                    <span className="c-meta">{folder.resource_count || 0} resource{(folder.resource_count || 0) === 1 ? "" : "s"}</span>
                </div>
            </div>
        </div>
    );
};

/** Resource card for the inline nested view — matches the mockup's
 * clean `.card` styling exactly (colored top bar, tinted icon, name,
 * progress bar, level·duration + avatar meta row). Every legacy action
 * lives in the `⋮` dropdown menu at top-right so the card face stays
 * uncluttered:
 *   • Open link
 *   • Mark: Not started / In progress / Completed / Skipped
 *   • React ▾ (emoji picker)
 *   • Learnings (inline expandable notes panel)
 *   • Assign
 *   • Add to / move to another collection
 *   • Edit
 *   • Delete
 * Click on the card body opens the URL directly (most common action). */
const InsideResourceCard = ({
    resource, color, canManage, allUsers,
    onEdit, onDelete, onAssign, onMove, onToggleNotes, onSetStatus, onReload, onReorder,
}) => {
    const [menu, setMenu] = useState(false);
    const [showRx, setShowRx] = useState(false);
    const [reorderMark, setReorderMark] = useState(null); // "before" | "after" | null
    const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
    const menuBtnRef = useRef(null);
    useEffect(() => {
        const close = () => { setMenu(false); setShowRx(false); };
        document.addEventListener("click", close);
        return () => document.removeEventListener("click", close);
    }, []);
    // When the menu opens, compute the button's viewport position so we
    // can render the dropdown as a `position:fixed` portal that never
    // gets clipped by any ancestor's overflow. If it would extend past
    // the viewport, flip it above the button instead.
    const openMenu = (e) => {
        e.stopPropagation();
        if (menu) { setMenu(false); return; }
        const rect = menuBtnRef.current?.getBoundingClientRect();
        if (rect) {
            const MENU_H = 430; // approx dropdown height (14 items)
            const MENU_W = 212;
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            // Flip above when below is cramped AND above has more room.
            const openUp = spaceBelow < MENU_H && spaceAbove > spaceBelow;
            const top = openUp
                ? Math.max(8, rect.top - Math.min(MENU_H, spaceAbove - 12))
                : Math.min(rect.bottom + 6, window.innerHeight - Math.min(MENU_H, spaceBelow) - 8);
            const left = Math.max(8, Math.min(rect.right - MENU_W, window.innerWidth - MENU_W - 8));
            setMenuPos({ top, left });
        }
        setMenu(true);
        setShowRx(false);
    };
    const r = resource;
    const status = r.my_status || "not_started";
    const pct = status === "completed" ? 100 : status === "in_progress" ? 55 : 0;
    const iconGlyph = LH_FMT_ICON[String(r.type || "").toLowerCase()] || "▤";
    const by = r.uploaded_by_name || r.created_by_name || r.owner_name || "";
    const initials = String(by).split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?";
    const openHref = r.url || (r.storage_path ? `${API}/learning/resources/${r.id}/download` : "");
    const hasOpen = !!openHref;
    const openResource = async (e) => {
        e?.stopPropagation?.();
        try { await api.post(`/learning/resources/${r.id}/open`, {}); } catch { /* ignore */ }
        if (openHref) window.open(openHref, "_blank", "noopener");
    };
    // Count badges next to menu items so users see engagement without
    // opening the dropdown.
    const noteCount = r.notes_count || 0;
    const assignedCount = (r.assigned_to || []).length;
    const rxTotal = (() => {
        const rx = r.reactions || {};
        return Object.values(rx).reduce((n, v) => n + (Array.isArray(v) ? v.length : (Number(v) || 0)), 0);
    })();
    const STATUS_OPTS = [
        { v: "not_started", label: "Not started" },
        { v: "in_progress", label: "In progress" },
        { v: "completed",   label: "Completed" },
        { v: "skipped",     label: "Skipped" },
    ];
    const toggleReaction = async (key) => {
        try {
            await api.post(`/learning/resources/${r.id}/react`, { reaction: key });
            onReload?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    return (
        <div
            className={`lh-res-wrap ${reorderMark ? `reorder-${reorderMark}` : ""}`}
            data-testid={`inside-resource-${r.id}`}
            onDragOver={(e) => {
                if (!canManage) return;
                if (!e.dataTransfer.types.includes("application/x-lh-resource")) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                // Compute left/right based on mouse position within the card.
                const rect = e.currentTarget.getBoundingClientRect();
                const mark = (e.clientX - rect.left) < rect.width / 2 ? "before" : "after";
                if (mark !== reorderMark) setReorderMark(mark);
            }}
            onDragLeave={() => setReorderMark(null)}
            onDrop={(e) => {
                const draggedId = e.dataTransfer.getData("application/x-lh-resource");
                const pos = reorderMark || "after";
                setReorderMark(null);
                if (!draggedId || draggedId === r.id) return;
                e.preventDefault();
                e.stopPropagation();
                onReorder?.(draggedId, r.id, pos);
            }}
        >
            <div
                className="card"
                onClick={hasOpen ? openResource : undefined}
                style={hasOpen ? {} : { cursor: "default" }}
                draggable={canManage}
                onDragStart={(e) => {
                    if (!canManage) return;
                    e.dataTransfer.setData("application/x-lh-resource", r.id);
                    e.dataTransfer.effectAllowed = "move";
                }}
            >
                <div className="card-accent" style={{ background: `var(--${color})` }} />
                <button
                    type="button"
                    ref={menuBtnRef}
                    className="card-menu-btn"
                    onClick={openMenu}
                    data-testid={`resource-menu-${r.id}`}
                    aria-label="Resource actions"
                >⋮</button>
                <div className="card-inner">
                    <div className="c-icon" style={{ background: `var(--${color}-tint)`, color: `var(--${color})` }}>{iconGlyph}</div>
                    <div className="c-name">{r.name || "Untitled resource"}</div>
                    {pct > 0 && (
                        <div className="res-progress-track">
                            <div className="res-progress-fill" style={{ width: `${pct}%`, background: `var(--${color})` }} />
                        </div>
                    )}
                    <div className="c-meta-row">
                        <span className="c-meta">
                            {r.level ? (
                                <>
                                    {r.level === "beginner" ? "Beginner" : r.level === "intermediate" ? "Intermediate" : r.level === "advanced" ? "Advanced" : "Resource"}
                                    {r.duration_minutes ? <> · <span className="mono">{r.duration_minutes}m</span></> : null}
                                </>
                            ) : (
                                <>
                                    {(TYPE_LABEL[r.type] || "Resource")}
                                    {r.duration_minutes ? <> · <span className="mono">{r.duration_minutes}m</span></> : null}
                                </>
                            )}
                        </span>
                        <span className="mini-avatar" title={by || undefined}>{initials}</span>
                    </div>
                </div>

                {/* Footer action strip — three most-used actions surfaced
                    directly on the card face so learners see them at a
                    glance. Everything else lives behind the ⋮ menu. */}
                <div className="c-actions" onClick={(e) => e.stopPropagation()}>
                    <button
                        type="button"
                        className="c-act"
                        title="Team learnings"
                        onClick={onToggleNotes}
                        data-testid={`toggle-notes-${r.id}`}
                    >
                        <MessageCircle className="h-3.5 w-3.5" />
                        <span className="c-act-label">Learnings{noteCount > 0 ? ` · ${noteCount}` : ""}</span>
                    </button>
                    <button
                        type="button"
                        className="c-act"
                        title="Assign to teammates"
                        onClick={onAssign}
                        data-testid={`assign-resource-${r.id}`}
                    >
                        <UserPlus className="h-3.5 w-3.5" />
                        <span className="c-act-label">Assign{assignedCount > 0 ? ` · ${assignedCount}` : ""}</span>
                    </button>
                    <button
                        type="button"
                        className="c-act"
                        title="Move / copy to another collection"
                        onClick={onMove}
                        data-testid={`addto-resource-${r.id}`}
                    >
                        <FolderInput className="h-3.5 w-3.5" />
                        <span className="c-act-label">Move</span>
                    </button>
                </div>
            </div>
            {/* Dropdown lives OUTSIDE the .card via a portal so no ancestor's
                overflow can clip it. Positioned with fixed coordinates
                computed from the ⋮ button's viewport rect. */}
            {menu && createPortal(
                <div
                    className="card-dropdown card-dropdown-wide lh-res-menu-fixed open"
                    style={{ top: menuPos.top, left: menuPos.left }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {hasOpen && (
                        <div data-testid={`open-resource-${r.id}`} onClick={(e) => { setMenu(false); openResource(e); }}>Open link ↗</div>
                    )}
                    <div className="cd-section">Mark as</div>
                    {STATUS_OPTS.map((o) => (
                        <div
                            key={o.v}
                            className={`cd-status ${status === o.v ? "cd-active" : ""}`}
                            data-testid={`status-${o.v}-${r.id}`}
                            onClick={() => { setMenu(false); onSetStatus(r.id, o.v); }}
                        >{status === o.v ? "✓ " : "   "}{o.label}</div>
                    ))}
                    <div className="cd-divider" />
                    <div
                        data-testid={`react-${r.id}`}
                        onClick={(e) => { e.stopPropagation(); setShowRx((v) => !v); }}
                    >
                        React {rxTotal > 0 ? `· ${rxTotal}` : ""} ▾
                    </div>
                    {showRx && (
                        <div className="cd-rx-row">
                            {REACTIONS.map((rx) => (
                                <button
                                    key={rx.key}
                                    type="button"
                                    className="cd-rx"
                                    title={rx.label}
                                    onClick={() => { setMenu(false); setShowRx(false); toggleReaction(rx.key); }}
                                >{rx.glyph}</button>
                            ))}
                        </div>
                    )}
                    <div
                        data-testid={`toggle-notes-${r.id}`}
                        onClick={() => { setMenu(false); onToggleNotes(); }}
                    >Learnings {noteCount > 0 ? `· ${noteCount}` : ""}</div>
                    <div
                        data-testid={`assign-resource-${r.id}`}
                        onClick={() => { setMenu(false); onAssign(); }}
                    >Assign {assignedCount > 0 ? `· ${assignedCount}` : ""}</div>
                    <div
                        data-testid={`move-resource-${r.id}`}
                        onClick={() => { setMenu(false); onMove(); }}
                    >Add to / Move</div>
                    {canManage && (
                        <>
                            <div className="cd-divider" />
                            <div data-testid={`edit-resource-${r.id}`} onClick={() => { setMenu(false); onEdit(); }}>Edit</div>
                            <div className="danger" data-testid={`delete-resource-${r.id}`} onClick={() => { setMenu(false); onDelete(); }}>Delete</div>
                        </>
                    )}
                </div>,
                document.body,
            )}
        </div>
    );
};

/** Lightweight wrapper that fetches notes for one resource and reuses
 * the legacy UserNotesPanel UI. Keeps the InsideResourceCard tidy. */
const InlineNotesPanel = ({ resourceId, onChanged }) => {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const load = useCallback(async () => {
        try {
            const r = await api.get(`/learning/resources/${resourceId}/notes`);
            setNotes(r.data || []);
        } catch { setNotes([]); }
        finally { setLoading(false); }
    }, [resourceId]);
    useEffect(() => { load(); }, [load]);
    const addNote = async ({ text, url }) => {
        await api.post(`/learning/resources/${resourceId}/notes`, { text, url });
        await load(); onChanged?.();
    };
    const editNote = async (nid, patch) => {
        await api.patch(`/learning/notes/${nid}`, patch);
        await load(); onChanged?.();
    };
    const deleteNote = async (nid) => {
        await api.delete(`/learning/notes/${nid}`);
        await load(); onChanged?.();
    };
    if (loading) return <div style={{ padding: 16, textAlign: "center", fontSize: 12, color: "var(--lh-slate)" }}>Loading learnings…</div>;
    return <UserNotesPanel notes={notes} onAdd={addNote} onEdit={editNote} onDelete={deleteNote} resourceId={resourceId} />;
};

/** Collection-level `Learnings` tab — every team contribution across every
 * resource in the collection, grouped by resource in a mockup-styled
 * card. Renders the full composer per group so users can add notes
 * without leaving the tab. When `focusRid` is set the matching card is
 * scrolled into view and highlighted. */
const CollectionLearningsPanel = ({ collectionId, focusRid, onFocusHandled, onChanged }) => {
    const [payload, setPayload] = useState({ notes: [], resources: [] });
    const [loading, setLoading] = useState(true);
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get(`/learning/collections/${collectionId}/notes`);
            setPayload(r.data || { notes: [], resources: [] });
        } catch {
            setPayload({ notes: [], resources: [] });
        } finally { setLoading(false); }
    }, [collectionId]);
    useEffect(() => { load(); }, [load]);

    // Group notes under their parent resource so contributors are grouped
    // by what they're actually about, not by author.
    const byRid = useMemo(() => {
        const m = {};
        for (const n of payload.notes || []) {
            if (!m[n.resource_id]) m[n.resource_id] = [];
            m[n.resource_id].push(n);
        }
        return m;
    }, [payload.notes]);

    // Show resources with notes first, then resources without so users
    // can also SEE what's still empty and jump in to contribute.
    const ordered = useMemo(() => {
        const withNotes = (payload.resources || []).filter((r) => (byRid[r.id]?.length || 0) > 0);
        const withoutNotes = (payload.resources || []).filter((r) => !(byRid[r.id]?.length));
        return [...withNotes, ...withoutNotes];
    }, [payload.resources, byRid]);

    // Scroll the focus card into view once notes are loaded.
    const refs = useRef({});
    useEffect(() => {
        if (!focusRid || loading) return;
        const el = refs.current[focusRid];
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
            el.classList.add("focus-flash");
            setTimeout(() => el.classList.remove("focus-flash"), 1400);
        }
        onFocusHandled?.();
    }, [focusRid, loading, onFocusHandled]);

    const addNote = async (rid, { text, url }) => {
        await api.post(`/learning/resources/${rid}/notes`, { text, url });
        await load(); onChanged?.();
    };
    const editNote = async (nid, patch) => {
        await api.patch(`/learning/notes/${nid}`, patch);
        await load(); onChanged?.();
    };
    const deleteNote = async (nid) => {
        await api.delete(`/learning/notes/${nid}`);
        await load(); onChanged?.();
    };

    if (loading) return <div className="empty-state"><p>Loading team learnings…</p></div>;

    const totalNotes = payload.notes?.length || 0;
    return (
        <div className="learnings-panel" data-testid="learnings-panel">
            <div className="section-label">
                Team learnings · {totalNotes} contribution{totalNotes === 1 ? "" : "s"}
            </div>
            {(payload.resources || []).length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">💬</div>
                    <p>Add a resource to this collection first — then you and your teammates can drop learnings on it.</p>
                </div>
            ) : (
                <div className="learnings-list">
                    {ordered.map((r) => {
                        const notes = byRid[r.id] || [];
                        const iconGlyph = LH_FMT_ICON[String(r.type || "").toLowerCase()] || "▤";
                        const lvl = r.level ? (r.level.charAt(0).toUpperCase() + r.level.slice(1)) : (TYPE_LABEL[r.type] || "Resource");
                        return (
                            <div
                                key={r.id}
                                ref={(el) => { refs.current[r.id] = el; }}
                                className="learnings-block"
                                data-testid={`learnings-for-${r.id}`}
                            >
                                <div className="lb-head">
                                    <div className="lb-icon">{iconGlyph}</div>
                                    <div className="lb-title">
                                        <div className="lb-name">{r.name || "Untitled resource"}</div>
                                        <div className="lb-meta">
                                            {lvl}
                                            {notes.length > 0 && <> · <span className="mono">{notes.length}</span> learning{notes.length === 1 ? "" : "s"}</>}
                                        </div>
                                    </div>
                                </div>
                                <div className="lb-body">
                                    <UserNotesPanel
                                        notes={notes}
                                        resourceId={r.id}
                                        onAdd={(payload) => addNote(r.id, payload)}
                                        onEdit={editNote}
                                        onDelete={deleteNote}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export const InsideCollectionView = ({ collection, rootLabel, onExit, onChanged, viewMode, onViewMode }) => {
    const { user } = useAuth();
    const [folders, setFolders] = useState([]);
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    // Folder path from root → current. Each entry is a folder id.
    // Empty array = at collection root. Last entry = active folder id.
    const [folderPath, setFolderPath] = useState([]);
    const activeFolderId = folderPath.length ? folderPath[folderPath.length - 1] : null;
    const [activeTab, setActiveTab] = useState("resources"); // "resources" | "activity"
    const [query, setQuery] = useState("");
    const [typeFilter, setTypeFilter] = useState("");   // "" = all
    const [levelFilter, setLevelFilter] = useState(""); // "" = all
    const [menuOpen, setMenuOpen] = useState(false);
    const [newModal, setNewModal] = useState(null); // "folder" | "resource" | null
    const [showAssign, setShowAssign] = useState(false);
    const [editingCollection, setEditingCollection] = useState(false);
    // Add-subfolder + move-folder modals (initiated from the folder
    // card's kebab menu). Both accept a folder object payload.
    const [addSubTo, setAddSubTo] = useState(null);      // folder to add subfolder into
    const [movingFolder, setMovingFolder] = useState(null); // folder to move
    // Per-resource modals: edit, move (add-to), assign, notes-expanded.
    const [editingRes, setEditingRes] = useState(null);   // resource object
    const [movingRes, setMovingRes] = useState(null);     // resource object
    const [assigningRes, setAssigningRes] = useState(null); // resource object
    // ID of the resource to jump-to inside the Learnings tab (set when
    // the user clicks the "Learnings" pill on a resource card).
    const [learningsFocus, setLearningsFocus] = useState(null);
    const [allUsers, setAllUsers] = useState([]);
    useEffect(() => {
        const close = () => setMenuOpen(false);
        document.addEventListener("click", close);
        return () => document.removeEventListener("click", close);
    }, []);
    // Load the org user list once (used by assign + assignee avatars).
    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/users").catch(() => ({ data: [] }));
                setAllUsers(r.data || []);
            } catch { /* ignore */ }
        })();
    }, []);

    // Assign / edit / delete permission — the backend is democratic
    // (`can_manage: true` for everyone) but the Assign flow still needs
    // manager or super_admin. Keep the split to mirror the old dialog.
    const isManagerOrAdmin = user?.role === "super_admin" || user?.role === "manager";
    const canManage = !!collection.can_manage;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [fR, rR] = await Promise.all([
                api.get(`/learning/collections/${collection.id}/folders`).catch(() => ({ data: [] })),
                api.get(`/learning/collections/${collection.id}/resources`).catch(() => ({ data: [] })),
            ]);
            setFolders(fR.data || []);
            setResources(rR.data || []);
        } finally { setLoading(false); }
    }, [collection.id]);
    useEffect(() => { load(); }, [load]);

    // Reset drill-down when the collection changes.
    useEffect(() => { setFolderPath([]); setQuery(""); }, [collection.id]);

    // ------ Mutations (folder + resource + collection) --------------
    const renameFolder = async (f) => {
        const n = window.prompt("Rename folder to:", f.name);
        if (!n || n.trim() === f.name) return;
        try {
            const r = await api.patch(`/learning/learning-folders/${f.id}`, { name: n.trim() });
            setFolders((prev) => prev.map((x) => x.id === f.id ? { ...x, ...r.data } : x));
            toast.success("Folder renamed");
        } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't rename"); }
    };
    const deleteFolder = async (f) => {
        // Compute the destructive impact so the confirmation is honest.
        const subIds = new Set([f.id]);
        let frontier = [f.id];
        while (frontier.length) {
            const kids = folders.filter((x) => frontier.includes(x.parent_folder_id || null)).map((x) => x.id);
            if (!kids.length) break;
            kids.forEach((id) => subIds.add(id));
            frontier = kids;
        }
        const resourceCount = resources.filter((r) => subIds.has(r.folder_id)).length;
        const subCount = subIds.size - 1;
        const parts = [`Delete folder "${f.name}"?`];
        if (subCount > 0) parts.push(`This will also permanently delete ${subCount} subfolder${subCount === 1 ? "" : "s"}`);
        if (resourceCount > 0) parts.push(`${subCount > 0 ? "and" : "This will also permanently delete"} ${resourceCount} resource${resourceCount === 1 ? "" : "s"} inside`);
        parts.push("This action cannot be undone.");
        if (!window.confirm(parts.join(" · "))) return;
        try {
            const r = await api.delete(`/learning/learning-folders/${f.id}`);
            const nFolders = r?.data?.folders_removed || 1;
            const nRes = r?.data?.resources_removed || 0;
            // Optimistic local cleanup — folders removed, resources gone.
            setFolders((prev) => prev.filter((x) => !subIds.has(x.id)));
            setResources((prev) => prev.filter((r) => !subIds.has(r.folder_id)));
            // If we're inside (a descendant of) the deleted folder,
            // bounce back up to the nearest surviving ancestor.
            if (folderPath.some((fid) => subIds.has(fid))) {
                setFolderPath((p) => p.slice(0, p.findIndex((fid) => subIds.has(fid))));
            }
            const msg = nRes > 0
                ? `Deleted ${nFolders} folder${nFolders === 1 ? "" : "s"} + ${nRes} resource${nRes === 1 ? "" : "s"}`
                : (nFolders > 1 ? `Deleted folder + ${nFolders - 1} subfolder${nFolders - 1 === 1 ? "" : "s"}` : "Folder deleted");
            toast.success(msg);
            onChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete"); }
    };
    const setResourceStatus = async (rid, status) => {
        try {
            await api.put(`/learning/resources/${rid}/status`, { status });
            setResources((rs) => rs.map((r) => r.id === rid ? { ...r, my_status: status } : r));
            onChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    const removeResource = async (r) => {
        if (!window.confirm(`Remove "${r.name}"?`)) return;
        try {
            await api.delete(`/learning/resources/${r.id}`);
            setResources((rs) => rs.filter((x) => x.id !== r.id));
            onChanged?.();
            toast.success("Resource removed");
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    const deleteCollection = async () => {
        if (!window.confirm(`Delete "${collection.name}" and everything inside it?`)) return;
        try {
            await api.delete(`/learning/collections/${collection.id}`);
            toast.success("Collection deleted");
            onExit?.();
            onChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
    };
    // Drag & drop: move a resource between folders / to root by patching
    // its `folder_id` via the same endpoint the modal uses. Optimistic
    // update so the card jumps immediately; toast + reload on error.
    const moveResourceToFolder = async (rid, targetFolderId) => {
        // Compare against current state — no-op if the resource is
        // already in the target folder.
        const cur = resources.find((x) => x.id === rid);
        if (!cur) return;
        const currentFid = cur.folder_id || null;
        const nextFid = targetFolderId || null;
        if (currentFid === nextFid) return;
        // Optimistic update
        setResources((rs) => rs.map((x) => x.id === rid ? { ...x, folder_id: nextFid } : x));
        setFolders((fs) => fs.map((f) => {
            if (f.id === currentFid) return { ...f, resource_count: Math.max(0, (f.resource_count || 0) - 1) };
            if (f.id === nextFid) return { ...f, resource_count: (f.resource_count || 0) + 1 };
            return f;
        }));
        try {
            await api.put(`/learning/resources/${rid}`, { folder_id: nextFid || "" });
            const dest = nextFid ? (folders.find((f) => f.id === nextFid)?.name || "folder") : "root";
            toast.success(`Moved to ${dest}`);
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Move failed");
            load();
        }
    };
    // Move a folder into another folder (or back to root). Guards
    // against cycles client-side; the backend also enforces this.
    const moveFolderToFolder = async (fid, targetParentId) => {
        const cur = folders.find((x) => x.id === fid);
        if (!cur) return;
        const currentParent = cur.parent_folder_id || null;
        const nextParent = targetParentId || null;
        if (currentParent === nextParent) return;
        if (nextParent === fid) return;
        // Reject nesting into own descendant (walk up the target's chain).
        if (nextParent) {
            let walker = folders.find((x) => x.id === nextParent);
            while (walker) {
                if (walker.id === fid) {
                    toast.error("Can't move a folder into one of its own subfolders");
                    return;
                }
                walker = walker.parent_folder_id ? folders.find((x) => x.id === walker.parent_folder_id) : null;
            }
        }
        setFolders((fs) => fs.map((x) => x.id === fid ? { ...x, parent_folder_id: nextParent } : x));
        try {
            await api.patch(`/learning/learning-folders/${fid}`, { parent_folder_id: nextParent || "" });
            const dest = nextParent ? (folders.find((f) => f.id === nextParent)?.name || "folder") : "root";
            toast.success(`Moved folder to ${dest}`);
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Move failed");
            load();
        }
    };
    // Root-crumb drop handler — drop a resource OR a folder onto any
    // breadcrumb (root/collection/ancestor folder) to bring it back
    // out of the current nesting level.
    const dropOnCrumb = (targetFolderId) => (e) => {
        const rid = e.dataTransfer.getData("application/x-lh-resource");
        const fid = e.dataTransfer.getData("application/x-lh-folder");
        if (!rid && !fid) return;
        e.preventDefault();
        if (rid) moveResourceToFolder(rid, targetFolderId);
        if (fid) moveFolderToFolder(fid, targetFolderId);
    };

    // Reorder resources in the CURRENT visible list (same folder /
    // root). Called from the card drag handlers below. Optimistically
    // updates local state then persists the new order.
    const reorderResources = async (draggedId, targetId, position) => {
        if (draggedId === targetId) return;
        // Only reorder within the current visible slice — never
        // shuffle across folders (folder drop handles that).
        const scope = resources.filter((r) => (r.folder_id || null) === activeFolderId);
        const draggedIdx = scope.findIndex((r) => r.id === draggedId);
        const targetIdx = scope.findIndex((r) => r.id === targetId);
        if (draggedIdx < 0 || targetIdx < 0) return;
        const next = scope.slice();
        const [moved] = next.splice(draggedIdx, 1);
        const insertAt = position === "before" ? (targetIdx < draggedIdx ? targetIdx : targetIdx - 1) : (targetIdx < draggedIdx ? targetIdx + 1 : targetIdx);
        next.splice(Math.max(0, insertAt), 0, moved);
        // Merge scope back into full resources array preserving the
        // order of resources that live in OTHER folders.
        const outside = resources.filter((r) => (r.folder_id || null) !== activeFolderId);
        setResources([...next, ...outside]);
        try {
            await api.put(`/learning/collections/${collection.id}/resources/reorder`, {
                resource_ids: next.map((r) => r.id),
            });
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reorder failed");
            load();
        }
    };

    const deptName = collection.owner_department_name || "";
    const color = lhDeptColor(deptName);
    const q = query.trim().toLowerCase();
    // Resolve every ancestor folder in the current path so we can
    // render the breadcrumb + know the "active" folder object.
    const foldersById = useMemo(() => {
        const m = {};
        folders.forEach((f) => { m[f.id] = f; });
        return m;
    }, [folders]);
    const pathFolders = folderPath.map((fid) => foldersById[fid]).filter(Boolean);
    const activeFolder = pathFolders[pathFolders.length - 1] || null;

    // Filter set: at every level, show child folders of the current
    // node (matched by parent_folder_id) PLUS every resource whose
    // folder_id == current. Text-filter overrides + narrows both.
    const subfolderCountsById = useMemo(() => {
        const m = {};
        folders.forEach((f) => {
            const p = f.parent_folder_id || null;
            if (!p) return;
            m[p] = (m[p] || 0) + 1;
        });
        return m;
    }, [folders]);
    const visibleFolders = useMemo(() => {
        return folders
            .filter((f) => (f.parent_folder_id || null) === activeFolderId)
            .filter((f) => !q || (f.name || "").toLowerCase().includes(q))
            .map((f) => ({ ...f, subfolder_count: subfolderCountsById[f.id] || 0 }));
    }, [folders, activeFolderId, q, subfolderCountsById]);

    const visibleResources = useMemo(() => {
        return resources
            .filter((r) => (r.folder_id || null) === activeFolderId)
            .filter((r) => !typeFilter || (r.type || "") === typeFilter)
            .filter((r) => !levelFilter || (r.level || "") === levelFilter)
            .filter((r) => !q || (r.name || "").toLowerCase().includes(q) || (r.description || "").toLowerCase().includes(q));
    }, [resources, activeFolderId, q, typeFilter, levelFilter]);

    const openResource = async (r) => {
        // Record "opened" for the receipts feature; non-fatal.
        try { await api.post(`/learning/resources/${r.id}/open`, {}); } catch { /* ignore */ }
        const url = r.url || (r.storage_path ? `${API}/learning/resources/${r.id}/download` : "");
        if (url) window.open(url, "_blank", "noopener");
        else toast.info("This resource has no URL — open the details dialog to add one.");
    };

    // Format-icon glyph mapping (mockup's ▶ / ▤ / ✓ / ⌘).
    const iconFor = (type) => LH_FMT_ICON[String(type || "").toLowerCase()] || "▤";
    // my_status → progress percent for the .res-progress-fill bar.
    const progressPct = (r) => {
        const s = r.my_status || r.status || "not_started";
        if (s === "completed") return 100;
        if (s === "in_progress") return 55;
        return 0;
    };
    // Short "By" avatar initials.
    const initialsOf = (name = "") => String(name).split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?";

    return (
        <>
            <div
                className="page-header"
                data-testid="inside-collection-header"
                style={
                    (collection.cover_image || "").trim()
                        ? {
                            backgroundImage: `linear-gradient(to bottom, rgba(255,255,255,.72), rgba(255,255,255,.98)), url(${collection.cover_image})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                          }
                        : undefined
                }
            >
                <div className="ph-orb o1" /><div className="ph-orb o2" />
                {onViewMode && (
                    <div className="ph-view-toggle">
                        <button type="button" onClick={() => onViewMode("shelf")} className={viewMode === "shelf" ? "active" : ""}>Shelf</button>
                        <button type="button" onClick={() => onViewMode("mindmap")} className={viewMode === "mindmap" ? "active" : ""}>Mind Map</button>
                    </div>
                )}
                <div className="ph-inner">
                    <div className="ph-eyebrow">
                        <span>Learning Hub</span>
                        <span className="sep">/</span>
                        <span>{rootLabel}</span>
                        <span className="sep">/</span>
                        <span>{collection.name || "Collection"}</span>
                        {pathFolders.map((pf) => (
                            <React.Fragment key={pf.id}>
                                <span className="sep">/</span>
                                <span>{pf.name}</span>
                            </React.Fragment>
                        ))}
                    </div>
                    <h1>{activeFolder ? activeFolder.name : (collection.name || "Untitled collection")}</h1>
                    {collection.description && !activeFolder && <p>{collection.description}</p>}
                    <div className="ph-stats">
                        <div className="ph-stat-chip"><span className="val mono">{folders.length}</span> folder{folders.length === 1 ? "" : "s"}</div>
                        <div className="ph-stat-chip"><span className="val mono">{resources.length}</span> resource{resources.length === 1 ? "" : "s"}</div>
                        {deptName && <div className="ph-stat-chip" style={{ background: `var(--${color}-tint)`, color: `var(--${color}-deep)`, border: "none" }}>{lhDeptShort(deptName)}</div>}
                    </div>
                </div>
            </div>

            <main className="content">
                <div className="toolbar" data-testid="inside-toolbar">
                    <div className="crumbs">
                        <button type="button" className="crumb" onClick={onExit} data-testid="inside-crumb-root">{rootLabel}</button>
                        <span className="crumb-sep">/</span>
                        <button
                            type="button"
                            className={`crumb ${!activeFolderId ? "current" : ""}`}
                            onClick={() => setFolderPath([])}
                            onDragOver={(e) => {
                                const t = e.dataTransfer.types;
                                if (t.includes("application/x-lh-resource") || t.includes("application/x-lh-folder")) {
                                    e.preventDefault();
                                    e.dataTransfer.dropEffect = "move";
                                }
                            }}
                            onDrop={dropOnCrumb(null)}
                            data-testid="inside-crumb-collection"
                        >{collection.name || "Collection"}</button>
                        {pathFolders.map((pf, idx) => {
                            const isLast = idx === pathFolders.length - 1;
                            return (
                                <React.Fragment key={pf.id}>
                                    <span className="crumb-sep">/</span>
                                    <button
                                        type="button"
                                        className={`crumb ${isLast ? "current" : ""}`}
                                        onClick={() => setFolderPath(folderPath.slice(0, idx + 1))}
                                        onDragOver={(e) => {
                                            const t = e.dataTransfer.types;
                                            if (t.includes("application/x-lh-resource") || t.includes("application/x-lh-folder")) {
                                                e.preventDefault();
                                                e.dataTransfer.dropEffect = "move";
                                            }
                                        }}
                                        onDrop={dropOnCrumb(pf.id)}
                                        data-testid={`inside-crumb-folder-${pf.id}`}
                                    >{pf.name}</button>
                                </React.Fragment>
                            );
                        })}
                    </div>
                    <div className="search-mini">
                        ⌕
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Filter this view..."
                            data-testid="inside-filter-input"
                        />
                    </div>
                    <div className="tb-actions">
                        {isManagerOrAdmin && (
                            <button
                                type="button"
                                className="btn btn-ghost"
                                data-testid="inside-assign-btn"
                                onClick={() => setShowAssign(true)}
                                title="Assign this collection to your managees"
                            >⇢ Assign</button>
                        )}
                        {canManage && (
                            <button
                                type="button"
                                className="btn btn-ghost"
                                data-testid="inside-edit-collection"
                                onClick={() => setEditingCollection(true)}
                                title="Edit collection name / description / skills"
                            >✎ Edit</button>
                        )}
                        {canManage && (
                            <button
                                type="button"
                                className="btn btn-ghost"
                                data-testid="inside-delete-collection"
                                onClick={deleteCollection}
                                title="Delete this collection"
                                style={{ color: "var(--lh-coral-deep)" }}
                            >🗑 Delete</button>
                        )}
                        <div style={{ position: "relative" }}>
                            <button
                                type="button"
                                className="btn btn-primary"
                                data-testid="inside-new-btn"
                                onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
                            >＋ New</button>
                            <div className={`new-menu ${menuOpen ? "open" : ""}`} onClick={(e) => e.stopPropagation()}>
                                <div
                                    className="new-menu-item"
                                    data-testid="inside-new-folder"
                                    onClick={() => { setMenuOpen(false); setNewModal("folder"); }}
                                ><span className="nmi-icon">▢</span> New folder{activeFolder ? ` in "${activeFolder.name}"` : ""}</div>
                                <div className="new-menu-divider" />
                                <div
                                    className="new-menu-item"
                                    data-testid="inside-new-resource"
                                    onClick={() => { setMenuOpen(false); setNewModal("resource"); }}
                                ><span className="nmi-icon">▤</span> Add resource</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Resources / Learnings / Activity tab bar — mirrors the mockup
                    section labels while giving the collection its own local
                    sub-nav. Learnings collects every team contribution across
                    every resource so you can browse them in one place. */}
                <div className="inside-tabs" data-testid="inside-tabs">
                    {[
                        { key: "resources", label: `Resources · ${resources.length}` },
                        { key: "learnings", label: "Learnings" },
                        { key: "activity",  label: "Activity" },
                    ].map((t) => (
                        <button
                            key={t.key}
                            type="button"
                            className={`inside-tab ${activeTab === t.key ? "active" : ""}`}
                            onClick={() => setActiveTab(t.key)}
                            data-testid={`inside-tab-${t.key}`}
                        >{t.label}</button>
                    ))}
                </div>

                {activeTab === "activity" ? (
                    <div className="inside-activity-wrap" data-testid="inside-activity">
                        <CollectionActivityPanel collectionId={collection.id} onChanged={() => { load(); onChanged?.(); }} />
                    </div>
                ) : activeTab === "learnings" ? (
                    <div className="inside-activity-wrap" data-testid="inside-learnings">
                        <CollectionLearningsPanel
                            collectionId={collection.id}
                            focusRid={learningsFocus}
                            onFocusHandled={() => setLearningsFocus(null)}
                            onChanged={() => { load(); onChanged?.(); }}
                        />
                    </div>
                ) : loading ? (
                    <div className="empty-state" data-testid="inside-loading"><p>Loading…</p></div>
                ) : (
                    <>
                        {/* Two-column layout: left-side filter rail + right-
                            side content (folders + resources). The rail
                            stacks Type & Level chip groups vertically. */}
                        <div className="lh-side-layout">
                            {(resources.length > 0 && !activeFolderId) && (
                                <aside className="lh-side-filters" data-testid="inside-filter-side">
                                    <div className="lh-side-group">
                                        <div className="lh-side-title">Type</div>
                                        <div className="lh-side-chips">
                                            <button
                                                type="button"
                                                onClick={() => setTypeFilter("")}
                                                className={`lh-chip lh-chip-sm ${!typeFilter ? "selected" : ""}`}
                                                data-testid="filter-type-all"
                                            >All</button>
                                            {[...new Set(resources.map((r) => r.type).filter(Boolean))].map((t) => (
                                                <button
                                                    key={t}
                                                    type="button"
                                                    onClick={() => setTypeFilter(t)}
                                                    className={`lh-chip lh-chip-sm ${typeFilter === t ? "selected" : ""}`}
                                                    data-testid={`filter-type-${t}`}
                                                >{LH_FMT_ICON[t] || "▤"} {TYPE_LABEL[t] || t}</button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="lh-side-group">
                                        <div className="lh-side-title">Level</div>
                                        <div className="lh-side-chips">
                                            {[
                                                { key: "",             label: "All" },
                                                { key: "beginner",     label: "● Beginner" },
                                                { key: "intermediate", label: "●● Intermediate" },
                                                { key: "advanced",     label: "●●● Advanced" },
                                            ].map((opt) => (
                                                <button
                                                    key={opt.key || "all"}
                                                    type="button"
                                                    onClick={() => setLevelFilter(opt.key)}
                                                    className={`lh-chip lh-chip-sm ${levelFilter === opt.key ? "selected" : ""}`}
                                                    data-testid={`filter-level-${opt.key || "all"}`}
                                                >{opt.label}</button>
                                            ))}
                                        </div>
                                    </div>
                                    {(typeFilter || levelFilter) && (
                                        <button
                                            type="button"
                                            className="lh-side-clear"
                                            onClick={() => { setTypeFilter(""); setLevelFilter(""); }}
                                            data-testid="filter-clear"
                                        >Clear filters ×</button>
                                    )}
                                </aside>
                            )}
                            <div className="lh-side-main">
                                {visibleFolders.length > 0 && (
                                    <>
                                        <div className="section-label">{activeFolder ? "Subfolders" : "Folders"}</div>
                                        <div className="grid" data-testid="inside-folders-grid">
                                            {visibleFolders.map((f) => (
                                                <InsideFolderCard
                                                    key={f.id}
                                                    folder={f}
                                                    color={color}
                                                    canManage={canManage}
                                                    onOpen={() => setFolderPath((p) => [...p, f.id])}
                                                    onRename={() => renameFolder(f)}
                                                    onDelete={() => deleteFolder(f)}
                                                    onAddSubfolder={() => setAddSubTo(f)}
                                                    onMove={() => setMovingFolder(f)}
                                                    onDropResource={moveResourceToFolder}
                                                    onDropFolder={moveFolderToFolder}
                                                />
                                            ))}
                                        </div>
                                    </>
                                )}

                                {visibleResources.length > 0 && (
                                    <>
                                        <div className="section-label">Resources</div>
                                        <div className="grid" data-testid="inside-resources-grid">
                                            {visibleResources.map((r) => (
                                                <InsideResourceCard
                                                    key={r.id}
                                                    resource={r}
                                                    color={color}
                                                    canManage={canManage}
                                                    allUsers={allUsers}
                                                    onEdit={() => setEditingRes(r)}
                                                    onDelete={() => removeResource(r)}
                                                    onAssign={() => setAssigningRes(r)}
                                                    onMove={() => setMovingRes(r)}
                                                    onToggleNotes={() => { setLearningsFocus(r.id); setActiveTab("learnings"); }}
                                                    onSetStatus={setResourceStatus}
                                                    onReload={load}
                                                    onReorder={reorderResources}
                                                />
                                            ))}
                                        </div>
                                    </>
                                )}

                                {visibleFolders.length === 0 && visibleResources.length === 0 && (
                                    <div className="empty-state" data-testid="inside-empty">
                                        <div className="empty-icon">{activeFolderId ? "▢" : "◆"}</div>
                                        <p>{q
                                            ? `Nothing here matches "${q}".`
                                            : (activeFolderId
                                                ? "This folder is empty. Add a subfolder or the first resource with ＋ New."
                                                : "This collection is empty. Create a folder to organize, or add the first resource.")}
                                        </p>
                                        {!q && (
                                            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                                                <button type="button" className="btn btn-ghost" onClick={() => setNewModal("folder")} data-testid="inside-empty-new-folder">＋ New folder</button>
                                                <button type="button" className="btn btn-primary" onClick={() => setNewModal("resource")} data-testid="inside-empty-new-resource">＋ Add resource</button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </main>

            {showAssign && (
                <AssignDialog
                    collection={collection}
                    onClose={() => setShowAssign(false)}
                    onSaved={async () => { setShowAssign(false); onChanged?.(); }}
                />
            )}

            {newModal === "folder" && (
                <InlineFolderDialog
                    collectionId={collection.id}
                    parentFolderId={activeFolderId}
                    parentFolderName={activeFolder?.name}
                    onClose={() => setNewModal(null)}
                    onCreated={(created) => {
                        setNewModal(null);
                        if (created && created.id) {
                            setFolders((prev) => [...prev, { ...created, resource_count: 0 }]);
                            onChanged?.();
                        } else {
                            load(); onChanged?.();
                        }
                    }}
                />
            )}
            {/* Add subfolder — reuses InlineFolderDialog with parent
                pre-filled from the kebab-menu context (not the current
                view). Great for adding a subfolder anywhere without
                first navigating into the parent. */}
            {addSubTo && (
                <InlineFolderDialog
                    collectionId={collection.id}
                    parentFolderId={addSubTo.id}
                    parentFolderName={addSubTo.name}
                    onClose={() => setAddSubTo(null)}
                    onCreated={(created) => {
                        setAddSubTo(null);
                        if (created && created.id) {
                            setFolders((prev) => [...prev, { ...created, resource_count: 0 }]);
                            onChanged?.();
                        } else {
                            load(); onChanged?.();
                        }
                    }}
                />
            )}
            {/* Move folder — pick a target parent from the folder tree
                (excluding self + descendants + current parent). Root is
                always a valid target. */}
            {movingFolder && (
                <MoveFolderDialog
                    folder={movingFolder}
                    allFolders={folders}
                    onClose={() => setMovingFolder(null)}
                    onMove={async (targetParentId) => {
                        await moveFolderToFolder(movingFolder.id, targetParentId);
                        setMovingFolder(null);
                    }}
                />
            )}
            {newModal === "resource" && (
                <AddResourceDialog
                    cid={collection.id}
                    defaultFolderId={activeFolderId || null}
                    collections={[collection]}
                    onClose={() => setNewModal(null)}
                    onAdded={() => { setNewModal(null); load(); onChanged?.(); }}
                />
            )}

            {/* Edit resource — reuses AddResourceDialog in edit mode. */}
            {editingRes && (
                <AddResourceDialog
                    cid={collection.id}
                    existing={editingRes}
                    collections={[collection]}
                    onClose={() => setEditingRes(null)}
                    onUpdated={() => { setEditingRes(null); load(); onChanged?.(); }}
                />
            )}
            {/* Assign resource — legacy per-resource dialog. */}
            {assigningRes && (
                <AssignResourceDialog
                    resource={assigningRes}
                    collection={collection}
                    allUsers={allUsers}
                    onClose={() => setAssigningRes(null)}
                    onSaved={async () => { setAssigningRes(null); await load(); onChanged?.(); }}
                />
            )}
            {/* Move / copy resource across collections. */}
            {movingRes && (
                <MoveResourceDialog
                    resource={movingRes}
                    onClose={() => setMovingRes(null)}
                    onMoved={async () => { setMovingRes(null); await load(); onChanged?.(); }}
                />
            )}
            {/* Edit collection — reuses the create dialog in edit mode. */}
            {editingCollection && (
                <CreateCollectionDialog
                    existing={collection}
                    onClose={() => setEditingCollection(false)}
                    onCreated={async () => { setEditingCollection(false); onChanged?.(); }}
                />
            )}
        </>
    );
};

/** Minimal HTML-styled "New folder" prompt that reuses the mockup's
 * modal chrome. Kept inline so we don't have to route through the huge
 * legacy CreateFolderDialog for a two-field form. */
const InlineFolderDialog = ({ collectionId, parentFolderId, parentFolderName, onClose, onCreated }) => {
    const [name, setName] = useState("");
    const [saving, setSaving] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        const nm = name.trim();
        if (!nm) return;
        setSaving(true);
        try {
            const payload = { name: nm };
            if (parentFolderId) payload.parent_folder_id = parentFolderId;
            const r = await api.post(`/learning/collections/${collectionId}/folders`, payload);
            toast.success(`Folder "${nm}" created${parentFolderName ? ` in "${parentFolderName}"` : ""}`);
            // Hand the freshly-created folder back to the parent so it
            // can patch local state instead of doing a full "Loading…"
            // refetch — keeps the view snappy per user's smoothness ask.
            onCreated?.(r?.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to create folder");
        } finally { setSaving(false); }
    };
    return createPortal(
        <div className="lh-shell">
            <div
                onClick={onClose}
                style={{ position: "fixed", inset: 0, background: "rgba(14,32,39,.5)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 20 }}
                data-testid="inline-folder-modal"
            >
                <form
                    onClick={(e) => e.stopPropagation()}
                    onSubmit={submit}
                    style={{ width: 460, maxWidth: "100%", background: "var(--lh-surface)", borderRadius: 18, boxShadow: "var(--lh-shadow-lg)", overflow: "hidden" }}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "19px 21px", borderBottom: "1px solid var(--lh-line)" }}>
                        <h3 style={{ fontFamily: "'Outfit', ui-sans-serif, system-ui, sans-serif", fontSize: 17.5, fontWeight: 700, color: "var(--lh-ink)" }}>
                            New folder{parentFolderName ? ` in "${parentFolderName}"` : ""}
                        </h3>
                        <button type="button" onClick={onClose} style={{ width: 27, height: 27, borderRadius: 8, border: "1px solid var(--lh-line)", background: "var(--lh-bg)", fontSize: 13, color: "var(--lh-slate)", cursor: "pointer" }}>✕</button>
                    </div>
                    <div style={{ padding: "19px 21px 21px" }}>
                        {parentFolderName && (
                            <div style={{ fontSize: 12, color: "var(--lh-slate)", marginBottom: 10 }}>
                                This subfolder will live inside <strong style={{ color: "var(--lh-ink)" }}>{parentFolderName}</strong>.
                            </div>
                        )}
                        <label style={{ display: "block", fontSize: 12, fontWeight: 700, marginBottom: 6, color: "var(--lh-ink)" }}>Folder name</label>
                        <input
                            autoFocus
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Bidding & Budgets"
                            data-testid="inline-folder-name"
                            style={{ width: "100%", border: "1px solid var(--lh-line)", borderRadius: 9, padding: "10px 12px", fontSize: 13.5, background: "var(--lh-bg)", outline: "none", color: "var(--lh-ink)" }}
                        />
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 9, marginTop: 15 }}>
                            <button type="button" onClick={onClose} className="btn btn-ghost">Cancel</button>
                            <button type="submit" disabled={saving} className="btn btn-primary" data-testid="inline-folder-submit">{saving ? "Creating…" : "Create folder"}</button>
                        </div>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
};


/** Move-folder picker — lists every eligible target folder in the
 * current collection (excludes self + descendants + current parent).
 * Root (= collection) is always an option so users can bring a nested
 * folder back out to the top level. Indentation reflects tree depth
 * so users can spot the right destination in a large collection. */
const MoveFolderDialog = ({ folder, allFolders, onClose, onMove }) => {
    const [saving, setSaving] = useState(false);
    // Build the set of forbidden IDs: the folder itself + every
    // descendant. Users can't move a folder into its own subtree.
    const forbidden = useMemo(() => {
        const s = new Set([folder.id]);
        let frontier = [folder.id];
        while (frontier.length) {
            const kids = allFolders.filter((x) => frontier.includes(x.parent_folder_id || null)).map((x) => x.id);
            if (!kids.length) break;
            kids.forEach((id) => s.add(id));
            frontier = kids;
        }
        return s;
    }, [folder.id, allFolders]);
    const currentParent = folder.parent_folder_id || null;
    // Build a depth-indexed list of every candidate folder (DFS).
    const options = useMemo(() => {
        const byParent = {};
        allFolders.forEach((f) => {
            const p = f.parent_folder_id || null;
            (byParent[p] = byParent[p] || []).push(f);
        });
        Object.values(byParent).forEach((arr) => arr.sort((a, b) => (a.name || "").localeCompare(b.name || "")));
        const out = [];
        const walk = (parentId, depth) => {
            for (const f of (byParent[parentId] || [])) {
                if (!forbidden.has(f.id)) out.push({ f, depth });
                walk(f.id, depth + 1);
            }
        };
        walk(null, 0);
        return out;
    }, [allFolders, forbidden]);
    const doMove = async (targetId) => {
        setSaving(true);
        try { await onMove(targetId); } finally { setSaving(false); }
    };
    return createPortal(
        <div className="lh-shell">
            <div
                onClick={onClose}
                style={{ position: "fixed", inset: 0, background: "rgba(14,32,39,.5)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 20 }}
                data-testid="move-folder-modal"
            >
                <div
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: 520, maxWidth: "100%", background: "var(--lh-surface)", borderRadius: 18, boxShadow: "var(--lh-shadow-lg)", overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: "85vh" }}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "19px 21px", borderBottom: "1px solid var(--lh-line)" }}>
                        <div>
                            <h3 style={{ fontFamily: "'Outfit', ui-sans-serif, system-ui, sans-serif", fontSize: 17.5, fontWeight: 700, color: "var(--lh-ink)" }}>Move &quot;{folder.name}&quot;</h3>
                            <p style={{ fontSize: 12, color: "var(--lh-slate)", marginTop: 3 }}>Pick a destination folder or move to the collection root.</p>
                        </div>
                        <button type="button" onClick={onClose} style={{ width: 27, height: 27, borderRadius: 8, border: "1px solid var(--lh-line)", background: "var(--lh-bg)", fontSize: 13, color: "var(--lh-slate)", cursor: "pointer" }}>✕</button>
                    </div>
                    <div style={{ padding: "12px 21px 21px", overflowY: "auto" }}>
                        {/* Root target */}
                        <button
                            type="button"
                            disabled={saving || currentParent === null}
                            onClick={() => doMove(null)}
                            data-testid="move-folder-target-root"
                            style={{
                                display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left",
                                padding: "10px 12px", borderRadius: 9,
                                border: "1px solid var(--lh-line)", background: currentParent === null ? "var(--lh-bg)" : "var(--lh-surface)",
                                cursor: currentParent === null || saving ? "not-allowed" : "pointer",
                                opacity: currentParent === null ? 0.5 : 1, marginBottom: 6,
                            }}
                        >
                            <span style={{ fontSize: 14 }}>◆</span>
                            <span style={{ fontWeight: 600, color: "var(--lh-ink)" }}>Collection root</span>
                            {currentParent === null && <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--lh-slate)" }}>current location</span>}
                        </button>
                        {options.length === 0 && (
                            <div style={{ padding: "18px 12px", fontSize: 13, color: "var(--lh-slate)", textAlign: "center" }}>
                                No other folders available. Create a new folder to move this into.
                            </div>
                        )}
                        {options.map(({ f, depth }) => {
                            const isCurrent = f.id === currentParent;
                            return (
                                <button
                                    key={f.id}
                                    type="button"
                                    disabled={saving || isCurrent}
                                    onClick={() => doMove(f.id)}
                                    data-testid={`move-folder-target-${f.id}`}
                                    style={{
                                        display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left",
                                        padding: "9px 12px", borderRadius: 9,
                                        border: "1px solid transparent",
                                        background: "transparent",
                                        cursor: isCurrent || saving ? "not-allowed" : "pointer",
                                        opacity: isCurrent ? 0.5 : 1, marginBottom: 2,
                                        paddingLeft: 12 + depth * 18,
                                    }}
                                    onMouseEnter={(e) => { if (!isCurrent && !saving) e.currentTarget.style.background = "var(--lh-bg)"; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                                >
                                    <span style={{ fontSize: 14, color: "var(--lh-slate)" }}>▢</span>
                                    <span style={{ color: "var(--lh-ink)" }}>{f.name}</span>
                                    {isCurrent && <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--lh-slate)" }}>current parent</span>}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};


const LearningHub = () => {
    const { user } = useAuth();
    const { depts } = useData();
    // View mode toggle — shelf (default) or the new mind-map view.
    const [viewMode, setViewMode] = useState("shelf");
    // Sub-nav tabs (the new edX-inspired IA). Default landing = the
    // personalised "My Team's Workspace" tab. Drives — and is driven by
    // — the URL param `/learning/:sub` so bookmarks + browser back work.
    //   "team"     → hero + continue-learning + team collections + team activity
    //   "library"  → full org catalogue + browse-by-department + what's new
    //   "assigned" → only stuff assigned to me
    //   "mine"     → only stuff I own
    const { sub: urlSub } = useParams();
    const initialTab = LH_TABS.includes(urlSub) ? urlSub : "team";
    const [lhTab, setLhTabState] = useState(initialTab);
    const setLhTab = (next) => {
        setLhTabState(next);
        // Keep the URL in sync so a share-link / bookmark restores the tab.
        // We push instead of replace so browser back returns to the previous
        // tab, which matches the mental model of "each tab is a page".
        navigate(next === "team" ? "/learning" : `/learning/${next}`);
    };
    // If the URL param changes (e.g. sidebar click), pull the new tab in.
    useEffect(() => {
        const next = LH_TABS.includes(urlSub) ? urlSub : "team";
        if (next !== lhTab) setLhTabState(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlSub]);
    // Continue-learning items (resources this user has opened but not
    // finished). Loaded lazily when the team tab is active.
    const [continueItems, setContinueItems] = useState([]);
    const myDeptName = useMemo(() => {
        const d = (depts || []).find((x) => x.id === user?.department_id);
        return d?.name || "";
    }, [depts, user?.department_id]);
    const [collections, setCollections] = useState([]);
    // Use `undefined` as the "unset" sentinel so a developer-driven
    // `setActiveId(null)` (explicit close) wins over the query-param
    // fallback below.
    const [activeIdState, setActiveIdState] = useState(undefined);
    const [showCreate, setShowCreate] = useState(false);
    const [showBulkCollections, setShowBulkCollections] = useState(false);
    // Collapse the "Browse by department" catalog by default — the page
    // stays focused on "What's new" + "My department" until the visitor
    // explicitly opts into the wider org view.
    const [browseOpen, setBrowseOpen] = useState(false);
    const [showQuickAdd, setShowQuickAdd] = useState(false);
    const [editingCollection, setEditingCollection] = useState(null);
    // Lifted catalog state so both AssignedFocus and the in-page shelves
    // operate off the same source of truth (one fetch, one cache).
    const [catalogResources, setCatalogResources] = useState([]);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // Deep-link: /learning?collection=<id> opens that collection's detail
    // dialog. Used by the Dashboard's collapsible "Assigned resources" bar.
    // We derive the effective activeId from state + query param so this is
    // pure rendering — once any explicit setActiveId fires (which sets
    // activeIdState to a non-undefined value, even null), the query param
    // is treated as consumed.
    const queryCid = searchParams.get("collection");
    const queryCidValid = queryCid && collections.some((c) => c.id === queryCid);
    const activeId = activeIdState !== undefined ? activeIdState : (queryCidValid ? queryCid : null);
    const setActiveId = setActiveIdState;

    const load = async () => {
        const [colsR, resR] = await Promise.all([
            api.get("/learning/collections"),
            api.get("/learning/resources").catch(() => ({ data: [] })),
        ]);
        setCollections(colsR.data);
        setCatalogResources(resR.data || []);
    };

    useEffect(() => { load(); }, []);

    // Hydrate the continue-learning strip when the user lands on / switches
    // to the team tab. We skip it on other tabs so the page doesn't fetch
    // things the user can't see.
    useEffect(() => {
        if (lhTab !== "team") return;
        (async () => {
            try {
                const r = await api.get("/learning/continue-learning?limit=6");
                setContinueItems(r.data || []);
            } catch (_e) {
                // Non-fatal — the strip just stays empty.
            }
        })();
    }, [lhTab]);

    const mine = collections.filter((c) => c.owner_id === user?.user_id);
    const assigned = collections.filter(
        (c) => c.owner_id !== user?.user_id && c.assigned_to?.includes(user?.user_id)
    );
    const myDeptId = user?.department_id || null;
    // "From my department" = my own + assigned-to-me + everyone in my dept.
    // Deduped by collection id, ordered by ownership precedence so the
    // user's own collections come first.
    const fromMyDept = useMemo(() => {
        const seen = new Set();
        const ordered = [];
        const push = (c) => {
            if (!c || seen.has(c.id)) return;
            seen.add(c.id);
            ordered.push(c);
        };
        mine.forEach(push);
        assigned.forEach(push);
        collections.forEach((c) => {
            if (myDeptId && c.owner_department_id === myDeptId) push(c);
        });
        return ordered;
    }, [collections, mine, assigned, myDeptId]);

    // "Catalog by department" = remaining collections grouped by the
    // owner's department. My-department collections are excluded here
    // because they already appear in the section above. Departments
    // without any visible collections aren't rendered.
    const byDepartment = useMemo(() => {
        const groups = new Map();
        const inMyDept = new Set(fromMyDept.map((c) => c.id));
        collections.forEach((c) => {
            if (inMyDept.has(c.id)) return;
            const key = c.owner_department_id || "__none__";
            const label = c.owner_department_name || "Unassigned department";
            if (!groups.has(key)) groups.set(key, { id: key, label, items: [] });
            groups.get(key).items.push(c);
        });
        return Array.from(groups.values()).sort((a, b) => {
            if (a.id === "__none__") return 1;
            if (b.id === "__none__") return -1;
            return a.label.localeCompare(b.label);
        });
    }, [collections, fromMyDept]);

    const active = collections.find((c) => c.id === activeId) || null;

    // "What's new this week" — surfaces the 4 most recently added
    // resources across the org so people can spot fresh material the
    // moment they land on the page. Falls back to "this month" if
    // nothing was added in the last 7 days so the rail isn't blank
    // for quiet weeks. Computed once per catalog change.
    const whatsNew = useMemo(() => computeWhatsNew(catalogResources), [catalogResources]);

    const handleDeleteCollection = async (c) => {
        if (!window.confirm(`Delete "${c.name}" and all its resources?`)) return;
        try {
            await api.delete(`/learning/collections/${c.id}`);
            toast.success("Collection deleted");
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    // Bookmarks — client-side persisted set of collection IDs the user
    // has saved from the Discover view. Backed by localStorage so the
    // state survives reloads without touching the server.
    const [savedIds, setSavedIds] = useState(() => {
        try {
            const raw = localStorage.getItem("lh:bookmarks");
            return new Set(raw ? JSON.parse(raw) : []);
        } catch { return new Set(); }
    });
    const toggleSave = (cid) => {
        setSavedIds((prev) => {
            const next = new Set(prev);
            if (next.has(cid)) { next.delete(cid); toast.success("Removed from bookmarks"); }
            else { next.add(cid); toast.success("Saved to your bookmarks"); }
            try { localStorage.setItem("lh:bookmarks", JSON.stringify(Array.from(next))); } catch { /* ignore */ }
            return next;
        });
    };
    // Discover-view state: dept-tab filter + search query.
    const [discoverDept, setDiscoverDept] = useState("__all__");
    const [discoverQuery, setDiscoverQuery] = useState("");
    // Reorder collections in the team-tab grid via drag & drop. Optimistic
    // update on the local `collections` slice through DataContext + PUT
    // to `/collections/reorder`.
    const reorderCollections = useCallback(async (draggedId, targetId, position) => {
        if (draggedId === targetId) return;
        // Work on the team-tab slice (fromMyDept) so we send a coherent
        // list of same-dept collection ids to the backend.
        const scope = fromMyDept;
        const draggedIdx = scope.findIndex((c) => c.id === draggedId);
        const targetIdx = scope.findIndex((c) => c.id === targetId);
        if (draggedIdx < 0 || targetIdx < 0) return;
        const next = scope.slice();
        const [moved] = next.splice(draggedIdx, 1);
        const insertAt = position === "before" ? (targetIdx < draggedIdx ? targetIdx : targetIdx - 1) : (targetIdx < draggedIdx ? targetIdx + 1 : targetIdx);
        next.splice(Math.max(0, insertAt), 0, moved);
        try {
            await api.put("/learning/collections/reorder", { collection_ids: next.map((c) => c.id) });
            await load();
            toast.success("Order saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reorder failed");
            load();
        }
    }, [fromMyDept, load]);

    // Per-collection resource-count / format buckets / total minutes —
    // computed once and shared across the discover cards, team cards, and
    // page-header stat chips so we only iterate `catalogResources` once.
    const resourceIndex = useMemo(() => {
        const idx = {};
        for (const r of catalogResources || []) {
            const cid = r.collection_id;
            if (!cid) continue;
            if (!idx[cid]) idx[cid] = { resources: 0, formats: [], seen: new Set(), totalMinutes: 0 };
            idx[cid].resources += 1;
            const t = r.type || "other";
            if (!idx[cid].seen.has(t)) { idx[cid].seen.add(t); idx[cid].formats.push(t); }
            idx[cid].totalMinutes += Number(r.duration_minutes || r.duration || 0) || 0;
        }
        return idx;
    }, [catalogResources]);

    return (
        <div className="lh-shell" data-testid="learning-page">
            {viewMode === "mindmap" ? (
                <div className="content" style={{ paddingTop: 20 }}>
                    <div className="ph-view-toggle" style={{ position: "static", display: "inline-flex", marginBottom: 16 }}>
                        <button type="button" onClick={() => setViewMode("shelf")} data-testid="learning-view-shelf">Shelf</button>
                        <button type="button" className="active" data-testid="learning-view-mindmap">Mind Map</button>
                    </div>
                    <LearningMindMap />
                </div>
            ) : (
                <div className="lh-main-scroll">
                    {active ? (
                        /* Inline nested view — the mockup's inside-a-collection
                           layout. Replaces the previous modal-detail flow for
                           casual navigation; the "Manage" button in the toolbar
                           still opens the full CollectionDetailDialog for
                           assign/activity/reorder power-user actions. */
                        <InsideCollectionView
                            collection={active}
                            rootLabel={lhTab === "team" ? "My Team's Library" : "All Library"}
                            onExit={() => setActiveId(null)}
                            onChanged={load}
                            viewMode={viewMode}
                            onViewMode={setViewMode}
                        />
                    ) : (
                        <>
                    {/* Pixel-perfect page-header (peach → mint, orbs, chips). Hidden on
                        Discover mode (library tab, root, all-departments filter) to
                        match the HTML mockup where the discover hero fully replaces it. */}
                    {!(lhTab === "library") && (
                        <LearningPageHeader
                            tab={lhTab}
                            deptName={myDeptName}
                            viewMode={viewMode}
                            onViewMode={setViewMode}
                            stats={
                                lhTab === "team"
                                    ? [
                                          { key: "collections", value: fromMyDept.length, label: `collection${fromMyDept.length !== 1 ? "s" : ""}` },
                                          { key: "resources",   value: catalogResources.filter((r) => myDeptId && r.owner_department_id === myDeptId).length, label: "resources" },
                                          { key: "assigned",    value: assigned.length, label: "assigned to you" },
                                      ]
                                    : [
                                          { key: "collections", value: collections.length, label: "collections" },
                                          { key: "resources",   value: catalogResources.length, label: "resources" },
                                          { key: "departments", value: byDepartment.length + (fromMyDept.length > 0 ? 1 : 0), label: "departments" },
                                      ]
                            }
                        />
                    )}

                    {/* Legacy internal tab-state router (no visible UI). */}
                    <LearningTabs
                        tab={lhTab}
                        setTab={setLhTab}
                        counts={{
                            team: fromMyDept.length,
                            library: collections.length,
                            assigned: assigned.length,
                            mine: mine.length,
                        }}
                    />

                    <main className="content">
                        {lhTab === "library" ? (
                            <>
                                {/* Discover mode — big centered hero + prompt-card + dept
                                    tabs + `.glib-grid` of collection cards. Pixel-perfect
                                    from the mockup, bound to real backend data. */}
                                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                                    <div className="ph-view-toggle" style={{ position: "static" }}>
                                        <button type="button" className={viewMode === "shelf" ? "active" : ""} onClick={() => setViewMode("shelf")} data-testid="learning-view-shelf">Shelf</button>
                                        <button type="button" className={viewMode === "mindmap" ? "active" : ""} onClick={() => setViewMode("mindmap")} data-testid="learning-view-mindmap">Mind Map</button>
                                    </div>
                                </div>
                                <DiscoverView
                                    collections={collections}
                                    resourceIndex={resourceIndex}
                                    savedIds={savedIds}
                                    catalogResources={catalogResources}
                                    onOpen={(c) => setActiveId(c.id)}
                                    onToggleSave={toggleSave}
                                    query={discoverQuery}
                                    setQuery={setDiscoverQuery}
                                    activeDept={discoverDept}
                                    setActiveDept={setDiscoverDept}
                                />
                            </>
                        ) : (
                            <TargetLibraryBody
                                tab={lhTab}
                                collections={fromMyDept}
                                catalogResources={catalogResources}
                                onOpenCollection={(c) => setActiveId(c.id)}
                                onNewCollection={() => setShowCreate(true)}
                                onAddResource={() => setShowQuickAdd(true)}
                                onReorderCollections={reorderCollections}
                            />
                        )}
                    </main>
                        </>
                    )}

                    {/* Modals — always mounted so create/edit/detail flows keep
                        working regardless of which view is active. */}
                    {showCreate && (
                        <CreateCollectionDialog
                            onClose={() => setShowCreate(false)}
                            onCreated={async () => { await load(); setShowCreate(false); }}
                        />
                    )}
                    {showQuickAdd && (
                        <AddResourceDialog
                            collections={collections}
                            onClose={() => setShowQuickAdd(false)}
                            onAdded={async () => { await load(); setShowQuickAdd(false); }}
                        />
                    )}
                    {editingCollection && (
                        <CreateCollectionDialog
                            existing={editingCollection.id ? editingCollection : null}
                            onClose={() => setEditingCollection(null)}
                            onCreated={async () => { await load(); setEditingCollection(null); }}
                        />
                    )}

                    {/* Everything below is disabled while the target-HTML view is
                        the active experience. The components remain in source so
                        data + functionality is fully preserved (reactions, mind
                        map, uploads, assignments, activity, search, AI Pick,
                        what's-new rail, browse-by-dept, trending) — they're
                        simply not rendered on the two main sub-nav views. */}
                    {false && (
                    <>

                    {/* Team-tab-only hero — RETIRED. Replaced by LearningPageHeader. */}

                    {/* Team-tab-only continue-learning strip. */}
                    {lhTab === "team" && (
                        <ContinueLearningStrip
                            items={continueItems}
                            onOpen={(r) => {
                                if (r.url) window.open(r.url, "_blank", "noopener");
                                setActiveId(r.collection_id);
                            }}
                        />
                    )}

            {/* "Build your skills" hero — RETIRED. */}

            {/* Backup widget MOVED to Control Panel → Learning · Sync. */}

            {/* All Department Library tab — dark navy hero + subject grid +
                Trending. Replaces the old thick AdvancedSearch panel with
                a hero-embedded search that opens the full Advanced Search
                on submit. */}
            {lhTab === "library" && (
                <LibraryHero
                    resourceCount={catalogResources.length}
                    deptCount={byDepartment.length + (fromMyDept.length > 0 ? 1 : 0)}
                />
            )}
            {lhTab === "library" && (
                <DepartmentBrowseGrid
                    myDept={{ id: myDeptId, name: myDeptName, items: fromMyDept }}
                    otherDepts={byDepartment}
                    catalogResources={catalogResources}
                    onOpen={(deptId) => deptId && navigate(`/learning/department/${deptId}`)}
                />
            )}
            {lhTab === "library" && (
                <TrendingSection
                    resources={catalogResources}
                    onOpen={(r) => {
                        if (r.url) window.open(r.url, "_blank", "noopener");
                        setActiveId(r.collection_id);
                    }}
                />
            )}

            {/* Advanced search — kept for the Team + Assigned + Mine tabs
                where the dense filter panel is still useful. The Library
                tab uses its hero search + subject tiles instead. */}
            {lhTab !== "library" && (
                <AdvancedSearch
                    resources={catalogResources}
                    collections={collections}
                    user={user}
                    onOpenResource={(r) => {
                        if (r.url) window.open(r.url, "_blank", "noopener");
                        setActiveId(r.collection_id);
                    }}
                />
            )}

            {/* What's new this week — retired from the Library tab in favour
                of the Trending section, but still surfaces on Team so
                first-time visitors see fresh content. */}
            {lhTab === "team" && whatsNew.items.length > 0 && (
                <WhatsNewRail
                    items={whatsNew.items}
                    window={whatsNew.window}
                    onOpen={(r) => setActiveId(r.collection_id)}
                />
            )}

            {/* Pick this for me — floating FAB (bottom-right). Kept out of
                the page flow so it doesn't compete with the search bar. */}
            <PickForMeFab
                catalogReady={catalogResources.length > 0}
                onOpenResource={(p) => setActiveId(p.collection_id)}
            />
            {/* Assigned-to-you focus section — renders on the Team and the
                dedicated Assigned tab. Hidden on other tabs. */}
            {(lhTab === "team" || lhTab === "assigned") && (
                <AssignedFocus
                    resources={catalogResources}
                    onResourcesChange={setCatalogResources}
                    onOpenCollection={(cid) => setActiveId(cid)}
                />
            )}

            {/* From my department — primary shelf. Combines the user's own
                collections, anything assigned to them, and all collections
                owned by department peers. Shown on Team + Contributions +
                Assigned tabs; hidden on the All-Library tab (which uses
                the Browse-by-Department shelves instead). */}
            {lhTab !== "library" && (
            <section className="space-y-3" data-testid="learning-my-department">
                <div className="flex items-end justify-between flex-wrap gap-3">
                    <div className="min-w-0 flex gap-3">
                        {/* Left-side vertical teal bar accent — the visual cue that
                            marks this shelf as the viewer's own department. */}
                        <div className="w-1 self-stretch rounded-full bg-gradient-to-b from-[#2EB4AC] to-[#25908A] shrink-0" aria-hidden />
                        <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-[#25908A] font-semibold">My department</div>
                            <h2 className="font-heading text-2xl font-bold text-slate-900 tracking-tight mt-1 flex items-center gap-2">
                                <Users className="h-6 w-6 text-[#2EB4AC]" />
                                {myDeptName || "From my department"}
                            </h2>
                            <p className="text-sm text-slate-500 mt-1">
                                {myDeptName
                                    ? "Your own collections, anything assigned to you, and what your teammates are curating."
                                    : "Your own collections, anything assigned to you, and what your department peers are curating."}
                            </p>
                        </div>
                    </div>
                    {user?.department_id && (
                        <button
                            onClick={() => navigate(`/learning/department/${user.department_id}`)}
                            data-testid={`learning-view-all-${user.department_id}`}
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-semibold text-[#25908A] hover:bg-teal-50 border border-teal-200 hover:border-[#2EB4AC] transition-colors flex-shrink-0"
                        >
                            View all <ChevronRight className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
                <CollectionShelf
                    title={lhTab === "assigned" ? "Assigned to me" : lhTab === "mine" ? "My contributions" : "My department"}
                    subtitle={lhTab === "assigned" ? "Everything a manager or teammate has assigned to you" : lhTab === "mine" ? "Everything you own and curate" : "Your team's collections"}
                    items={lhTab === "assigned" ? assigned : lhTab === "mine" ? mine : fromMyDept}
                    emptyHint={
                        lhTab === "assigned"
                            ? "Nothing assigned to you yet — anything a manager assigns will show up here."
                            : lhTab === "mine"
                                ? 'You haven\'t created a collection yet. Click "New collection" to start one.'
                                : 'Start by creating a collection with the "New collection" button above — it will appear here for everyone in your department.'
                    }
                    onOpen={(c) => setActiveId(c.id)}
                    onEdit={(c) => setEditingCollection(c)}
                    onDelete={handleDeleteCollection}
                    user={user}
                    reorderable={lhTab === "team" && (!!user?.department_id || user?.role === "super_admin")}
                    onReorder={async (ids) => {
                        // Optimistic — reorder the in-memory collections so the shelf
                        // updates instantly. Then persist and reload if the server
                        // count-doesn't-match (defensive).
                        setCollections((prev) => {
                            const byId = Object.fromEntries(prev.map((x) => [x.id, x]));
                            const reorderedFromDept = ids.map((id) => byId[id]).filter(Boolean);
                            const others = prev.filter((x) => !ids.includes(x.id));
                            return [...reorderedFromDept, ...others];
                        });
                        try {
                            await api.put("/learning/collections/reorder", {
                                department_id: user?.department_id || null,
                                collection_ids: ids,
                            });
                            toast.success("Order saved");
                        } catch (e) {
                            toast.error(e?.response?.data?.detail || "Reorder failed");
                            load?.();
                        }
                    }}
                />
            </section>
            )}

            {/* Legacy Browse-by-Department collapsible — replaced by the
                subject-tile grid + Trending section on the Library tab. */}
            {false && lhTab === "library" && byDepartment.length > 0 && (
                <section className="space-y-3" data-testid="learning-by-department">
                    <button
                        type="button"
                        onClick={() => setBrowseOpen((v) => !v)}
                        aria-expanded={browseOpen}
                        data-testid="learning-browse-by-dept-toggle"
                        className="group w-full text-left rounded-2xl border border-slate-200 bg-white hover:border-[#2EB4AC]/50 hover:bg-teal-50/40 px-5 py-4 transition-colors flex items-center justify-between gap-4"
                    >
                        <div className="min-w-0 flex-1">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-[#25908A] font-semibold">Catalog</div>
                            <h2 className="font-heading text-2xl font-bold text-slate-900 tracking-tight mt-1 flex items-center gap-2">
                                <Sparkles className="h-6 w-6 text-[#2EB4AC]" />
                                Browse by department
                            </h2>
                            <p className="text-sm text-slate-500 mt-1">Every other department&apos;s collections — click to {browseOpen ? "hide" : "reveal"} what the rest of Odyssey is learning.</p>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                            <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-slate-600 bg-slate-100 rounded-full px-3 py-1">
                                <span className="font-semibold text-slate-800">{byDepartment.reduce((n, g) => n + g.items.length, 0)}</span> collections ·
                                <span className="font-semibold text-slate-800">{byDepartment.length}</span> depts
                            </span>
                            <div
                                aria-hidden
                                className={`h-9 w-9 rounded-full border border-teal-200 text-[#25908A] bg-white group-hover:bg-teal-50 flex items-center justify-center transition-transform ${browseOpen ? "rotate-180" : ""}`}
                            >
                                <ChevronDown className="h-4 w-4" />
                            </div>
                        </div>
                    </button>

                    {browseOpen && (
                        <div className="space-y-4 pt-2" data-testid="learning-browse-by-dept-content">
                            {byDepartment.map((g) => (
                                <div key={g.id} className="relative">
                                    <CollectionShelf
                                        title={g.label}
                                        subtitle={`Collections from ${g.label}`}
                                        items={g.items}
                                        emptyHint=""
                                        onOpen={(c) => setActiveId(c.id)}
                                        onEdit={(c) => setEditingCollection(c)}
                                        onDelete={handleDeleteCollection}
                                        user={user}
                                        headerAction={g.id && g.id !== "no-dept" ? (
                                            <button
                                                onClick={() => navigate(`/learning/department/${g.id}`)}
                                                data-testid={`learning-view-all-${g.id}`}
                                                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold text-[#25908A] hover:bg-teal-50 border border-teal-200 hover:border-[#2EB4AC] transition-colors"
                                            >
                                                View all <ChevronRight className="h-3.5 w-3.5" />
                                            </button>
                                        ) : null}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            )}

            {showCreate && <CreateCollectionDialog onClose={() => setShowCreate(false)} onCreated={async () => { await load(); setShowCreate(false); }} />}
            {showBulkCollections && <BulkCollectionsDialog onClose={() => setShowBulkCollections(false)} onImported={async () => { await load(); setShowBulkCollections(false); }} />}
            {showQuickAdd && (
                <AddResourceDialog
                    collections={collections}
                    onClose={() => setShowQuickAdd(false)}
                    onAdded={async () => { await load(); setShowQuickAdd(false); }}
                />
            )}
            {editingCollection && (
                <CreateCollectionDialog
                    existing={editingCollection}
                    onClose={() => setEditingCollection(null)}
                    onCreated={async () => { await load(); setEditingCollection(null); }}
                />
            )}
            {active && <CollectionDetailDialog collection={active} isOwner={!!active.can_manage} onClose={() => setActiveId(null)} onChanged={load} />}
            </>
            )}
                </div>
            )}
        </div>
    );
};

// ---------- "What's new this week" rail ----------
// Compact 4-up grid of the freshest resources across the org. Each card
// lights up with the type icon + a "NEW" pulse and opens the parent
// collection on click. The header includes a relative-time window pill
// so users know exactly how fresh the picks are.
const WHATSNEW_TYPE_ACCENT = {
    video:   "from-rose-500 to-orange-400",
    course:  "from-violet-500 to-fuchsia-400",
    article: "from-sky-500 to-cyan-400",
    doc:     "from-slate-600 to-slate-400",
    podcast: "from-amber-500 to-yellow-400",
    book:    "from-emerald-500 to-teal-400",
    other:   "from-slate-500 to-slate-400",
};

const fmtRelative = (iso) => {
    const t = Date.parse(iso || "");
    if (!Number.isFinite(t)) return "";
    const diff = Math.max(0, Date.now() - t);
    const hours = Math.floor(diff / (60 * 60 * 1000));
    if (hours < 1) return "just now";
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return "yesterday";
    if (days < 7) return `${days}d ago`;
    const weeks = Math.floor(days / 7);
    if (weeks < 5) return `${weeks}w ago`;
    return new Date(t).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

const WhatsNewRail = ({ items, window: w, onOpen }) => (
    <section data-testid="learning-whats-new" className="space-y-3">
        <div className="flex items-end justify-between flex-wrap gap-3">
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#25908A] font-semibold inline-flex items-center gap-1.5">
                    <span className="relative flex h-1.5 w-1.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2EB4AC] opacity-70" />
                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#2EB4AC]" />
                    </span>
                    Fresh
                </div>
                <h2 className="font-heading text-2xl font-bold text-slate-900 tracking-tight mt-1 flex items-center gap-2">
                    <Sparkles className="h-6 w-6 text-[#2EB4AC]" />
                    What&apos;s new {w === "week" ? "this week" : "lately"}
                </h2>
                <p className="text-sm text-slate-500 mt-1">The latest resources added across Odyssey — discover before everyone else.</p>
            </div>
            <span className="text-xs font-semibold text-[#25908A] bg-teal-50 border border-teal-200 px-2.5 py-1 rounded-full">
                {items.length} {items.length === 1 ? "pick" : "picks"}
            </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {items.map((r) => {
                const TypeIcon = TYPE_ICON[r.type] || Globe;
                const accent = WHATSNEW_TYPE_ACCENT[r.type] || WHATSNEW_TYPE_ACCENT.other;
                return (
                    <button
                        key={r.id}
                        type="button"
                        onClick={() => onOpen(r)}
                        data-testid={`whats-new-card-${r.id}`}
                        className="group relative text-left bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-[#2EB4AC] hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
                    >
                        <div className={`relative h-24 bg-gradient-to-br ${accent} flex items-center justify-center overflow-hidden`}>
                            <div className="absolute inset-0 opacity-20" style={{
                                backgroundImage: "radial-gradient(circle at 20% 20%, white 0, transparent 40%), radial-gradient(circle at 80% 60%, white 0, transparent 35%)",
                            }} />
                            <div className="relative h-12 w-12 rounded-xl bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center">
                                <TypeIcon className="h-5 w-5 text-white" />
                            </div>
                            <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider bg-white/95 text-slate-900">
                                <span className="relative flex h-1.5 w-1.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-75" />
                                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-rose-500" />
                                </span>
                                New
                            </span>
                            <span className="absolute top-2 right-2 text-[10px] font-semibold text-white/95 bg-black/30 px-1.5 py-0.5 rounded-md tabular-nums backdrop-blur-sm">
                                {fmtRelative(r.created_at)}
                            </span>
                        </div>
                        <div className="p-3">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold inline-flex items-center gap-1">
                                <TypeIcon className="h-3 w-3" />
                                {TYPE_LABEL[r.type] || r.type}
                                {r.collection_name && <span className="normal-case tracking-normal text-slate-400 truncate"> · {r.collection_name}</span>}
                            </div>
                            <h3 className="mt-1 font-heading font-bold text-slate-900 text-sm leading-snug line-clamp-2 group-hover:text-[#25908A] transition-colors">
                                {r.name}
                            </h3>
                            {r.collection_owner_name && (
                                <div className="mt-2 text-[11px] text-slate-500 inline-flex items-center gap-1">
                                    <span className="h-4 w-4 rounded-full bg-slate-100 text-slate-600 text-[9px] font-bold inline-flex items-center justify-center uppercase">
                                        {(r.collection_owner_name || "?").charAt(0)}
                                    </span>
                                    <span className="truncate">{r.collection_owner_name}</span>
                                </div>
                            )}
                        </div>
                    </button>
                );
            })}
        </div>
    </section>
);

// ---------- Advanced search ----------
// Collapsible filter rail with all the dimensions a catalog user cares
// about. We keep it client-side because the catalog endpoint already
// returns every resource the user can see (catalog is open across the
// org). Filtering in-memory means instant feedback while typing.
const AdvancedSearch = ({ resources, collections, user, onOpenResource }) => {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [fType, setFType] = useState("all");
    const [fDept, setFDept] = useState("all");
    const [fStatus, setFStatus] = useState("all");
    const [fOwner, setFOwner] = useState("all");
    const [fDate, setFDate] = useState("any");
    const [mineOnly, setMineOnly] = useState(false);

    const depts = useMemo(() => {
        const m = new Map();
        resources.forEach((r) => {
            const id = r.collection_department_id || "__none__";
            const label = r.collection_department_name || "Unassigned";
            if (!m.has(id)) m.set(id, { id, label });
        });
        return Array.from(m.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [resources]);

    const owners = useMemo(() => {
        const m = new Map();
        resources.forEach((r) => {
            const id = r.collection_owner_id || "__none__";
            const label = r.collection_owner_name || "Unknown";
            if (!m.has(id)) m.set(id, { id, label });
        });
        return Array.from(m.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [resources]);

    const results = useMemo(
        () => filterResources(resources, { q, fType, fDept, fStatus, fOwner, fDate, mineOnly, uid: user?.user_id }),
        [resources, q, fType, fDept, fStatus, fOwner, fDate, mineOnly, user]
    );

    const activeCount = [
        q.trim() ? 1 : 0,
        fType !== "all" ? 1 : 0,
        fDept !== "all" ? 1 : 0,
        fStatus !== "all" ? 1 : 0,
        fOwner !== "all" ? 1 : 0,
        fDate !== "any" ? 1 : 0,
        mineOnly ? 1 : 0,
    ].reduce((s, n) => s + n, 0);

    const reset = () => {
        setQ(""); setFType("all"); setFDept("all"); setFStatus("all");
        setFOwner("all"); setFDate("any"); setMineOnly(false);
    };

    void collections;
    return (
        <section data-testid="learning-search" className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="p-4 sm:p-5 flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[240px]">
                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                        data-testid="search-q"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search across name, URL, description, collection…"
                        className="h-11 w-full pl-10 pr-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2EB4AC] focus:border-[#2EB4AC] bg-white outline-none text-sm"
                    />
                </div>
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    data-testid="search-toggle-filters"
                    className={`inline-flex items-center gap-2 h-11 px-3 rounded-lg border text-sm font-medium transition-colors ${
                        open || activeCount > 0
                            ? "border-[#2EB4AC] text-[#25908A] bg-teal-50"
                            : "border-slate-200 text-slate-700 bg-white hover:border-[#2EB4AC]"
                    }`}
                >
                    <Filter className="h-4 w-4" />
                    Filters
                    {activeCount > 0 && (
                        <span className="ml-1 inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-[#2EB4AC] text-white text-[10px] font-bold tabular-nums">{activeCount}</span>
                    )}
                </button>
                {activeCount > 0 && (
                    <button
                        type="button"
                        onClick={reset}
                        data-testid="search-clear"
                        className="inline-flex items-center gap-1.5 h-11 px-3 rounded-lg text-sm text-slate-500 hover:text-slate-800"
                    >
                        <X className="h-3.5 w-3.5" /> Clear
                    </button>
                )}
                {(q || activeCount > 0) && (
                    <span className="text-xs text-slate-500 tabular-nums">
                        <span className="font-semibold text-slate-800">{results.length}</span> / {resources.length} resources
                    </span>
                )}
            </div>

            {open && (
                <div className="px-4 sm:px-5 pb-4 sm:pb-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 border-t border-slate-200 pt-4">
                    <FilterSelect testid="search-type" label="Type" value={fType} onChange={setFType} options={[
                        { v: "all", l: "Any type" },
                        ...TYPES.map((t) => ({ v: t, l: TYPE_LABEL[t] })),
                    ]} />
                    <FilterSelect testid="search-dept" label="Department" value={fDept} onChange={setFDept} options={[
                        { v: "all", l: "Any department" },
                        ...depts.map((d) => ({ v: d.id, l: d.label })),
                    ]} />
                    <FilterSelect testid="search-status" label="Status" value={fStatus} onChange={setFStatus} options={[
                        { v: "all", l: "Any status" },
                        { v: "not_started", l: "Not started" },
                        { v: "in_progress", l: "In progress" },
                        { v: "completed", l: "Completed" },
                        { v: "skipped", l: "Skipped" },
                    ]} />
                    <FilterSelect testid="search-owner" label="Owner" value={fOwner} onChange={setFOwner} options={[
                        { v: "all", l: "Anyone" },
                        ...owners.map((o) => ({ v: o.id, l: o.label })),
                    ]} />
                    <FilterSelect testid="search-date" label="Added" value={fDate} onChange={setFDate} options={[
                        { v: "any", l: "Any date" },
                        { v: "7d", l: "Last 7 days" },
                        { v: "30d", l: "Last 30 days" },
                        { v: "90d", l: "Last 90 days" },
                    ]} />
                    <label className="flex items-end gap-2 cursor-pointer pb-2.5">
                        <input
                            type="checkbox"
                            checked={mineOnly}
                            onChange={(e) => setMineOnly(e.target.checked)}
                            data-testid="search-mine"
                            className="h-4 w-4 accent-[#2EB4AC]"
                        />
                        <span className="text-xs text-slate-700 font-medium">Assigned to me only</span>
                    </label>
                </div>
            )}

            {(q || activeCount > 0) && (
                <div className="border-t border-slate-200 bg-slate-50/30" data-testid="search-results">
                    {results.length === 0 ? (
                        <div className="px-5 py-10 text-center text-sm text-slate-500">
                            Nothing matches those filters. <button onClick={reset} className="text-[#25908A] font-medium hover:underline">Reset</button>
                        </div>
                    ) : (
                        <ul className="divide-y divide-slate-200 max-h-[460px] overflow-y-auto scrollbar-thin">
                            {results.slice(0, 50).map((r) => {
                                const TypeIcon = TYPE_ICON[r.type] || Globe;
                                return (
                                    <li key={r.id}>
                                        <button
                                            type="button"
                                            onClick={() => onOpenResource(r)}
                                            data-testid={`search-row-${r.id}`}
                                            className="w-full px-5 py-3 flex items-center gap-3 hover:bg-white transition-colors text-left group"
                                        >
                                            <div className="h-9 w-9 rounded-lg bg-white border border-slate-200 text-[#25908A] flex items-center justify-center shrink-0 group-hover:border-[#2EB4AC] transition-colors">
                                                <TypeIcon className="h-4 w-4" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="font-medium text-sm text-slate-900 truncate group-hover:text-[#25908A]">{r.name || r.url}</div>
                                                <div className="text-[11px] text-slate-500 truncate">
                                                    {TYPE_LABEL[r.type] || r.type}{r.collection_name ? ` · ${r.collection_name}` : ""}{r.collection_department_name ? ` · ${r.collection_department_name}` : ""}
                                                </div>
                                            </div>
                                            <span className="hidden sm:inline-flex items-center gap-1 h-6 px-2 rounded-full text-[10px] font-semibold border bg-white text-slate-700 border-slate-200">
                                                {r.my_status?.replace("_", " ") || "not started"}
                                            </span>
                                            {r.url && <ExternalLink className="h-3.5 w-3.5 text-slate-400 group-hover:text-[#2EB4AC] shrink-0" />}
                                        </button>
                                    </li>
                                );
                            })}
                            {results.length > 50 && (
                                <li className="px-5 py-2 text-center text-[11px] text-slate-500">
                                    Showing 50 of {results.length} — tighten filters to narrow down.
                                </li>
                            )}
                        </ul>
                    )}
                </div>
            )}
        </section>
    );
};

// ---------- "Pick this for me" — Floating AI goal matcher ----------
// Fixed bottom-right FAB with a violet→pink→amber gradient, pulsing
// aura, and a Brain icon — visually distinct from the search bar.
// On click opens a portal modal with the goal input + numbered results.
// Exported so the global Layout can mount it on every page (not just /learning).
export const PickForMeFab = ({ catalogReady, onOpenResource }) => {
    const [open, setOpen] = useState(false);
    const [goal, setGoal] = useState("");
    const [busy, setBusy] = useState(false);
    const [picks, setPicks] = useState(null);
    // Rotate through friendly conversational prompts on the pill so it
    // never feels static — swaps every 4.5s with a soft fade transition.
    const PROMPTS = [
        "Hey, what's on your mind?",
        "Got 10 minutes? Let's learn.",
        "Curious about something? Ask away.",
        "Need a recommendation? I got you.",
    ];
    const [promptIdx, setPromptIdx] = useState(0);
    useEffect(() => {
        const id = setInterval(() => {
            setPromptIdx((i) => (i + 1) % PROMPTS.length);
        }, 4500);
        return () => clearInterval(id);
    }, [PROMPTS.length]);
    const submit = async (e) => {
        e?.preventDefault();
        const q = goal.trim();
        if (!q) { toast.error("Tell me what you want to learn"); return; }
        setBusy(true);
        try {
            const r = await api.post("/ai/recommend-resources", { goal: q, limit: 5 });
            setPicks(r.data?.picks || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "AI couldn't pick");
        } finally { setBusy(false); }
    };
    const close = () => { setOpen(false); };
    const clear = () => { setPicks(null); setGoal(""); };

    // We deliberately do NOT gate the orb on catalogReady anymore — if the
    // /learning/resources probe ever flakes (slow Mongo, transient 500,
    // network blip during mount) the orb used to vanish permanently for
    // that user with no recovery path. The pick modal itself shows an
    // empty-state when the catalog is genuinely empty, so the orb is safe
    // to render unconditionally for any authenticated user.
    // Portal the widget directly to <body> so it escapes any ancestor with
    // `transform`/`filter` (Layout's animate-fade-in-up leaves a residual
    // matrix transform that would trap `position: fixed` and pin the
    // widget to the bottom of the document instead of the viewport).
    return (
        <>
            {createPortal(
            <div className="fixed bottom-6 right-6 z-[100] pointer-events-none">
                <div className="relative pointer-events-auto group">
                    {/* Outer ambient glow — same Siri palette, but bigger
                        and softer so the whole pill feels like it's
                        breathing on the page. */}
                    <span aria-hidden className="absolute -inset-3 rounded-full pointer-events-none"
                        style={{
                            background: "radial-gradient(60% 100% at 20% 50%, rgba(236,72,153,0.45) 0%, transparent 60%), radial-gradient(60% 100% at 80% 50%, rgba(99,102,241,0.45) 0%, transparent 60%)",
                            filter: "blur(14px)",
                            animation: "siri-glow 4.2s ease-in-out infinite",
                        }} />

                    {/* The horizontal pill — mascot anchored on the left,
                        text reveals on hover. Idle state: the pill is
                        translated down so only the top half pokes above
                        the viewport's bottom edge ("peeking"). On hover
                        the whole assembly slides up + the brand label and
                        rotating prompt expand horizontally to the right. */}
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        data-testid="pick-fab"
                        aria-label="Ask Odyssey Smart Bot what to learn next"
                        title="Tell Odyssey Smart Bot what you want to learn"
                        className="relative inline-flex items-center gap-0 group-hover:gap-3 h-14 px-2 group-hover:pr-5 rounded-full overflow-hidden transition-all duration-500 ease-out group-hover:scale-[1.03] active:scale-[0.98] translate-y-[36%] group-hover:translate-y-0"
                        style={{
                            background: "linear-gradient(135deg, #ffffff 0%, #f1f5f9 55%, #e0e7ff 100%)",
                            boxShadow:
                                "0 0 0 1px rgba(255,255,255,0.9) inset, 0 0 0 1px rgba(148,163,184,0.35), 0 18px 40px -12px rgba(99,102,241,0.55), 0 0 28px -2px rgba(236,72,153,0.35)",
                        }}
                    >
                        {/* Embedded robot mascot on the left — inline SVG
                            so the eyes can blink + glance left/right via
                            SMIL animations with no extra asset. */}
                        <span
                            className="relative h-11 w-11 shrink-0 inline-flex items-center justify-center"
                            style={{ animation: "bot-bob 3.2s ease-in-out infinite" }}
                        >
                            <svg
                                viewBox="0 0 64 64"
                                className="h-11 w-11 drop-shadow-[0_4px_8px_rgba(0,0,0,0.4)]"
                                aria-hidden="true"
                            >
                                <defs>
                                    <radialGradient id="botBody" cx="50%" cy="32%" r="70%">
                                        <stop offset="0%" stopColor="#5a6173" />
                                        <stop offset="55%" stopColor="#33394a" />
                                        <stop offset="100%" stopColor="#181c28" />
                                    </radialGradient>
                                    <linearGradient id="botShine" x1="50%" y1="0%" x2="50%" y2="55%">
                                        <stop offset="0%" stopColor="rgba(255,255,255,0.35)" />
                                        <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                                    </linearGradient>
                                    <radialGradient id="eyeGlow" cx="50%" cy="50%" r="60%">
                                        <stop offset="0%" stopColor="#ffffff" />
                                        <stop offset="55%" stopColor="#e2e8f0" />
                                        <stop offset="100%" stopColor="#94a3b8" />
                                    </radialGradient>
                                </defs>
                                {/* Shift the whole bot down +5 so the
                                    antenna + head + mouth sit visually
                                    centered inside the 64×64 viewBox
                                    instead of biased toward the top. */}
                                <g transform="translate(0, 5)">
                                    <line x1="32" y1="0" x2="32" y2="5" stroke="#7a8298" strokeWidth="1.4" strokeLinecap="round" />
                                    <circle cx="32" cy="-1" r="2" fill="#2EB4AC">
                                        <animate attributeName="opacity" values="1;0.4;1" dur="1.8s" repeatCount="indefinite" />
                                    </circle>
                                    <rect x="9" y="6" width="46" height="42" rx="14" fill="url(#botBody)" />
                                    <rect x="9" y="6" width="46" height="20" rx="14" fill="url(#botShine)" />
                                    <rect x="5" y="20" width="5" height="14" rx="2.5" fill="#2a2f3d" />
                                    <rect x="54" y="20" width="5" height="14" rx="2.5" fill="#2a2f3d" />
                                    <rect x="14" y="16" width="36" height="22" rx="11" fill="#0a0d14" />
                                    <rect x="14" y="16" width="36" height="11" rx="11" fill="rgba(255,255,255,0.05)" />
                                    <g>
                                        <circle cx="24" cy="27" r="3.4" fill="url(#eyeGlow)">
                                            <animate attributeName="cx" values="24;24;26;24;22;24;24" keyTimes="0;0.2;0.32;0.5;0.62;0.8;1" dur="5.2s" repeatCount="indefinite" />
                                            <animate attributeName="ry" values="3.4;3.4;0.3;3.4;3.4" keyTimes="0;0.46;0.5;0.54;1" dur="5.2s" repeatCount="indefinite" />
                                        </circle>
                                    </g>
                                    <g>
                                        <circle cx="40" cy="27" r="3.4" fill="url(#eyeGlow)">
                                            <animate attributeName="cx" values="40;40;42;40;38;40;40" keyTimes="0;0.2;0.32;0.5;0.62;0.8;1" dur="5.2s" repeatCount="indefinite" />
                                            <animate attributeName="ry" values="3.4;3.4;0.3;3.4;3.4" keyTimes="0;0.46;0.5;0.54;1" dur="5.2s" repeatCount="indefinite" />
                                        </circle>
                                    </g>
                                    <rect x="28" y="42" width="8" height="2" rx="1" fill="#3a3f4e" opacity="0.7" />
                                </g>
                            </svg>
                        </span>

                        {/* Engaging copy with shimmering accent — collapsed
                            to width 0 while idle so the pill reads as just
                            a mascot. Expands on hover via max-width +
                            opacity transitions, with a small delay so the
                            slide-up reads as a sequence (peek → pop). */}
                        <span className="flex flex-col items-start leading-tight text-left overflow-hidden max-w-0 opacity-0 group-hover:max-w-[16rem] group-hover:opacity-100 transition-all duration-500 ease-out group-hover:delay-150 whitespace-nowrap">
                            <span
                                className="text-[10px] uppercase tracking-[0.22em] font-semibold"
                                style={{
                                    background: "linear-gradient(90deg, #6d28d9, #c026d3, #db2777, #6d28d9)",
                                    backgroundSize: "200% 100%",
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    backgroundClip: "text",
                                    animation: "siri-shimmer 3.4s linear infinite",
                                }}
                            >
                                Odyssey Smart Bot
                            </span>
                            <span className="text-[13px] font-bold text-slate-900 whitespace-nowrap mt-0.5 inline-flex items-center gap-1.5">
                                <span
                                    key={promptIdx}
                                    className="inline-block"
                                    style={{ animation: "siri-fade-in 0.6s ease-out both" }}
                                >
                                    {PROMPTS[promptIdx]}
                                </span>
                                <span aria-hidden className="text-violet-500 transition-transform duration-300 group-hover:translate-x-1">→</span>
                            </span>
                        </span>

                        {/* Soft conic sheen that sweeps on hover */}
                        <span aria-hidden className="absolute inset-0 rounded-full pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                            style={{
                                background: "conic-gradient(from 180deg at 50% 50%, transparent 0deg, rgba(255,255,255,0.18) 60deg, transparent 120deg)",
                                mixBlendMode: "overlay",
                            }} />
                    </button>
                </div>
                <style>{`
                    @keyframes siri-glow {
                        0%, 100% { opacity: 0.5; transform: scale(0.96); }
                        50% { opacity: 0.95; transform: scale(1.08); }
                    }
                    @keyframes siri-shimmer {
                        0%   { background-position: 0% 50%; }
                        100% { background-position: 200% 50%; }
                    }
                    @keyframes siri-fade-in {
                        0%   { opacity: 0; transform: translateY(4px); filter: blur(2px); }
                        100% { opacity: 1; transform: translateY(0);  filter: blur(0); }
                    }
                    @keyframes bot-bob {
                        0%, 100% { transform: translateY(0) rotate(-2deg); }
                        50%      { transform: translateY(-2px) rotate(2deg); }
                    }
                    @keyframes siri-blob-a {
                        0%   { transform: translate(-20%, -20%) scale(0.9); }
                        33%  { transform: translate( 20%, -10%) scale(1.05); }
                        66%  { transform: translate(  0%,  18%) scale(0.95); }
                        100% { transform: translate(-20%, -20%) scale(0.9); }
                    }
                    @keyframes siri-blob-b {
                        0%   { transform: translate( 20%,  20%) scale(1.0); }
                        33%  { transform: translate(-15%,  18%) scale(0.85); }
                        66%  { transform: translate( 18%, -15%) scale(1.1); }
                        100% { transform: translate( 20%,  20%) scale(1.0); }
                    }
                    @keyframes siri-blob-c {
                        0%   { transform: translate( 18%, -18%) scale(0.95); }
                        33%  { transform: translate(-18%,   0%) scale(1.1); }
                        66%  { transform: translate(  5%,  20%) scale(0.9); }
                        100% { transform: translate( 18%, -18%) scale(0.95); }
                    }
                    @keyframes siri-blob-d {
                        0%   { transform: translate(-15%,  15%) scale(0.85); }
                        50%  { transform: translate( 15%, -15%) scale(1.0); }
                        100% { transform: translate(-15%,  15%) scale(0.85); }
                    }
                `}</style>
            </div>,
            document.body
            )}

            {open && createPortal(
                <div className="fixed inset-0 z-[60] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={close}>
                    <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden" data-testid="pick-modal">
                        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3" style={{
                            background: "linear-gradient(135deg, rgba(109,40,217,0.08) 0%, rgba(219,39,119,0.08) 50%, rgba(245,158,11,0.08) 100%)",
                        }}>
                            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 via-pink-500 to-amber-400 text-white flex items-center justify-center shadow-md">
                                <Brain className="h-5 w-5 drop-shadow" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="text-[10px] uppercase tracking-[0.18em] text-violet-700 font-semibold">Pick this for me · AI</div>
                                <h3 className="font-heading text-lg font-bold text-slate-900 truncate">Tell me what you want to learn</h3>
                                <p className="text-[11px] text-slate-500 mt-0.5">Odyssey Smart Bot scans the catalog and hands back 3–5 resources with one-line reasons.</p>
                            </div>
                            <button onClick={close} aria-label="Close" className="h-8 w-8 rounded-lg hover:bg-white/60 text-slate-500 inline-flex items-center justify-center">✕</button>
                        </div>
                        <div className="p-5 space-y-4">
                            <form onSubmit={submit} className="flex gap-2 flex-wrap">
                                <input
                                    autoFocus
                                    value={goal}
                                    onChange={(e) => setGoal(e.target.value)}
                                    placeholder='e.g. "I want to get better at negotiation"'
                                    data-testid="pick-goal-input"
                                    className="h-11 flex-1 min-w-[240px] px-3 border border-violet-200 rounded-lg bg-white focus:ring-2 focus:ring-violet-300 focus:border-violet-300 outline-none text-sm"
                                />
                                <button
                                    type="submit"
                                    disabled={busy || !goal.trim()}
                                    data-testid="pick-submit"
                                    className="inline-flex items-center gap-2 h-11 px-5 rounded-lg text-white font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    style={{
                                        background: "linear-gradient(135deg, #6d28d9 0%, #db2777 50%, #f59e0b 100%)",
                                        boxShadow: "0 8px 24px -8px rgba(124,58,237,0.55)",
                                    }}
                                >
                                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                                    {busy ? "Thinking…" : "Pick for me"}
                                </button>
                            </form>
                            {picks && (
                                <div className="rounded-lg border border-violet-200 overflow-hidden">
                                    {picks.length === 0 ? (
                                        <div className="px-5 py-8 text-center text-sm text-slate-500">
                                            AI couldn&apos;t find a confident match. Try a more specific goal.
                                        </div>
                                    ) : (
                                        <ul className="divide-y divide-slate-100 max-h-[55vh] overflow-y-auto" data-testid="pick-results">
                                            {picks.map((p, idx) => {
                                                const TypeIcon = TYPE_ICON[p.type] || Globe;
                                                const reason = (p.reason || "").trim() || "Matches your goal well";
                                                return (
                                                    <li key={p.id}>
                                                        <button
                                                            type="button"
                                                            onClick={() => { onOpenResource(p); close(); }}
                                                            data-testid={`pick-row-${p.id}`}
                                                            className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-violet-50/40 transition-colors group"
                                                        >
                                                            <span className="mt-0.5 h-7 w-7 rounded-full bg-gradient-to-br from-violet-500 via-pink-500 to-amber-400 text-white text-[11px] font-bold flex items-center justify-center shrink-0 shadow-sm">
                                                                {idx + 1}
                                                            </span>
                                                            <div className="h-9 w-9 rounded-lg bg-white border border-slate-200 text-violet-700 flex items-center justify-center shrink-0">
                                                                <TypeIcon className="h-4 w-4" />
                                                            </div>
                                                            <div className="min-w-0 flex-1">
                                                                <div className="font-semibold text-sm text-slate-900 group-hover:text-violet-700 transition-colors">{p.name}</div>
                                                                <div className="mt-0.5 text-[12px] text-violet-700 italic leading-snug">
                                                                    &ldquo;{reason}&rdquo;
                                                                </div>
                                                                {p.collection_name && (
                                                                    <div className="mt-1 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">in {p.collection_name}</div>
                                                                )}
                                                            </div>
                                                            <ChevronRight className="h-4 w-4 text-slate-400 shrink-0 group-hover:text-violet-700 self-center" />
                                                        </button>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                    <div className="px-4 py-2 border-t border-violet-100 flex items-center justify-between bg-violet-50/30">
                                        <span className="text-[11px] text-slate-500">
                                            {picks.length > 0 ? `${picks.length} pick${picks.length === 1 ? "" : "s"} for "${goal.slice(0, 60)}"` : ""}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={clear}
                                            data-testid="pick-clear"
                                            className="inline-flex items-center gap-1.5 h-7 px-2 rounded-md text-[11px] font-semibold text-slate-500 hover:text-slate-800 hover:bg-white transition-colors"
                                        >
                                            <X className="h-3 w-3" /> Clear results
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};



const FilterSelect = ({ testid, label, value, onChange, options }) => (
    <label className="block">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">{label}</span>
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testid}
            className="h-10 w-full px-2.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2EB4AC] focus:border-[#2EB4AC] bg-white outline-none"
        >
            {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
    </label>
);

// Pure filter — module-level so the linter doesn't flag Date.now() inside
// useMemo, and trivially unit-testable if we ever extract this file.
const filterResources = (resources, { q, fType, fDept, fStatus, fOwner, fDate, mineOnly, uid }) => {
    const ql = (q || "").trim().toLowerCase();
    const now = Date.now();
    const windowMs = fDate === "7d" ? 7 * 24 * 3600e3
                  : fDate === "30d" ? 30 * 24 * 3600e3
                  : fDate === "90d" ? 90 * 24 * 3600e3 : null;
    return resources.filter((r) => {
        if (ql) {
            const haystack = `${r.name || ""} ${r.url || ""} ${r.description || ""} ${r.collection_name || ""}`.toLowerCase();
            if (!haystack.includes(ql)) return false;
        }
        if (fType !== "all" && (r.type || "other") !== fType) return false;
        if (fDept !== "all" && (r.collection_department_id || "__none__") !== fDept) return false;
        if (fStatus !== "all" && (r.my_status || "not_started") !== fStatus) return false;
        if (fOwner !== "all" && (r.collection_owner_id || "__none__") !== fOwner) return false;
        if (mineOnly && !(r.is_my_grant || (uid && r.assigned_to?.includes(uid)))) return false;
        if (windowMs) {
            const t = Date.parse(r.created_at || "");
            if (!Number.isFinite(t) || (now - t) > windowMs) return false;
        }
        return true;
    });
};

// ---------- Backup widget ----------
// Sits under the hero on /learning. Every teammate can pull an offline
// ZIP of their own department; super-admins can also pull the whole org.
// The Google Drive slot flips between three states based on the backend
// status endpoint: "unconfigured" (no env vars), "connected" (last sync
// ok, folder link visible + sync-now button) and "error" (last sync
// failed — surface the error inline).
export const BackupWidget = ({ user, depts }) => {
    const [downloading, setDownloading] = useState(null);
    const [driveOpen, setDriveOpen] = useState(false);
    const [driveStatus, setDriveStatus] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const myDept = React.useMemo(() => (depts || []).find((d) => d.id === user?.department_id), [depts, user?.department_id]);
    const isAdmin = user?.role === "super_admin";

    const loadDrive = async () => {
        try {
            const r = await api.get("/learning/backup/drive-status");
            setDriveStatus(r.data);
        } catch { setDriveStatus(null); }
    };
    React.useEffect(() => { loadDrive(); const t = setInterval(loadDrive, 30000); return () => clearInterval(t); }, []);

    const download = async (kind) => {
        setDownloading(kind);
        try {
            const params = kind === "dept" ? { department_id: user.department_id } : {};
            const r = await api.get("/learning/backup", { params, responseType: "blob" });
            const cd = r.headers?.["content-disposition"] || "";
            const m = cd.match(/filename="?([^";]+)"?/i);
            const fn = m ? m[1] : `odyssey-learning-${kind}.zip`;
            const blob = new Blob([r.data], { type: "application/zip" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = fn;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Backup downloaded");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Backup failed");
        } finally {
            setDownloading(null);
        }
    };

    const syncNow = async () => {
        setSyncing(true);
        try {
            const r = await api.post("/learning/backup/drive-sync");
            if (r.data?.ok) {
                toast.success(`Mirrored to Drive · ${r.data.files_uploaded} files`);
            } else {
                toast.error(r.data?.detail || "Sync failed");
            }
            await loadDrive();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Sync failed");
        } finally {
            setSyncing(false);
        }
    };

    const driveConfigured = !!driveStatus?.configured;
    const driveOk = driveConfigured && driveStatus?.last_status === "ok";
    const driveError = driveConfigured && driveStatus?.last_status === "error";
    const lastSync = driveStatus?.last_sync_at ? new Date(driveStatus.last_sync_at).toLocaleString() : null;

    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6" data-testid="learning-backup-widget">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#25908A] font-bold">Backup & Sync</div>
                    <h3 className="font-heading text-lg font-semibold text-slate-900 mt-1">Keep your learning library safe</h3>
                    <p className="text-sm text-slate-500 mt-1">Download a snapshot any time — every change also auto-syncs to your team&apos;s shared Google Drive.</p>
                </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-4">
                {/* My department download */}
                <div className="border border-slate-200 rounded-xl p-4 flex flex-col hover:border-[#2EB4AC] transition-colors">
                    <div className="inline-flex items-center gap-2">
                        <div className="h-8 w-8 rounded-lg bg-teal-50 text-[#25908A] border border-teal-100 flex items-center justify-center">
                            <BackupDeptIcon />
                        </div>
                        <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Department</div>
                            <div className="text-sm font-semibold text-slate-900 truncate">{myDept?.name || "No home department"}</div>
                        </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-2 flex-1">
                        {"Every collection + resource in your team\u2019s library as a ZIP of shortcut files. Works offline."}
                    </p>
                    <button
                        type="button"
                        disabled={!user?.department_id || downloading === "dept"}
                        onClick={() => download("dept")}
                        data-testid="learning-backup-dept-btn"
                        className="mt-3 inline-flex items-center justify-center gap-2 h-9 px-3 rounded-lg text-sm font-semibold bg-[#2EB4AC] hover:bg-[#25908A] text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        {downloading === "dept" ? <Loader2 className="h-4 w-4 animate-spin" /> : <BackupDownloadIcon />}
                        Download my dept
                    </button>
                </div>

                {/* Whole org download — admin only */}
                <div className={`border rounded-xl p-4 flex flex-col transition-colors ${isAdmin ? "border-slate-200 hover:border-[#2EB4AC]" : "border-slate-100 bg-slate-50/50"}`}>
                    <div className="inline-flex items-center gap-2">
                        <div className={`h-8 w-8 rounded-lg flex items-center justify-center border ${isAdmin ? "bg-amber-50 text-amber-700 border-amber-100" : "bg-slate-100 text-slate-400 border-slate-200"}`}>
                            <BackupOrgIcon />
                        </div>
                        <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Whole org</div>
                            <div className="text-sm font-semibold text-slate-900 truncate">Every department</div>
                        </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-2 flex-1">
                        {isAdmin ? "One ZIP with every department\u2019s library. Admin-only." : "Only super admins can download the full org backup."}
                    </p>
                    <button
                        type="button"
                        disabled={!isAdmin || downloading === "org"}
                        onClick={() => download("org")}
                        data-testid="learning-backup-org-btn"
                        className={`mt-3 inline-flex items-center justify-center gap-2 h-9 px-3 rounded-lg text-sm font-semibold transition-colors ${
                            isAdmin
                                ? "bg-white text-slate-800 border border-slate-300 hover:border-slate-500"
                                : "bg-white text-slate-400 border border-slate-200 cursor-not-allowed"
                        }`}
                    >
                        {downloading === "org" ? <Loader2 className="h-4 w-4 animate-spin" /> : <BackupDownloadIcon />}
                        Download org
                    </button>
                </div>

                {/* Google Drive auto-sync */}
                <div className={`border rounded-xl p-4 flex flex-col relative overflow-hidden ${
                    driveOk ? "border-emerald-200 bg-emerald-50/30"
                    : driveError ? "border-rose-200 bg-rose-50/30"
                    : "border-dashed border-slate-300"
                }`} data-testid="learning-backup-drive-card">
                    {!driveConfigured && (
                        <span className="absolute -right-8 top-3 rotate-12 text-[9px] font-bold uppercase tracking-widest bg-amber-100 text-amber-800 px-8 py-0.5 border-y border-amber-200">Coming soon</span>
                    )}
                    <div className="inline-flex items-center gap-2">
                        <div className="h-8 w-8 rounded-lg bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center">
                            <BackupDriveIcon />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1.5">
                                Google Drive
                                {driveOk && <span className="inline-flex items-center gap-1 text-emerald-700"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> Connected</span>}
                                {driveError && <span className="inline-flex items-center gap-1 text-rose-700"><span className="h-1.5 w-1.5 rounded-full bg-rose-500" /> Needs attention</span>}
                            </div>
                            <div className="text-sm font-semibold text-slate-900 truncate">
                                {driveConfigured ? (driveStatus?.folder_url ? <a href={driveStatus.folder_url} target="_blank" rel="noopener noreferrer" className="hover:text-[#25908A] hover:underline">Open shared folder ↗</a> : "Auto-mirror") : "Auto-mirror"}
                            </div>
                        </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-2 flex-1">
                        {driveOk && lastSync ? `Auto-syncs on every change · Last sync ${lastSync} · ${driveStatus?.last_summary?.files_uploaded ?? 0} files mirrored.` :
                         driveError ? "Auto-sync attempted but Google refused. Ask an admin to check the shared folder is a Shared Drive (not a personal My Drive folder)." :
                         "Auto-mirrors to your team\u2019s shared Google Drive within seconds of every create, edit, move or delete."}
                    </p>
                    {driveError && driveStatus?.last_error && (
                        <div className="mt-2 text-[10px] text-rose-800 bg-rose-100/60 border border-rose-200 rounded-md p-2 max-h-24 overflow-y-auto font-mono leading-tight">
                            {driveStatus.last_error}
                        </div>
                    )}
                    {driveConfigured && isAdmin && (
                        <div className="mt-3">
                            <button
                                type="button"
                                onClick={syncNow}
                                disabled={syncing}
                                data-testid="learning-backup-drive-sync-now"
                                className="inline-flex items-center gap-2 h-9 px-3 rounded-lg text-sm font-semibold bg-white text-blue-700 border border-blue-200 hover:bg-blue-50 disabled:opacity-50 transition-colors"
                            >
                                {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                                Sync now
                            </button>
                        </div>
                    )}
                    {!driveConfigured && isAdmin && (
                        <button
                            type="button"
                            onClick={() => setDriveOpen(true)}
                            data-testid="learning-backup-drive-btn"
                            className="mt-3 inline-flex items-center gap-2 h-9 px-3 rounded-lg text-sm font-semibold bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 transition-colors"
                        >
                            Set up →
                        </button>
                    )}
                </div>
            </div>
            {driveOpen && <DriveSetupSheet onClose={() => setDriveOpen(false)} />}
        </div>
    );
};

// Inline SVG icons kept local so we don't have to churn the LearningHub
// icon import list. Small teal/gold/blue color set matches the widget cards.
const BackupDeptIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21V7l9-4 9 4v14"/><path d="M9 21v-8h6v8"/></svg>);
const BackupOrgIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>);
const BackupDriveIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12.5 2H7L2 10.5 4.5 15h5L14 8Z"/><path d="M17 2h-4.5l5 8.5H22Z"/><path d="M14.5 15h7L18 21h-7Z"/></svg>);
const BackupDownloadIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>);

// Drive setup sheet — checklist of what the admin needs to configure
// before Phase 2 can flip live. Rendered as a light informational modal
// (no OAuth kick-off here; that ships once credentials land in .env).
const DriveSetupSheet = ({ onClose }) => (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} data-testid="learning-backup-drive-sheet">
        <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl ring-1 ring-slate-900/5 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 pt-5 pb-3 border-b border-slate-100">
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-blue-700">Google Drive auto-mirror</div>
                <div className="mt-1 font-heading text-xl font-semibold text-slate-900">Two-minute setup</div>
                <p className="text-sm text-slate-500 mt-1">Once the credentials below live in the backend, Drive will start mirroring every Learning Hub change in real time.</p>
            </div>
            <div className="px-6 py-4 text-sm text-slate-700 space-y-3">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                    <div className="font-bold uppercase tracking-wider text-[10px] mb-1">Important</div>
                    Google service accounts have <strong>no storage quota of their own</strong>, so they can&apos;t create files inside a personal &ldquo;My Drive&rdquo; folder. Use a <strong>Shared Drive</strong> instead (Workspace feature).
                </div>
                <ol className="list-decimal pl-4 space-y-2">
                    <li>Open <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">Google Cloud Console</a> → create or pick a project.</li>
                    <li>Enable <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">Google Drive API</span> in <em>APIs &amp; Services → Library</em>.</li>
                    <li>Create a <span className="font-semibold">Service Account</span> under <em>IAM &amp; Admin → Service Accounts</em>. Download the JSON key.</li>
                    <li>In Google Drive, open <em>New → More → Shared drive</em> and create a Shared Drive (not a regular folder in My Drive). Open the Shared Drive → Manage members → add the service account&apos;s email as a <strong>Content Manager</strong>.</li>
                    <li>Optional: create a subfolder inside the Shared Drive to keep the mirror scoped. Copy its URL.</li>
                    <li>Paste the JSON key + folder URL back here — we&apos;ll wire the sync and flip this card to &ldquo;Connected&rdquo;.</li>
                </ol>
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-900">
                    Once we have the credentials, every create / edit / move / delete inside the Learning Hub will mirror to Drive within seconds. Existing resources back-fill on first sync.
                </div>
            </div>
            <div className="px-6 py-3 border-t border-slate-100 flex justify-end gap-2">
                <button onClick={onClose} className="h-9 px-4 rounded-lg text-sm font-semibold text-slate-600 hover:text-slate-900">Got it</button>
            </div>
        </div>
    </div>
);

// ---------- Horizontal scrolling shelf of CollectionCards ----------
// Netflix-style row. On narrow screens it falls back to natural overflow
// scroll; on wider screens the chevron buttons paginate one viewport-width
// at a time.
const CollectionShelf = ({ title, subtitle, accent = "teal", items, emptyHint, onOpen, onEdit, onDelete, user, reorderable = false, onReorder, headerAction = null }) => {
    const scrollerRef = useRef(null);
    // Drag state — indexes into `items`. Kept local; when a drop lands we
    // hand the new id order up to the parent via onReorder.
    const [dragFrom, setDragFrom] = useState(null);
    const [dragOver, setDragOver] = useState(null);
    const scroll = (dir) => {
        const el = scrollerRef.current;
        if (!el) return;
        const step = Math.max(280, el.clientWidth * 0.85);
        el.scrollBy({ left: dir * step, behavior: "smooth" });
    };
    const accentCls =
        accent === "purple"
            ? "from-purple-50 to-white border-purple-100"
            : "from-teal-50/40 to-white border-teal-100";

    const commitDrop = (toIdx) => {
        if (!reorderable || dragFrom === null || toIdx === null || toIdx === dragFrom) {
            setDragFrom(null); setDragOver(null);
            return;
        }
        const next = items.slice();
        const [moved] = next.splice(dragFrom, 1);
        next.splice(toIdx, 0, moved);
        onReorder?.(next.map((c) => c.id));
        setDragFrom(null); setDragOver(null);
    };

    return (
        <div className={`bg-gradient-to-b ${accentCls} border rounded-2xl p-5`}>
            <div className="flex items-center justify-between gap-3 mb-4">
                <div className="min-w-0 flex-1">
                    <h3 className="font-heading font-semibold text-slate-900 text-lg tracking-tight truncate">{title}</h3>
                    {subtitle && <p className="text-xs text-slate-500 mt-0.5 truncate">{subtitle} · {items.length}</p>}
                </div>
                {/* Right-side controls — View-all + scroll arrows grouped so
                    the header reads as [ Title …………………………… actions ]. */}
                <div className="flex items-center gap-2 shrink-0">
                    {items.length > 1 && (
                        <div className="hidden sm:flex items-center gap-1">
                            <button
                                type="button"
                                onClick={() => scroll(-1)}
                                aria-label={`Scroll ${title} left`}
                                className="h-8 w-8 rounded-full bg-white border border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-300 flex items-center justify-center transition-colors"
                            >
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button
                            type="button"
                            onClick={() => scroll(1)}
                            aria-label={`Scroll ${title} right`}
                            className="h-8 w-8 rounded-full bg-white border border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-300 flex items-center justify-center transition-colors"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                )}
                    {headerAction}
                </div>
            </div>

            {items.length === 0 ? (
                <div className="text-sm text-slate-500 italic px-1">{emptyHint || "Nothing here yet."}</div>
            ) : (
                <div
                    ref={scrollerRef}
                    className="flex gap-4 overflow-x-auto scrollbar-thin scroll-smooth snap-x snap-mandatory pb-1"
                >
                    {items.map((c, idx) => (
                        <div
                            key={c.id}
                            className={`snap-start shrink-0 w-[280px] sm:w-[300px] transition-all ${dragOver === idx && dragFrom !== null && dragFrom !== idx ? "ring-2 ring-[#2EB4AC] ring-offset-2 rounded-2xl" : ""} ${dragFrom === idx ? "opacity-40" : ""}`}
                            draggable={reorderable}
                            onDragStart={reorderable ? (e) => {
                                setDragFrom(idx);
                                try { e.dataTransfer.effectAllowed = "move"; } catch { /* noop */ }
                            } : undefined}
                            onDragOver={reorderable ? (e) => { e.preventDefault(); setDragOver(idx); } : undefined}
                            onDragLeave={reorderable ? () => setDragOver((v) => (v === idx ? null : v)) : undefined}
                            onDrop={reorderable ? (e) => { e.preventDefault(); commitDrop(idx); } : undefined}
                            onDragEnd={reorderable ? () => { setDragFrom(null); setDragOver(null); } : undefined}
                            data-testid={reorderable ? `dnd-collection-${c.id}` : undefined}
                        >
                            <CollectionCard
                                c={c}
                                onOpen={() => onOpen(c)}
                                isMine={c.owner_id === user?.user_id}
                                canManage={!!c.can_manage}
                                isAssigned={c.assigned_to?.includes(user?.user_id)}
                                onEdit={() => onEdit(c)}
                                onDelete={() => onDelete(c)}
                                reorderable={reorderable}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// (The legacy <Tab> component was removed when the page moved to the
// catalog + shelves layout. See <PillRow> in components/LearningCatalog.jsx
// for the modern equivalent.)


// Each cover combines a color palette + a gradient direction (option D) and gets a deterministic icon (option A).
const CARD_COVERS = [
    { from: "from-amber-50", to: "to-amber-200", dir: "bg-gradient-to-r", iconBg: "bg-amber-300", iconText: "text-amber-800" },
    { from: "from-emerald-50", to: "to-emerald-200", dir: "bg-gradient-to-br", iconBg: "bg-emerald-300", iconText: "text-emerald-800" },
    { from: "from-sky-50", to: "to-sky-200", dir: "bg-gradient-to-b", iconBg: "bg-sky-300", iconText: "text-sky-800" },
    { from: "from-violet-50", to: "to-violet-200", dir: "bg-gradient-to-r", iconBg: "bg-violet-300", iconText: "text-violet-800" },
    { from: "from-rose-50", to: "to-rose-200", dir: "bg-gradient-to-br", iconBg: "bg-rose-300", iconText: "text-rose-800" },
    { from: "from-orange-50", to: "to-orange-200", dir: "bg-gradient-to-b", iconBg: "bg-orange-300", iconText: "text-orange-800" },
    { from: "from-teal-50", to: "to-teal-200", dir: "bg-gradient-to-r", iconBg: "bg-teal-300", iconText: "text-teal-800" },
];
const CARD_ICONS = [BookOpen, Megaphone, Code, Compass, Lightbulb, Rocket, Target, Brain, Palette, Briefcase, Layers, Sparkles];

const hashString = (s = "") => {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return h;
};
const pickCover = (name = "") => {
    const h = hashString(name);
    return {
        ...CARD_COVERS[h % CARD_COVERS.length],
        Icon: CARD_ICONS[Math.floor(h / 7) % CARD_ICONS.length],
    };
};

// ---------- "Edited by" strip — audit trail avatars for a collection ----------
// Shows up to 3 avatars of the most recent editors with a hover tooltip
// spelling out who did what and when. The list arrives pre-trimmed from
// the backend (`recent_edits`) and is already newest-first.
const EDIT_ACTION_LABELS = {
    "collection.update": "edited",
    "resource.create": "added a resource",
    "resource.update": "updated a resource",
    "resource.delete": "removed a resource",
    "folder.create": "added a folder",
    "folder.rename": "renamed a folder",
    "folder.delete": "removed a folder",
};
const _relTime = (iso) => {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    if (isNaN(then)) return "";
    const diff = Math.max(0, Date.now() - then);
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};
const EditedByStrip = ({ edits }) => {
    if (!edits || edits.length === 0) return null;
    const first = edits[0];
    const firstLabel = EDIT_ACTION_LABELS[first.action] || "made a change";
    return (
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 text-[10px] text-slate-500" data-testid="collection-edited-by">
            <span className="uppercase tracking-wider font-bold text-slate-400">Edited by</span>
            <div className="flex -space-x-1.5">
                {edits.slice(0, 3).map((e, i) => {
                    const label = EDIT_ACTION_LABELS[e.action] || "made a change";
                    const tip = `${e.name} · ${label} · ${_relTime(e.at)}`;
                    return (
                        <span
                            key={`${e.user_id}-${e.at}-${i}`}
                            title={tip}
                            className="h-5 w-5 rounded-full ring-2 ring-white bg-slate-100 text-slate-700 text-[9px] font-bold inline-flex items-center justify-center overflow-hidden uppercase"
                        >
                            {e.picture ? (
                                <img src={e.picture} alt="" className="h-full w-full object-cover" />
                            ) : (
                                (e.name || "?").charAt(0)
                            )}
                        </span>
                    );
                })}
            </div>
            <span className="truncate">
                <span className="font-semibold text-slate-700">{first.name}</span>{" "}
                {firstLabel + " "}
                <span className="text-slate-400">· {_relTime(first.at)}</span>
            </span>
        </div>
    );
};


const CollectionCard = ({ c, onOpen, isMine, canManage, isAssigned, onEdit, onDelete, reorderable = false }) => {
    const pct = c.resource_count ? Math.round((c.completed_count / c.resource_count) * 100) : 0;
    const cover = pickCover(c.name);
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);
    const triggerRef = useRef(null);
    const [menuPos, setMenuPos] = useState(null);

    useEffect(() => {
        if (!menuOpen) return;
        const onDoc = (e) => {
            if (!menuRef.current?.contains(e.target) && !triggerRef.current?.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        const onScroll = () => setMenuOpen(false);
        document.addEventListener("mousedown", onDoc);
        window.addEventListener("scroll", onScroll, true);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            window.removeEventListener("scroll", onScroll, true);
        };
    }, [menuOpen]);

    const openMenu = (e) => {
        e.stopPropagation();
        const rect = triggerRef.current.getBoundingClientRect();
        setMenuPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
        setMenuOpen((v) => !v);
    };

    return (
        <div
            data-testid={`collection-${c.id}`}
            className={`group bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden hover:border-[#2EB4AC] hover:shadow-md transition-all ${reorderable ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"}`}
            onClick={onOpen}
        >
            {/* Cover band — gradient direction + icon picked deterministically from name */}
            <div className={`relative h-24 ${cover.dir} ${cover.from} ${cover.to} flex items-center justify-center`}>
                <div className={`h-12 w-12 rounded-2xl ${cover.iconBg} ${cover.iconText} flex items-center justify-center shadow-sm`}>
                    <cover.Icon className="h-5 w-5" />
                </div>
                {!isMine && isAssigned && (
                    <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-white/80 backdrop-blur text-purple-800 border border-purple-200">
                        <UserPlus className="h-3 w-3" /> Assigned
                    </span>
                )}
                {!isMine && !isAssigned && canManage && (
                    <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-white/80 backdrop-blur text-teal-800 border border-teal-200">
                        <UserPlus className="h-3 w-3" /> Team
                    </span>
                )}
                {canManage && (
                    <button
                        ref={triggerRef}
                        onClick={openMenu}
                        data-testid={`collection-menu-${c.id}`}
                        aria-label="Collection options"
                        className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white/80 hover:bg-white text-slate-700 backdrop-blur flex items-center justify-center shadow-sm border border-white/40"
                    >
                        <MoreHorizontal className="h-4 w-4" />
                    </button>
                )}
            </div>

            {/* Body */}
            <div className="p-5">
                <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                        <h3 className="font-heading font-semibold text-slate-900 text-lg leading-tight group-hover:text-[#25908A] line-clamp-2">{c.name}</h3>
                        {c.description && <p className="text-sm text-slate-500 mt-1.5 line-clamp-2">{c.description}</p>}
                    </div>
                    {/* Collection progress ring — replaces the flat bar.
                        Hidden when there are no resources because a 0/0
                        ring is just visual noise. */}
                    {c.resource_count > 0 && (
                        <div className="shrink-0">
                            <ProgressRing
                                value={pct}
                                status={pct >= 100 ? "completed" : pct > 0 ? "in_progress" : "not_started"}
                                size={52}
                                stroke={5}
                                data-testid={`collection-progress-${c.id}`}
                            />
                        </div>
                    )}
                </div>

                <div className="mt-4 flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-medium">
                        <LinkIcon className="h-3 w-3" /> {c.resource_count} {c.resource_count === 1 ? "item" : "items"}
                    </span>
                    {c.resource_count > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 font-medium tabular-nums">
                            <Check className="h-3 w-3" /> {c.completed_count} done
                        </span>
                    )}
                    {!isMine && c.owner_name && (
                        <span className="inline-flex items-center gap-1 truncate">
                            <span className="h-4 w-4 rounded-full bg-slate-200 text-slate-700 text-[8px] font-bold inline-flex items-center justify-center uppercase">
                                {(c.owner_name || "?").charAt(0)}
                            </span>
                            <span className="truncate">{c.owner_name}</span>
                        </span>
                    )}
                </div>

                {/* Skills-you'll-gain chips (edX-style). Only render when the
                    collection owner has tagged skills — the card should
                    stay compact for legacy collections that don't yet have
                    the field. Capped at 4 visible chips + "+N" overflow. */}
                {Array.isArray(c.skills) && c.skills.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`collection-skills-${c.id}`}>
                        <Expandable
                            items={c.skills}
                            limit={4}
                            moreLabel={(n) => `+${n}`}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium text-slate-500 bg-slate-50 border border-slate-200 hover:border-[#2EB4AC] hover:text-[#25908A]"
                            testid={`collection-skills-more-${c.id}`}
                            render={(s, i) => (
                                <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-teal-50 text-[#25908A] border border-teal-100">
                                    {s}
                                </span>
                            )}
                        />
                    </div>
                )}

                {/* Audit trail — last 3 people who touched this collection */}
                <EditedByStrip edits={c.recent_edits} />
            </div>

            {/* Card-level menu portal */}
            {menuOpen && menuPos && createPortal(
                <div
                    ref={menuRef}
                    style={{ position: "fixed", top: menuPos.top, right: menuPos.right, zIndex: 1000 }}
                    className="w-44 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden py-1"
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onEdit?.(); }}
                        data-testid={`collection-edit-${c.id}`}
                        className="w-full flex items-center gap-2 px-3 h-9 text-sm text-slate-700 hover:bg-slate-50"
                    >
                        <Pencil className="h-3.5 w-3.5" /> Edit
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onDelete?.(); }}
                        data-testid={`collection-delete-${c.id}`}
                        className="w-full flex items-center gap-2 px-3 h-9 text-sm text-red-600 hover:bg-red-50"
                    >
                        <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                </div>,
                document.body
            )}
        </div>
    );
};

export const CreateCollectionDialog = ({ existing, onClose, onCreated }) => {
    const isEdit = !!existing;
    const { user } = useAuth();
    const { depts } = useData();
    const [name, setName] = useState(existing?.name || "");
    // Department the collection will live under. Non-admins are locked to
    // their own department; admins can pick any dept from the org list.
    const isAdmin = user?.role === "super_admin";
    const [deptId, setDeptId] = useState(existing?.owner_department_id || user?.department_id || "");
    // Cover treatment — either a raw image URL OR one of the five color
    // presets that map to the mockup's card-color palette.
    const [coverImage, setCoverImage] = useState(existing?.cover_image || "");
    const [coverColor, setCoverColor] = useState(existing?.cover_color || "teal");
    const [saving, setSaving] = useState(false);
    const COLOR_PRESETS = [
        { key: "teal",   label: "Teal",   from: "#0C7C6E", to: "#08443C" },
        { key: "amber",  label: "Amber",  from: "#E7A73E", to: "#8A5A0F" },
        { key: "coral",  label: "Coral",  from: "#E2604F", to: "#A3372A" },
        { key: "blue",   label: "Blue",   from: "#3E7CB1", to: "#1F4E76" },
        { key: "violet", label: "Violet", from: "#7C6FD6", to: "#4E3FA8" },
    ];
    const activePreset = COLOR_PRESETS.find((p) => p.key === coverColor) || COLOR_PRESETS[0];
    // Available departments = all depts the user knows about. Non-admins
    // see a locked dropdown pinned to their own dept.
    const availableDepts = useMemo(() => {
        const arr = (depts || []).filter((d) => d && d.id && d.name);
        return arr.sort((a, b) => a.name.localeCompare(b.name));
    }, [depts]);

    const submit = async () => {
        if (!name.trim()) { toast.error("Collection name is required"); return; }
        setSaving(true);
        try {
            const payload = {
                name: name.trim(),
                cover_image: coverImage.trim(),
                cover_color: coverColor || "teal",
            };
            if (isAdmin && deptId) payload.owner_department_id = deptId;
            if (isEdit) {
                await api.put(`/learning/collections/${existing.id}`, payload);
                toast.success("Collection updated");
            } else {
                await api.post("/learning/collections", payload);
                toast.success("Collection created");
            }
            onCreated();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setSaving(false); }
    };
    return (
        <Modal title={isEdit ? "Edit collection" : "New collection"} onClose={onClose}>
            <div>
                <Field label={<>Collection name <span style={{ color: "#E2604F" }}>*</span></>}>
                    <input
                        data-testid="collection-name-input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="lh-form-control"
                        placeholder="e.g. Amazon Marketplace | Fundamentals"
                        autoFocus
                    />
                </Field>
                <Field label="Department">
                    <select
                        data-testid="collection-department-input"
                        value={deptId}
                        onChange={(e) => setDeptId(e.target.value)}
                        disabled={!isAdmin}
                        className="lh-form-control"
                    >
                        {!deptId && <option value="">— Pick a department —</option>}
                        {availableDepts.map((d) => (
                            <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                    </select>
                    {!isAdmin && (
                        <p className="lh-hint">Non-admins can only create collections in their own department.</p>
                    )}
                </Field>
                <Field label="Cover image">
                    {/* Big color-treated preview — shows the picked image OR
                        renders the fallback color treatment when the URL is
                        empty (or the image fails to load). */}
                    <div
                        className="lh-cover-preview"
                        data-testid="collection-cover-preview"
                        style={
                            coverImage.trim()
                                ? { backgroundImage: `url(${coverImage.trim()})`, backgroundSize: "cover", backgroundPosition: "center" }
                                : { background: `linear-gradient(135deg, ${activePreset.from}, ${activePreset.to})` }
                        }
                    >
                        {!coverImage.trim() && <div className="lh-cover-caption">No image — using color treatment</div>}
                    </div>
                    <input
                        data-testid="collection-cover-url"
                        value={coverImage}
                        onChange={(e) => setCoverImage(e.target.value)}
                        className="lh-form-control"
                        placeholder="Paste an image URL (optional)…"
                        style={{ marginTop: 10 }}
                    />
                    <div className="lh-swatch-row" data-testid="collection-cover-swatches">
                        {COLOR_PRESETS.map((p) => (
                            <button
                                key={p.key}
                                type="button"
                                title={p.label}
                                aria-label={p.label}
                                data-testid={`swatch-${p.key}`}
                                onClick={() => setCoverColor(p.key)}
                                className={`lh-swatch ${coverColor === p.key ? "selected" : ""}`}
                                style={{ background: `linear-gradient(135deg, ${p.from}, ${p.to})` }}
                            />
                        ))}
                    </div>
                    <p className="lh-hint">Paste a URL for a real cover, or pick a color treatment — used as the fallback either way.</p>
                </Field>
            </div>
            <ModalFooter onCancel={onClose} onPrimary={submit} loading={saving} primaryLabel={isEdit ? "Save" : "Create collection"} />
        </Modal>
    );
};

// ---------- Bulk collections import ----------
// Users pick "Bulk import" from the Hub header. They can either:
//   1. Download a two-column CSV template (`name,description`), fill it and
//      re-upload, OR
//   2. Paste comma / TSV rows straight into the textarea.
// We parse on the client (tiny CSV parser that respects double-quoted
// values with embedded commas) so the preview updates as they type, then
// POST the cleaned rows to `/learning/collections/bulk` which handles
// dedupe + skip reasons and returns `{created, skipped}`.

// Minimal CSV parser — handles quoted values with commas/newlines inside
// and the standard `""` escape. Good enough for the two-column template we
// ship; we deliberately don't pull in a heavyweight CSV lib for this.
const parseCsvRows = (text) => {
    const rows = [];
    let cur = [];
    let field = "";
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (inQuotes) {
            if (ch === '"' && text[i + 1] === '"') { field += '"'; i++; }
            else if (ch === '"') { inQuotes = false; }
            else { field += ch; }
        } else if (ch === '"') {
            inQuotes = true;
        } else if (ch === "," || ch === "\t") {
            cur.push(field); field = "";
        } else if (ch === "\n" || ch === "\r") {
            if (field !== "" || cur.length > 0) { cur.push(field); rows.push(cur); }
            cur = []; field = "";
            if (ch === "\r" && text[i + 1] === "\n") i++;
        } else {
            field += ch;
        }
    }
    if (field !== "" || cur.length > 0) { cur.push(field); rows.push(cur); }
    return rows;
};

const BULK_TEMPLATE_CSV =
    "name,description\n" +
    "Amazon Ads Playbook,Weekly-refreshed guides for Amazon sponsored ads\n" +
    "Onboarding — Week 1,Everything a new hire should read in their first 5 days\n" +
    "Q1 Campaign Deep-Dives,Post-mortems and creatives from Q1 campaigns\n";

export const BulkCollectionsDialog = ({ onClose, onImported }) => {
    const [pasted, setPasted] = useState("");
    const [rows, setRows] = useState([]);   // [{name, description}]
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);  // {created, skipped} after submit
    const fileRef = useRef(null);

    // Reparse rows every time the textarea changes so the preview stays
    // live. We treat the first row as a header if it literally reads
    // `name`/`description` (case-insensitive), otherwise every non-empty
    // row counts as data.
    useEffect(() => {
        const raw = parseCsvRows(pasted);
        if (raw.length === 0) { setRows([]); return; }
        const first = (raw[0] || []).map((v) => (v || "").trim().toLowerCase());
        const start = (first[0] === "name") ? 1 : 0;
        const parsed = [];
        for (let i = start; i < raw.length; i++) {
            const r = raw[i];
            const name = (r[0] || "").trim();
            const desc = (r[1] || "").trim();
            if (!name && !desc) continue;
            parsed.push({ name, description: desc });
        }
        setRows(parsed);
    }, [pasted]);

    const downloadTemplate = () => {
        const blob = new Blob([BULK_TEMPLATE_CSV], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "learning-collections-template.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    const onFilePicked = async (file) => {
        if (!file) return;
        if (file.size > 512 * 1024) { toast.error("File too large (max 512 KB)"); return; }
        try {
            const text = await file.text();
            setPasted(text);
        } catch { toast.error("Couldn't read that file"); }
    };

    const submit = async () => {
        const cleaned = rows.filter((r) => (r.name || "").trim().length > 0);
        if (cleaned.length === 0) { toast.error("Add at least one row with a name"); return; }
        if (cleaned.length > 500) { toast.error("Split into batches of 500 rows or fewer"); return; }
        setBusy(true);
        try {
            const r = await api.post("/learning/collections/bulk", { items: cleaned });
            setResult(r.data || { created: [], skipped: [] });
            const created = (r.data?.created || []).length;
            const skipped = (r.data?.skipped || []).length;
            if (created > 0) {
                toast.success(`Created ${created} collection${created === 1 ? "" : "s"}${skipped > 0 ? ` — ${skipped} skipped` : ""}`);
            } else {
                toast.warning(`Nothing created — ${skipped} row${skipped === 1 ? "" : "s"} skipped`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bulk import failed");
        } finally { setBusy(false); }
    };

    const done = () => { onImported?.(); };

    return (
        <Modal title="Bulk import collections" onClose={result ? done : onClose}>
            {!result ? (
                <div className="space-y-4">
                    <div className="flex items-center flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={downloadTemplate}
                            data-testid="bulk-collections-download-template"
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold transition"
                        >
                            <FileText className="h-4 w-4" /> Download CSV template
                        </button>
                        <button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            data-testid="bulk-collections-upload-file"
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold transition"
                        >
                            <UploadCloud className="h-4 w-4" /> Upload CSV
                        </button>
                        <input
                            ref={fileRef}
                            type="file"
                            accept=".csv,text/csv,text/plain"
                            className="hidden"
                            onChange={(e) => onFilePicked(e.target.files?.[0])}
                            data-testid="bulk-collections-file-input"
                        />
                        <span className="text-[11px] text-slate-500">…or paste rows below</span>
                    </div>

                    <Field label="Collections (one per line — `name, description`)">
                        <textarea
                            value={pasted}
                            onChange={(e) => setPasted(e.target.value)}
                            data-testid="bulk-collections-paste-input"
                            rows={9}
                            placeholder={"name,description\nAmazon Ads Playbook, Weekly-refreshed guides\nOnboarding — Week 1, First-week reading list"}
                            className="w-full px-3 py-2 text-sm font-mono border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2EB4AC]/25 focus:border-[#2EB4AC] outline-none resize-y"
                        />
                    </Field>

                    <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-3">
                        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-2 flex items-center justify-between">
                            <span>Preview · {rows.length} row{rows.length === 1 ? "" : "s"}</span>
                            {rows.length > 500 && <span className="text-rose-600">Max 500 per batch</span>}
                        </div>
                        {rows.length === 0 ? (
                            <div className="text-xs text-slate-400 italic">Paste rows or upload a CSV to see the preview here.</div>
                        ) : (
                            <div className="max-h-56 overflow-y-auto -mx-1 px-1" data-testid="bulk-collections-preview">
                                <ul className="space-y-1">
                                    <Expandable
                                        items={rows}
                                        limit={100}
                                        className="text-[11px] text-[#25908A] font-semibold pl-2"
                                        testid="import-preview-more"
                                        render={(r, i) => (
                                            <li key={i} className="text-xs bg-white rounded-md border border-slate-100 px-2.5 py-1.5 flex items-baseline gap-2">
                                                <span className="text-slate-400 tabular-nums w-6 text-right">{i + 1}.</span>
                                                <span className="font-semibold text-slate-800 truncate">{r.name || <em className="text-rose-500 not-italic">(missing name)</em>}</span>
                                                {r.description && <span className="text-slate-500 truncate">— {r.description}</span>}
                                            </li>
                                        )}
                                    />
                                </ul>
                            </div>
                        )}
                    </div>

                    <ModalFooter
                        onCancel={onClose}
                        onPrimary={submit}
                        loading={busy}
                        primaryLabel={`Create ${rows.filter((r) => r.name).length} collection${rows.filter((r) => r.name).length === 1 ? "" : "s"}`}
                        disabled={rows.filter((r) => r.name).length === 0 || busy}
                    />
                </div>
            ) : (
                <div className="space-y-4" data-testid="bulk-collections-result">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-4">
                            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Created</div>
                            <div className="text-2xl font-heading font-bold text-emerald-800">{result.created.length}</div>
                        </div>
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Skipped</div>
                            <div className="text-2xl font-heading font-bold text-slate-700">{result.skipped.length}</div>
                        </div>
                    </div>
                    {result.skipped.length > 0 && (
                        <div className="rounded-lg border border-slate-100 bg-white p-3">
                            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">Skip reasons</div>
                            <ul className="space-y-1 max-h-40 overflow-y-auto">
                                {result.skipped.map((s, i) => (
                                    <li key={i} className="text-xs flex items-baseline gap-2">
                                        <span className="text-rose-500">•</span>
                                        <span className="font-semibold text-slate-700 truncate">{s.name || "(no name)"}</span>
                                        <span className="text-slate-500 truncate">— {s.reason}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <ModalFooter onCancel={done} onPrimary={done} primaryLabel="Done" />
                </div>
            )}
        </Modal>
    );
};

// ---------- CollectionActivityPanel — full audit trail with Undo ----------
// Fetches the collection's `edit_history` (up to 10 entries, newest first)
// via GET /learning/collections/{cid}/activity. Rows for `collection.update`
// and `folder.rename` show a live "Undo" button that hits the /undo endpoint;
// destructive actions (create/delete resource or folder) surface a
// "This action can't be undone" note per the user's spec.
const _relTimeShort = (iso) => {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    if (isNaN(then)) return "";
    const diff = Math.max(0, Date.now() - then);
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};
const _actionSentence = (e) => {
    const a = e.action;
    if (a === "collection.update") {
        const before = e.before || {}; const after = e.after || {};
        const parts = [];
        if (before.name !== undefined && before.name !== after.name) parts.push(`renamed to "${after.name}"`);
        if (before.description !== undefined && before.description !== after.description) parts.push("edited the description");
        return parts.length ? parts.join(" and ") : "edited the collection";
    }
    if (a === "folder.rename") {
        const b = (e.before || {}).name; const af = (e.after || {}).name;
        return b && af ? `renamed folder "${b}" → "${af}"` : "renamed a folder";
    }
    if (a === "folder.create") return "added a folder";
    if (a === "folder.delete") return "removed a folder";
    if (a === "resource.create") return "added a resource";
    if (a === "resource.update") return "updated a resource";
    if (a === "resource.delete") return "removed a resource";
    return "made a change";
};

const CollectionActivityPanel = ({ collectionId, onChanged }) => {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyEntry, setBusyEntry] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/learning/collections/${collectionId}/activity`);
            setEntries(r.data?.entries || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load activity");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [collectionId]);

    const doUndo = async (entry) => {
        if (!entry?.entry_id) return;
        setBusyEntry(entry.entry_id);
        try {
            await api.post(`/learning/collections/${collectionId}/activity/${entry.entry_id}/undo`);
            toast.success("Change reverted");
            await load();
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Undo failed");
        } finally { setBusyEntry(null); }
    };

    if (loading) return <div className="text-center text-slate-500 text-sm py-10"><Loader2 className="h-5 w-5 mx-auto animate-spin text-[#2EB4AC]" /></div>;
    if (entries.length === 0) return (
        <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center text-sm text-slate-500">
            No activity yet — edits, folder renames, and resource changes will show up here.
        </div>
    );
    return (
        <ul className="divide-y divide-slate-100 rounded-xl border border-slate-100 bg-white" data-testid="collection-activity-list">
            {entries.map((e) => {
                const isDestructive = ["resource.delete", "folder.delete"].includes(e.action);
                return (
                    <li key={e.entry_id || `${e.at}-${e.action}`} className="px-4 py-3 flex items-start gap-3">
                        <span className="h-8 w-8 rounded-full ring-2 ring-white bg-slate-100 text-slate-700 text-xs font-bold inline-flex items-center justify-center overflow-hidden uppercase shrink-0">
                            {e.picture ? (<img src={e.picture} alt="" className="h-full w-full object-cover" />) : ((e.name || "?").charAt(0))}
                        </span>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm">
                                <span className="font-semibold text-slate-900">{e.name}</span>{" "}
                                <span className="text-slate-600">{_actionSentence(e)}</span>
                            </div>
                            <div className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-2 flex-wrap">
                                <span>{_relTimeShort(e.at)}</span>
                                {isDestructive && (
                                    <span className="inline-flex items-center gap-1 text-slate-400 italic">· This action can&apos;t be undone</span>
                                )}
                            </div>
                        </div>
                        {e.undoable ? (
                            <button
                                type="button"
                                onClick={() => doUndo(e)}
                                disabled={busyEntry === e.entry_id}
                                data-testid={`activity-undo-${e.entry_id}`}
                                className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[11px] font-semibold text-slate-700 border border-slate-200 hover:border-[#2EB4AC] hover:text-[#25908A] disabled:opacity-40 disabled:cursor-not-allowed transition"
                            >
                                {busyEntry === e.entry_id ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                                Undo
                            </button>
                        ) : (
                            <span className="text-[10px] uppercase tracking-wider text-slate-300 font-bold shrink-0 self-center">Read-only</span>
                        )}
                    </li>
                );
            })}
        </ul>
    );
};

const CollectionDetailDialog = ({ collection, isOwner, onClose, onChanged }) => {
    const { user } = useAuth();
    const { users } = useData();
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [addFolderId, setAddFolderId] = useState("");  // folder to pre-select when Add is opened from a folder
    const [editingRes, setEditingRes] = useState(null);
    const [assigningRes, setAssigningRes] = useState(null);
    const [showAssign, setShowAssign] = useState(false);
    // NEW — folders inside this collection.
    const [folders, setFolders] = useState([]);
    const [collapsed, setCollapsed] = useState({});  // { folder_id: bool }
    const [newFolderOpen, setNewFolderOpen] = useState(false);
    const [newFolderName, setNewFolderName] = useState("");
    const [creatingFolder, setCreatingFolder] = useState(false);
    // "resources" (default, existing UI) vs "activity" (audit trail with Undo)
    const [detailTab, setDetailTab] = useState("resources");

    const load = async () => {
        setLoading(true);
        try {
            const [rr, ff] = await Promise.all([
                api.get(`/learning/collections/${collection.id}/resources`),
                api.get(`/learning/collections/${collection.id}/folders`),
            ]);
            setResources(rr.data);
            setFolders(ff.data || []);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [collection.id]);

    const createFolder = async () => {
        const n = newFolderName.trim();
        if (!n) return;
        setCreatingFolder(true);
        try {
            const r = await api.post(`/learning/collections/${collection.id}/folders`, { name: n });
            setFolders((prev) => [...prev, r.data].sort((a, b) => a.name.localeCompare(b.name)));
            setNewFolderName("");
            setNewFolderOpen(false);
            toast.success(`Folder “${n}” created`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't create folder");
        } finally { setCreatingFolder(false); }
    };

    const renameFolder = async (f) => {
        const n = window.prompt("Rename folder to:", f.name);
        if (!n || n.trim() === f.name) return;
        try {
            const r = await api.patch(`/learning/learning-folders/${f.id}`, { name: n.trim() });
            setFolders((prev) => prev.map((x) => x.id === f.id ? { ...x, ...r.data } : x));
            toast.success("Folder renamed");
        } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't rename"); }
    };

    const deleteFolder = async (f) => {
        if (!window.confirm(`Delete folder “${f.name}”? Resources inside will move back to the collection root.`)) return;
        try {
            await api.delete(`/learning/learning-folders/${f.id}`);
            setFolders((prev) => prev.filter((x) => x.id !== f.id));
            // Detach folder_id on the local resources list
            setResources((prev) => prev.map((r) => r.folder_id === f.id ? { ...r, folder_id: null } : r));
            toast.success("Folder deleted");
        } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete"); }
    };

    // Drag state for owner-only reorder. Stores the index being dragged
    // and the index hovered so we can render an insertion line. On drop
    // we splice + optimistically update, then persist to the server.
    const [dragFromIdx, setDragFromIdx] = useState(null);
    const [dragOverIdx, setDragOverIdx] = useState(null);
    const persistReorder = async (orderedIds) => {
        try {
            await api.put(`/learning/collections/${collection.id}/resources/reorder`, { resource_ids: orderedIds });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save order");
            // Snap back to the server's truth
            load();
        }
    };
    const handleDrop = (toIdx) => {
        if (dragFromIdx === null || toIdx === null || dragFromIdx === toIdx) {
            setDragFromIdx(null); setDragOverIdx(null);
            return;
        }
        setResources((rs) => {
            const next = rs.slice();
            const [moved] = next.splice(dragFromIdx, 1);
            // Adjust target index when moving downward
            const adjusted = dragFromIdx < toIdx ? toIdx - 1 : toIdx;
            next.splice(adjusted, 0, moved);
            persistReorder(next.map((x) => x.id));
            return next;
        });
        setDragFromIdx(null);
        setDragOverIdx(null);
    };

    const setStatus = async (rid, status) => {
        try {
            await api.put(`/learning/resources/${rid}/status`, { status });
            setResources((rs) => rs.map((r) => r.id === rid ? { ...r, my_status: status } : r));
            onChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    const removeRes = async (rid) => {
        if (!window.confirm("Remove this resource?")) return;
        try {
            await api.delete(`/learning/resources/${rid}`);
            setResources((rs) => rs.filter((r) => r.id !== rid));
            onChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    const isManagerOrAdmin = user?.role === "manager" || user?.role === "super_admin";
    const assignedNames = (collection.assigned_to || [])
        .map((uid) => users.find((u) => u.user_id === uid)?.name)
        .filter(Boolean);

    return (
        <Modal title={collection.name} onClose={onClose} wide>
            <div className="space-y-5">
                {collection.description && <p className="text-sm text-slate-500">{collection.description}</p>}

                <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                        <span>{resources.length} resource{resources.length !== 1 ? "s" : ""}</span>
                        {!isOwner && collection.owner_name && (
                            <span>· by {collection.owner_name}</span>
                        )}
                        {assignedNames.length > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 border border-purple-200 text-[11px]">
                                <Users className="h-3 w-3" /> Shared with {assignedNames.length}
                            </span>
                        )}
                    </div>
                    {isOwner && (
                        <div className="flex items-center gap-2 flex-wrap">
                            {isManagerOrAdmin && (
                                <button onClick={() => setShowAssign(true)} data-testid="assign-collection-btn" className="inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-slate-200 hover:border-[#2EB4AC] hover:text-[#2EB4AC] text-sm font-medium transition-colors">
                                    <UserPlus className="h-4 w-4" /> Assign to managees
                                </button>
                            )}
                            <button
                                onClick={() => setNewFolderOpen((v) => !v)}
                                data-testid="new-folder-btn"
                                className="inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-slate-200 hover:border-amber-500 hover:text-amber-600 text-sm font-medium transition-colors"
                            >
                                <FolderPlus className="h-4 w-4" /> New folder
                            </button>
                            <button onClick={() => { setAddFolderId(""); setShowAdd(true); }} data-testid="add-resource-btn" className="inline-flex items-center gap-2 h-10 px-4 bg-[#2EB4AC] hover:bg-[#25908A] text-white rounded-lg font-medium text-sm transition-colors">
                                <Plus className="h-4 w-4" /> Add resource
                            </button>
                        </div>
                    )}
                </div>

                {/* Inline "new folder" input row */}
                {isOwner && newFolderOpen && (
                    <div className="border border-amber-200 bg-amber-50/50 rounded-lg p-3 flex items-center gap-2" data-testid="new-folder-form">
                        <FolderPlus className="h-4 w-4 text-amber-600 flex-shrink-0" />
                        <input
                            autoFocus
                            value={newFolderName}
                            onChange={(e) => setNewFolderName(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && createFolder()}
                            placeholder="Folder name (e.g. Onboarding, Q3 courses)"
                            data-testid="new-folder-input"
                            className="flex-1 h-9 px-3 border border-slate-200 rounded-md bg-white focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none text-sm"
                            maxLength={120}
                        />
                        <button
                            onClick={createFolder}
                            disabled={creatingFolder || !newFolderName.trim()}
                            data-testid="new-folder-submit"
                            className="h-9 px-3 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold disabled:opacity-60"
                        >
                            {creatingFolder ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
                        </button>
                        <button
                            onClick={() => { setNewFolderOpen(false); setNewFolderName(""); }}
                            className="h-9 px-2 rounded-md text-xs text-slate-500 hover:bg-slate-100"
                        >
                            Cancel
                        </button>
                    </div>
                )}

                {/* Tabs — Resources (default) vs Activity (audit trail + undo).
                    Activity is available to everyone since curation is now
                    org-wide; the tab acts as the safety net + accountability
                    layer. */}
                <div className="flex items-center gap-1 border-b border-slate-100 -mt-1" data-testid="collection-detail-tabs">
                    {[
                        { key: "resources", label: `Resources · ${resources.length}` },
                        { key: "activity", label: "Activity" },
                    ].map((t) => (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => setDetailTab(t.key)}
                            data-testid={`collection-detail-tab-${t.key}`}
                            className={`px-3 h-9 -mb-px text-sm font-semibold border-b-2 transition ${
                                detailTab === t.key
                                    ? "border-[#2EB4AC] text-[#1F7F79]"
                                    : "border-transparent text-slate-500 hover:text-slate-800"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {detailTab === "activity" ? (
                    <CollectionActivityPanel collectionId={collection.id} onChanged={onChanged} />
                ) : loading ? (
                    <div className="text-center text-slate-500 text-sm py-10"><Loader2 className="h-5 w-5 mx-auto animate-spin text-[#2EB4AC]" /></div>
                ) : resources.length === 0 && folders.length === 0 ? (
                    <div className="border-2 border-dashed border-teal-300 bg-teal-50/30 rounded-lg p-8 text-center">
                        <LinkIcon className="h-6 w-6 text-[#2EB4AC] mx-auto" />
                        <p className="mt-2 text-sm font-medium text-slate-900">No resources yet</p>
                        {isOwner && <p className="text-xs text-slate-500">Click &quot;Add resource&quot; and paste a link — preview will be auto-generated.</p>}
                    </div>
                ) : (
                    <div className="space-y-3" data-testid="resource-list">
                        {/* Folder groups first — each collapsible, with rename/delete for owners */}
                        {folders.map((f) => {
                            const inside = resources.filter((r) => r.folder_id === f.id);
                            const isCollapsed = !!collapsed[f.id];
                            return (
                                <div key={f.id} className="border border-amber-200 bg-amber-50/30 rounded-xl overflow-hidden" data-testid={`folder-group-${f.id}`}>
                                    <div className="flex items-center gap-2 px-3 py-2.5 bg-amber-50/60">
                                        <button
                                            onClick={() => setCollapsed((c) => ({ ...c, [f.id]: !isCollapsed }))}
                                            data-testid={`folder-toggle-${f.id}`}
                                            className="inline-flex items-center gap-2 flex-1 min-w-0 text-left"
                                        >
                                            <ChevronDown className={`h-4 w-4 text-amber-700 transition-transform ${isCollapsed ? "-rotate-90" : ""}`} />
                                            <FolderIcon className="h-4 w-4 text-amber-600" />
                                            <span className="font-semibold text-sm text-slate-900 truncate">{f.name}</span>
                                            <span className="text-[10px] uppercase tracking-wider text-amber-700 font-bold bg-white/60 rounded-full px-1.5 py-0.5 tabular-nums">{inside.length}</span>
                                        </button>
                                        {isOwner && (
                                            <>
                                                <button
                                                    onClick={() => { setAddFolderId(f.id); setShowAdd(true); }}
                                                    data-testid={`folder-add-btn-${f.id}`}
                                                    title="Add resource to this folder"
                                                    className="h-7 w-7 rounded-lg text-amber-700 hover:bg-amber-100 flex items-center justify-center"
                                                >
                                                    <Plus className="h-3.5 w-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => renameFolder(f)}
                                                    data-testid={`folder-rename-btn-${f.id}`}
                                                    title="Rename folder"
                                                    className="h-7 w-7 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-white/70 flex items-center justify-center"
                                                >
                                                    <Edit3 className="h-3.5 w-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => deleteFolder(f)}
                                                    data-testid={`folder-delete-btn-${f.id}`}
                                                    title="Delete folder"
                                                    className="h-7 w-7 rounded-lg text-slate-500 hover:text-rose-600 hover:bg-white/70 flex items-center justify-center"
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                    {!isCollapsed && (
                                        <div className="p-3 space-y-3">
                                            {inside.length === 0 ? (
                                                <div className="text-xs text-slate-500 text-center py-3">
                                                    Empty folder{isOwner ? " · click + to add a resource" : ""}
                                                </div>
                                            ) : (
                                                inside.map((r) => (
                                                    <ResourceCard
                                                        key={r.id}
                                                        r={r}
                                                        canDelete={isOwner}
                                                        canEdit={isOwner}
                                                        onEdit={() => setEditingRes(r)}
                                                        onRemove={() => removeRes(r.id)}
                                                        onStatus={(s) => setStatus(r.id, s)}
                                                        isOwner={isOwner}
                                                        onAssign={() => setAssigningRes(r)}
                                                        allUsers={users}
                                                        collectionAssignees={collection.assigned_to || []}
                                                        onMoved={async ({ to, mode }) => {
                                                            if (to === collection.id && mode === "copy") await load();
                                                            else if (mode === "move" && to !== collection.id) setResources((rs) => rs.filter((x) => x.id !== r.id));
                                                            onChanged?.();
                                                        }}
                                                        collectionOwnerId={collection.owner_id}
                                                    />
                                                ))
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {/* Root-level (no folder) resources — reorderable as before */}
                        {isOwner && resources.filter((r) => !r.folder_id).length > 1 && (
                            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold pl-1">
                                Drag the grip handle to reorder
                            </div>
                        )}
                        {resources.filter((r) => !r.folder_id).map((r, idx) => (
                            <div
                                key={r.id}
                                onDragOver={isOwner ? (e) => { e.preventDefault(); setDragOverIdx(idx); } : undefined}
                                onDrop={isOwner ? (e) => { e.preventDefault(); handleDrop(idx); } : undefined}
                                onDragLeave={isOwner ? () => setDragOverIdx((cur) => cur === idx ? null : cur) : undefined}
                                className={`relative transition-opacity ${dragFromIdx === idx ? "opacity-40" : ""}`}
                                data-testid={`resource-row-${r.id}`}
                            >
                                {isOwner && dragOverIdx === idx && dragFromIdx !== idx && (
                                    <div className="absolute -top-1.5 left-0 right-0 h-1 bg-[#2EB4AC] rounded-full shadow-[0_0_10px_rgba(46,180,172,0.65)] pointer-events-none" />
                                )}
                                <ResourceCard
                                    r={r}
                                    canDelete={isOwner}
                                    canEdit={isOwner}
                                    onEdit={() => setEditingRes(r)}
                                    onRemove={() => removeRes(r.id)}
                                    onStatus={(s) => setStatus(r.id, s)}
                                    isOwner={isOwner}
                                    onAssign={() => setAssigningRes(r)}
                                    allUsers={users}
                                    collectionAssignees={collection.assigned_to || []}
                                    onMoved={async ({ to, mode }) => {
                                        // If MOVED, drop from this collection's list (it's
                                        // gone from here). If COPIED, keep it — the original
                                        // stays; only re-fetch to pick up the new resource
                                        // on the target's card (via `onChanged`).
                                        if (mode !== "copy") {
                                            setResources((rs) => rs.filter((x) => x.id !== r.id));
                                        }
                                        onChanged?.();
                                        toast.success(
                                            mode === "copy"
                                                ? `Copied to “${to?.name || "collection"}”`
                                                : `Moved to “${to?.name || "collection"}”`
                                        );
                                    }}
                                    dragHandleProps={isOwner ? {
                                        draggable: true,
                                        onDragStart: (e) => {
                                            setDragFromIdx(idx);
                                            try { e.dataTransfer.effectAllowed = "move"; } catch { /* noop */ }
                                        },
                                        onDragEnd: () => { setDragFromIdx(null); setDragOverIdx(null); },
                                    } : null}
                                />
                            </div>
                        ))}
                        {/* Drop-zone trailer so a card can be moved to the very end */}
                        {isOwner && resources.length > 0 && (
                            <div
                                onDragOver={(e) => { e.preventDefault(); setDragOverIdx(resources.length); }}
                                onDrop={(e) => { e.preventDefault(); handleDrop(resources.length); }}
                                onDragLeave={() => setDragOverIdx((cur) => cur === resources.length ? null : cur)}
                                className="relative h-4"
                            >
                                {dragOverIdx === resources.length && (
                                    <div className="absolute top-0 left-0 right-0 h-1 bg-[#2EB4AC] rounded-full shadow-[0_0_10px_rgba(46,180,172,0.65)]" />
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {showAdd && <AddResourceDialog cid={collection.id} defaultFolderId={addFolderId} onClose={() => { setShowAdd(false); setAddFolderId(""); }} onAdded={async () => { await load(); setShowAdd(false); setAddFolderId(""); onChanged?.(); }} />}
            {editingRes && <AddResourceDialog
                cid={collection.id}
                existing={editingRes}
                onClose={() => setEditingRes(null)}
                onUpdated={(fresh) => {
                    if (!fresh?.id) return;
                    // Optimistic local merge — keeps the rename visible
                    // immediately, even if the follow-up `load()` is slow
                    // or transient-fails. We merge (not replace) so that
                    // reactions/receipts/my_status decorations the GET
                    // adds are preserved.
                    setResources((prev) => prev.map((r) => r.id === fresh.id ? { ...r, ...fresh } : r));
                }}
                onAdded={async () => { await load(); setEditingRes(null); onChanged?.(); }}
            />}
            {assigningRes && (
                <AssignResourceDialog
                    resource={assigningRes}
                    collection={collection}
                    allUsers={users}
                    onClose={() => setAssigningRes(null)}
                    onSaved={async () => { await load(); setAssigningRes(null); onChanged?.(); }}
                />
            )}
            {showAssign && <AssignDialog collection={collection} onClose={() => setShowAssign(false)} onSaved={async () => { setShowAssign(false); onChanged?.(); }} />}
        </Modal>
    );
};

export const ResourceCard = ({ r, canDelete, canEdit, onEdit, onRemove, onStatus, isOwner, onAssign, allUsers, collectionAssignees, dragHandleProps, onMoved }) => {
    const TypeIcon = TYPE_ICON[r.type] || Globe;

    const [notesOpen, setNotesOpen] = useState(false);
    const [notes, setNotes] = useState([]);
    const [notesLoaded, setNotesLoaded] = useState(false);
    const [showMove, setShowMove] = useState(false);

    const loadNotes = useCallback(async () => {
        try {
            const res = await api.get(`/learning/resources/${r.id}/notes`);
            setNotes(res.data || []);
            setNotesLoaded(true);
        } catch {
            setNotesLoaded(true);
        }
    }, [r.id]);

    const toggleNotes = async () => {
        const willOpen = !notesOpen;
        setNotesOpen(willOpen);
        if (willOpen && !notesLoaded) await loadNotes();
    };

    const addNote = async ({ text, url }) => {
        try {
            const res = await api.post(`/learning/resources/${r.id}/notes`, { text, url });
            setNotes((ns) => [res.data, ...ns]);
            toast.success("Learning shared with the team");
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    const editNote = async (nid, { text, url }) => {
        try {
            const res = await api.patch(`/learning/notes/${nid}`, { text, url });
            setNotes((ns) => ns.map((n) => (n.id === nid ? res.data : n)));
            toast.success("Updated");
            return true;
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); return false; }
    };

    const deleteNote = async (nid) => {
        try {
            await api.delete(`/learning/notes/${nid}`);
            setNotes((ns) => ns.filter((n) => n.id !== nid));
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    const img = r.preview?.image;
    const site = r.preview?.site;
    // Build a download href for file-mode resources (those with a
    // `storage_path`). Every segment stays URL-encoded so paths with
    // spaces / non-ASCII still resolve cleanly, and cookies flow with the
    // same-origin request so the backend's auth gate lets it through.
    const fileHref = r.storage_path
        ? `${API}/learning/files/${r.storage_path.split("/").map(encodeURIComponent).join("/")}`
        : "";
    const hasUrl = !!r.url;
    const hasFile = !!fileHref;
    const openHref = hasUrl ? r.url : fileHref;
    const openable = hasUrl || hasFile;
    const openLabel = hasFile && !hasUrl ? "Open file" : "Open";
    const Wrap = openable ? "a" : "div";
    const wrapProps = openable
        ? {
              href: openHref,
              target: "_blank",
              rel: "noreferrer",
              // File downloads suggest the original filename so browsers
              // don't drop the storage UUID name on the user.
              ...(hasFile && !hasUrl ? { download: r.file_original_name || r.name || "" } : {}),
              onClick: () => pingOpen(r.id),
          }
        : {};

    return (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden hover:shadow-sm transition-all flex flex-col" data-testid={`resource-${r.id}`}>
            <div className="flex">
            {dragHandleProps && (
                <div
                    {...dragHandleProps}
                    data-testid={`drag-handle-${r.id}`}
                    className="hidden sm:flex flex-shrink-0 w-8 cursor-grab active:cursor-grabbing items-center justify-center text-slate-300 hover:text-[#2EB4AC] hover:bg-teal-50 border-r border-slate-100 transition-colors select-none"
                    title="Drag to reorder"
                >
                    <GripVertical className="h-4 w-4" />
                </div>
            )}
            <div className="flex flex-col sm:flex-row flex-1 min-w-0">
                {img ? (
                    <Wrap {...wrapProps} className="sm:w-44 h-28 sm:h-auto bg-slate-100 flex-shrink-0 overflow-hidden">
                        <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                    </Wrap>
                ) : (
                    <Wrap {...wrapProps} className="sm:w-44 h-28 sm:h-auto bg-gradient-to-br from-teal-50 to-slate-50 flex-shrink-0 flex items-center justify-center">
                        <TypeIcon className="h-8 w-8 text-[#2EB4AC]" />
                    </Wrap>
                )}
                <div className="p-4 flex-1 min-w-0 flex flex-col">
                    <div className="flex items-start gap-2 justify-between">
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 text-[11px] text-slate-500 uppercase tracking-wider font-semibold">
                                <span className="inline-flex items-center gap-1">
                                    <TypeIcon className="h-3 w-3" /> {TYPE_LABEL[r.type] || "Link"}
                                </span>
                                {site && <span className="truncate normal-case tracking-normal">· {site}</span>}
                            </div>
                            <h4 className="mt-1 font-heading font-semibold text-slate-900 truncate">{r.name}</h4>
                            {r.description && <p className="text-sm text-slate-500 mt-1 line-clamp-2">{r.description}</p>}
                        </div>
                        {(canEdit || canDelete) && (
                            <div className="flex items-center gap-1 flex-shrink-0">
                                {canEdit && (
                                    <button onClick={onEdit} data-testid={`edit-resource-${r.id}`} className="h-8 w-8 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-900 flex items-center justify-center" aria-label="Edit">
                                        <Pencil className="h-4 w-4" />
                                    </button>
                                )}
                                {canDelete && (
                                    <button onClick={onRemove} data-testid={`delete-resource-${r.id}`} className="h-8 w-8 rounded-md hover:bg-red-50 text-red-600 flex items-center justify-center" aria-label="Remove">
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="mt-3 flex items-center gap-3 flex-wrap">
                        {openable && (
                            <a
                                href={openHref}
                                target="_blank"
                                rel="noreferrer"
                                {...(hasFile && !hasUrl ? { download: r.file_original_name || r.name || "" } : {})}
                                onClick={() => pingOpen(r.id)}
                                data-testid={`open-resource-${r.id}`}
                                className="inline-flex items-center gap-1 h-7 px-2.5 -ml-2.5 rounded-md text-xs font-bold text-[#1F7F79] hover:bg-teal-50 transition-colors"
                            >
                                {openLabel} <ExternalLink className="h-3 w-3" />
                            </a>
                        )}

                        {/* Reactions cluster — collapses to a single "+" by default */}
                        <ReactionBar
                            resourceId={r.id}
                            reactions={r.reactions}
                            myReactions={r.my_reactions}
                        />

                        {/* Subtle dot separator before the meta actions */}
                        <span className="h-1 w-1 rounded-full bg-slate-300" aria-hidden />

                        <button
                            onClick={toggleNotes}
                            data-testid={`toggle-notes-${r.id}`}
                            title="Team learnings"
                            className={`inline-flex items-center gap-1.5 h-7 px-2 rounded-md text-xs font-medium transition-colors ${
                                notesOpen
                                    ? "bg-teal-50 text-[#1F7F79]"
                                    : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                            }`}
                        >
                            <MessageCircle className="h-3.5 w-3.5" />
                            <span>Learnings</span>
                            {notes.length > 0 && (
                                <span className={`tabular-nums px-1.5 h-4 inline-flex items-center justify-center rounded-full text-[10px] font-bold ${notesOpen ? "bg-[#2EB4AC] text-white" : "bg-slate-200 text-slate-600"}`}>
                                    {notes.length}
                                </span>
                            )}
                        </button>

                        {isOwner && (
                            <button
                                onClick={onAssign}
                                data-testid={`assign-resource-${r.id}`}
                                title="Assign to teammates"
                                className="inline-flex items-center gap-1.5 h-7 px-2 rounded-md text-xs font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                            >
                                <UserPlus className="h-3.5 w-3.5" />
                                <span className="hidden sm:inline">{(r.assigned_to?.length || 0) > 0 ? `Assigned · ${r.assigned_to.length}` : "Assign"}</span>
                                <span className="sm:hidden">{(r.assigned_to?.length || 0) > 0 ? r.assigned_to.length : ""}</span>
                            </button>
                        )}
                        {isOwner && (
                            <ReceiptsButton resourceId={r.id} assignedCount={r.assigned_to?.length || 0} />
                        )}
                        {/* "Add to collection" — a lightweight way to move a
                             resource into a department collection. Anyone
                             who can see the resource can trigger this; the
                             backend gates the actual move by department. */}
                        <button
                            onClick={() => setShowMove(true)}
                            data-testid={`move-resource-${r.id}`}
                            title="Add to another collection"
                            className="inline-flex items-center gap-1.5 h-7 px-2 rounded-md text-xs font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                        >
                            <FolderInput className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">Add to…</span>
                        </button>
                        {(r.assigned_to?.length || 0) > 0 && allUsers && (
                            <div className="flex -space-x-1.5" data-testid={`assignee-stack-${r.id}`}>
                                <Expandable
                                    items={r.assigned_to}
                                    limit={4}
                                    moreLabel={(n) => `+${n}`}
                                    className="h-5 px-1.5 rounded-full bg-slate-100 text-slate-600 border border-white text-[9px] font-semibold inline-flex items-center justify-center hover:bg-teal-50 hover:text-[#25908A]"
                                    testid={`assignee-more-${r.id}`}
                                    render={(uid) => {
                                    const u = (allUsers || []).find((x) => x.user_id === uid);
                                    const initial = (u?.name || u?.email || "?").charAt(0).toUpperCase();
                                    return (
                                        <span
                                            key={uid}
                                            title={u?.name || u?.email || uid}
                                            className="h-5 w-5 rounded-full bg-teal-50 text-[#25908A] border border-white text-[9px] font-bold inline-flex items-center justify-center"
                                        >
                                            {initial}
                                        </span>
                                    );
                                }}
                                />
                            </div>
                        )}
                        <div className="ml-auto">
                            <ProgressTracker
                                rid={r.id}
                                current={r.my_status || "not_started"}
                                onSet={onStatus}
                            />
                        </div>
                    </div>
                </div>
            </div>
            </div>
            {notesOpen && (
                <UserNotesPanel notes={notes} onAdd={addNote} onEdit={editNote} onDelete={deleteNote} resourceId={r.id} />
            )}
            {showMove && (
                <MoveResourceDialog
                    resource={r}
                    onClose={() => setShowMove(false)}
                    onMoved={(payload) => {
                        setShowMove(false);
                        onMoved?.(payload);
                    }}
                />
            )}
        </div>
    );
};

// ---------- Interactive 3-step progress tracker ----------
// A clickable milestone bar that replaces the older dropdown. Each milestone
// has its own glyph (clock for in-progress, check for completed, dot for not-
// started) so users get a status read at-a-glance, even without color.
const HAPPY_PATH = [
    { v: "not_started", label: "Not started", Icon: Circle },
    { v: "in_progress", label: "In progress", Icon: Activity },
    { v: "completed", label: "Completed", Icon: Check },
];

const ProgressTracker = ({ rid, current, onSet }) => {
    const isSkipped = current === "skipped";
    const idx = HAPPY_PATH.findIndex((s) => s.v === current);
    const safeIdx = idx < 0 ? 0 : idx;
    const pct = isSkipped ? 0 : (safeIdx / (HAPPY_PATH.length - 1)) * 100;
    const ActiveIcon = isSkipped ? SkipForward : HAPPY_PATH[safeIdx].Icon;

    return (
        <div className="flex items-center gap-3" data-testid={`progress-tracker-${rid}`}>
            <div className={`relative flex items-center ${isSkipped ? "opacity-40" : ""}`}>
                {/* Track */}
                <div className="h-1.5 w-32 bg-slate-200 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-gradient-to-r from-[#2EB4AC] to-[#25908A] transition-[width] duration-500 ease-out"
                        style={{ width: `${pct}%` }}
                    />
                </div>
                {/* Milestone glyphs sitting on the track */}
                <div className="absolute inset-0 flex items-center justify-between -mx-1.5">
                    {HAPPY_PATH.map((opt, i) => {
                        const filled = i <= safeIdx && !isSkipped;
                        const isCurrent = i === safeIdx && !isSkipped;
                        const Icon = opt.Icon;
                        return (
                            <button
                                key={opt.v}
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onSet(opt.v); }}
                                data-testid={`progress-step-${rid}-${opt.v}`}
                                title={opt.label}
                                aria-label={`Mark as ${opt.label}`}
                                className={`group h-6 w-6 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${
                                    filled
                                        ? "bg-[#2EB4AC] border-[#2EB4AC] text-white"
                                        : "bg-white border-slate-300 text-slate-400 hover:border-[#2EB4AC] hover:text-[#2EB4AC] hover:scale-110"
                                } ${isCurrent ? "ring-2 ring-offset-1 ring-[#2EB4AC]/40 scale-110 shadow-sm" : ""}`}
                            >
                                <Icon className="h-3 w-3" strokeWidth={2.5} />
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Active label, glyph + Skip toggle */}
            <div className="flex flex-col items-start min-w-[78px]">
                <span className={`text-[11px] font-medium tabular-nums tracking-tight inline-flex items-center gap-1 ${
                    isSkipped ? "text-slate-400 line-through" : "text-slate-700"
                }`}>
                    <ActiveIcon className="h-3 w-3" />
                    {isSkipped ? "Skipped" : HAPPY_PATH[safeIdx].label}
                </span>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onSet(isSkipped ? "not_started" : "skipped"); }}
                    data-testid={`progress-skip-${rid}`}
                    className="text-[10px] text-slate-400 hover:text-slate-700 underline-offset-2 hover:underline transition-colors"
                >
                    {isSkipped ? "Unskip" : "Skip this"}
                </button>
            </div>
        </div>
    );
};

// Community-style "Learnings" panel — anyone on the team can post their
// takeaways/links for a resource; everyone can read them. Each entry shows
// author + relative timestamp; edit/delete are gated to the original author.
const initialsOf = (name = "") => {
    const parts = String(name).trim().split(/\s+/).slice(0, 2);
    return parts.map((p) => p[0] || "").join("").toUpperCase() || "·";
};

const relativeTimeAgo = (iso) => {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return "";
    const diff = Math.max(0, Date.now() - t);
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

// Deterministic teal-family tint per user_id so the same teammate gets
// the same avatar colour across renders without needing extra state.
const _avatarTints = [
    "from-teal-500 to-emerald-600",
    "from-sky-500 to-indigo-600",
    "from-violet-500 to-fuchsia-600",
    "from-amber-500 to-orange-600",
    "from-rose-500 to-pink-600",
    "from-cyan-500 to-blue-600",
];
const tintForUid = (id) => {
    const s = String(id || "");
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
    return _avatarTints[Math.abs(h) % _avatarTints.length];
};

const NoteItem = ({ n, onEdit, onDelete }) => {
    const [editing, setEditing] = useState(false);
    const [draftText, setDraftText] = useState(n.text || "");
    const [draftUrl, setDraftUrl] = useState(n.url || "");
    const [saving, setSaving] = useState(false);

    const wasEdited = n.updated_at && n.created_at && n.updated_at !== n.created_at;

    const startEdit = () => {
        setDraftText(n.text || "");
        setDraftUrl(n.url || "");
        setEditing(true);
    };
    const cancelEdit = () => {
        setEditing(false);
        setDraftText(n.text || "");
        setDraftUrl(n.url || "");
    };
    const saveEdit = async () => {
        const t = draftText.trim();
        const u = (draftUrl || "").trim();
        const url = u && !/^https?:\/\//i.test(u) ? `https://${u}` : u;
        if (!t && !url) { toast.error("A learning can't be empty."); return; }
        setSaving(true);
        const ok = await onEdit(n.id, { text: t, url });
        setSaving(false);
        if (ok) setEditing(false);
    };

    return (
        <li className="group bg-white border border-slate-200 hover:border-slate-300 rounded-lg p-3 flex items-start gap-3 transition-colors" data-testid={`user-note-${n.id}`}>
            {/* Author avatar — deterministic tint */}
            <span
                title={n.author_name}
                className={`h-8 w-8 rounded-full bg-gradient-to-br ${tintForUid(n.user_id)} text-white text-[11px] font-bold inline-flex items-center justify-center flex-shrink-0 shadow-sm ring-2 ring-white`}
            >
                {initialsOf(n.author_name || "·")}
            </span>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold text-slate-900">{n.author_name || "Someone"}</span>
                    <span className="text-[11px] text-slate-400" title={n.created_at}>{relativeTimeAgo(n.created_at)}</span>
                    {wasEdited && <span className="text-[10px] text-slate-400 italic" title={`edited ${n.updated_at}`}>· edited</span>}
                    {n.is_mine && !editing && (
                        <span className="ml-auto inline-flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                            <button
                                onClick={startEdit}
                                aria-label="Edit"
                                data-testid={`edit-user-note-${n.id}`}
                                className="h-6 w-6 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-900 inline-flex items-center justify-center"
                            >
                                <Pencil className="h-3 w-3" />
                            </button>
                            <button
                                onClick={() => onDelete(n.id)}
                                aria-label="Delete"
                                data-testid={`delete-user-note-${n.id}`}
                                className="h-6 w-6 rounded hover:bg-rose-50 text-slate-500 hover:text-rose-600 inline-flex items-center justify-center"
                            >
                                <Trash2 className="h-3 w-3" />
                            </button>
                        </span>
                    )}
                </div>

                {editing ? (
                    <div className="mt-2 space-y-2">
                        <textarea
                            value={draftText}
                            onChange={(e) => setDraftText(e.target.value)}
                            rows={3}
                            data-testid={`edit-user-note-text-${n.id}`}
                            className="w-full p-2 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-[#2EB4AC]/30 focus:border-[#2EB4AC] outline-none resize-none"
                        />
                        <input
                            value={draftUrl}
                            onChange={(e) => setDraftUrl(e.target.value)}
                            placeholder="https://… (optional link)"
                            data-testid={`edit-user-note-url-${n.id}`}
                            className="h-8 w-full px-2 text-xs border border-slate-200 rounded-md focus:ring-2 focus:ring-[#2EB4AC]/30 focus:border-[#2EB4AC] outline-none"
                        />
                        <div className="flex items-center gap-2">
                            <button
                                onClick={saveEdit}
                                disabled={saving}
                                data-testid={`save-edit-user-note-${n.id}`}
                                className="h-7 px-3 rounded-md bg-[#2EB4AC] hover:bg-[#25908A] text-white text-[11px] font-bold disabled:opacity-50 inline-flex items-center gap-1.5"
                            >
                                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                                Save
                            </button>
                            <button
                                onClick={cancelEdit}
                                disabled={saving}
                                className="h-7 px-3 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-600 text-[11px] font-semibold"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        {n.text && <div className="mt-1 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{n.text}</div>}
                        {n.url && (
                            <a
                                href={n.url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1.5 mt-2 px-2.5 h-7 rounded-md bg-teal-50/70 hover:bg-teal-50 text-[#1F7F79] text-[11px] font-medium border border-teal-100 max-w-full"
                            >
                                <Link2 className="h-3 w-3 flex-shrink-0" />
                                <span className="truncate">{n.url.replace(/^https?:\/\//, "")}</span>
                                <ExternalLink className="h-2.5 w-2.5 flex-shrink-0 opacity-60" />
                            </a>
                        )}
                    </>
                )}
            </div>
        </li>
    );
};

const UserNotesPanel = ({ notes, onAdd, onEdit, onDelete, resourceId }) => {
    const [text, setText] = useState("");
    const [url, setUrl] = useState("");
    const [showLink, setShowLink] = useState(false);
    const [adding, setAdding] = useState(false);

    // Auto-prepend protocol so users can paste "example.com/x" without errors.
    const normaliseUrl = (raw) => {
        const v = (raw || "").trim();
        if (!v) return "";
        if (/^https?:\/\//i.test(v)) return v;
        return `https://${v}`;
    };

    const submit = async () => {
        const t = text.trim();
        const u = normaliseUrl(url);
        if (!t && !u) {
            toast.error("Add a learning or a link.");
            return;
        }
        setAdding(true);
        try {
            await onAdd({ text: t, url: u });
            setText("");
            setUrl("");
            setShowLink(false);
        } finally { setAdding(false); }
    };

    // Cmd/Ctrl+Enter from anywhere in the panel commits the note.
    const onKey = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            submit();
        }
    };

    const canSubmit = !!(text.trim() || url.trim());

    return (
        <div
            className="border-t border-slate-200 bg-gradient-to-b from-teal-50/30 to-white"
            data-testid={`user-notes-panel-${resourceId}`}
            onKeyDown={onKey}
        >
            {/* Header — soft brand chip; the focus should be on the conversation */}
            <div className="flex items-center justify-between px-4 pt-3 pb-2">
                <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                    <Paperclip className="h-3.5 w-3.5 text-[#2EB4AC]" />
                    Team learnings
                    {notes.length > 0 && (
                        <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#2EB4AC]/10 text-[#1F7F79] text-[10px] font-bold tabular-nums">
                            {notes.length}
                        </span>
                    )}
                </div>
                <span className="text-[10px] text-slate-400 italic">Visible to everyone</span>
            </div>

            {/* Conversation thread */}
            <div className="px-4 pb-3 space-y-2">
                {notes.length === 0 ? (
                    <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-slate-50/80 text-xs text-slate-500">
                        <Sparkles className="h-3.5 w-3.5 text-[#2EB4AC] flex-shrink-0" />
                        <span>Be the first to share a takeaway, counterpoint, or related link.</span>
                    </div>
                ) : (
                    <ul className="space-y-2">
                        {notes.map((n) => (
                            <NoteItem key={n.id} n={n} onEdit={onEdit} onDelete={onDelete} />
                        ))}
                    </ul>
                )}
            </div>

            {/* Composer — single growing textarea, "+ Add link" reveals the URL field inline */}
            <div className="px-4 pb-4">
                <div className="bg-white border border-slate-200 rounded-xl shadow-sm focus-within:border-[#2EB4AC] focus-within:ring-2 focus-within:ring-[#2EB4AC]/15 transition-all overflow-hidden">
                    <textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="Share what you learnt…"
                        rows={2}
                        data-testid={`user-note-text-${resourceId}`}
                        className="w-full px-3.5 pt-3 pb-1 text-sm text-slate-800 placeholder:text-slate-400 bg-transparent border-0 focus:ring-0 outline-none resize-none"
                    />
                    {showLink && (
                        <div className="px-3.5 pb-1">
                            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
                                <Link2 className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                                <input
                                    value={url}
                                    autoFocus
                                    onChange={(e) => setUrl(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") { e.preventDefault(); submit(); }
                                    }}
                                    placeholder="https://… (optional link)"
                                    data-testid={`user-note-url-${resourceId}`}
                                    className="flex-1 bg-transparent text-xs text-slate-800 placeholder:text-slate-400 border-0 focus:ring-0 outline-none"
                                />
                                <button
                                    onClick={() => { setUrl(""); setShowLink(false); }}
                                    aria-label="Remove link"
                                    className="text-slate-400 hover:text-rose-500"
                                >
                                    <X className="h-3 w-3" />
                                </button>
                            </div>
                        </div>
                    )}
                    <div className="flex items-center justify-between px-3 py-2 border-t border-slate-100">
                        <div className="flex items-center gap-1">
                            {!showLink && (
                                <button
                                    onClick={() => setShowLink(true)}
                                    data-testid={`user-note-add-link-${resourceId}`}
                                    title="Attach a link"
                                    className="inline-flex items-center gap-1 px-2 h-7 rounded-md text-[11px] font-medium text-slate-500 hover:text-[#1F7F79] hover:bg-teal-50 transition-colors"
                                >
                                    <Link2 className="h-3 w-3" /> Add link
                                </button>
                            )}
                            <span className="text-[10px] text-slate-400 ml-1 hidden sm:inline-flex items-center">
                                <kbd className="px-1 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200 text-[9px]">⌘</kbd>
                                <span className="mx-0.5">+</span>
                                <kbd className="px-1 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200 text-[9px]">↵</kbd>
                                <span className="ml-1">to post</span>
                            </span>
                        </div>
                        <button
                            onClick={submit}
                            disabled={adding || !canSubmit}
                            data-testid={`add-user-note-${resourceId}`}
                            className="h-8 px-3.5 rounded-lg bg-[#2EB4AC] hover:bg-[#25908A] text-white text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5 transition-all shadow-sm hover:shadow disabled:shadow-none"
                        >
                            {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                            Post
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ---------- MoveResourceDialog ----------
// Lets a teammate add an existing single resource to another collection.
// The list of available targets is fetched once per open, grouped by
// "Your collections" and "In your department" so the picker feels less
// like a mono-list. A search box on top filters both groups in real time.
const MoveResourceDialog = ({ resource, onClose, onMoved }) => {
    const { user } = useAuth();
    const [collections, setCollections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [movingId, setMovingId] = useState("");
    // "move" (default, existing behaviour — same id travels) or "copy"
    // (create a new resource, current one stays where it is, unassigned).
    const [mode, setMode] = useState("move");

    // ESC to close — modal-standard behaviour.
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await api.get("/learning/collections");
                if (!alive) return;
                setCollections(Array.isArray(r.data) ? r.data : []);
            } catch (e) {
                if (alive) toast.error(e?.response?.data?.detail || "Couldn't load collections");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    const uid = user?.user_id;
    const myDept = user?.department_id;
    const isAdmin = user?.role === "super_admin";

    // Filter out the resource's current collection so the user can't
    // "move" it to where it already is (backend rejects it anyway).
    const eligible = useMemo(() => {
        const q = query.trim().toLowerCase();
        return collections
            .filter((c) => c.id !== resource.collection_id)
            .filter((c) => {
                // Permission gate mirrors the backend: mine OR same-dept OR admin.
                if (isAdmin) return true;
                if (c.owner_id === uid) return true;
                if (myDept && c.owner_department_id === myDept) return true;
                return false;
            })
            .filter((c) => {
                if (!q) return true;
                return (
                    (c.name || "").toLowerCase().includes(q) ||
                    (c.description || "").toLowerCase().includes(q) ||
                    (c.owner_name || "").toLowerCase().includes(q)
                );
            });
    }, [collections, query, resource.collection_id, uid, myDept, isAdmin]);

    const mine = eligible.filter((c) => c.owner_id === uid);
    const departmentPeers = eligible.filter((c) => c.owner_id !== uid);

    const doMove = async (targetId) => {
        setMovingId(targetId);
        try {
            const endpoint = mode === "copy" ? "copy" : "move";
            const r = await api.post(`/learning/resources/${resource.id}/${endpoint}`, { collection_id: targetId });
            onMoved?.({ ...r.data, mode });
        } catch (e) {
            toast.error(e?.response?.data?.detail || (mode === "copy" ? "Copy failed" : "Move failed"));
        } finally {
            setMovingId("");
        }
    };

    const renderRow = (c) => (
        <button
            key={c.id}
            onClick={() => doMove(c.id)}
            disabled={!!movingId}
            data-testid={`move-target-${c.id}`}
            className="lh-move-row"
        >
            <span className="lh-move-avatar">
                {(c.name || "?").charAt(0).toUpperCase()}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#0E2027", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</div>
                <div style={{ fontSize: 11, color: "#5B6B70", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {c.owner_id === uid ? "You own this" : c.owner_name ? `by ${c.owner_name}` : ""}
                    {c.owner_department_name ? ` · ${c.owner_department_name}` : ""}
                    {typeof c.resource_count === "number" ? ` · ${c.resource_count} resource${c.resource_count === 1 ? "" : "s"}` : ""}
                </div>
            </div>
            {movingId === c.id ? (
                <Loader2 className="h-4 w-4 animate-spin" style={{ color: "#0C7C6E" }} />
            ) : (
                <ChevronRight className="h-4 w-4" style={{ color: "#C3CBC8" }} />
            )}
        </button>
    );

    return (
        <Modal title={mode === "copy" ? "Copy to collection" : "Move to collection"} onClose={onClose}>
            <div>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".08em", fontWeight: 700, color: "#5B6B70", marginBottom: 4 }}>Resource</div>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: "#0E2027", marginBottom: 14 }} title={resource.name}>{resource.name}</div>

                <Field label="Mode">
                    <div className="lh-chip-select" data-testid="move-mode-toggle">
                        {[
                            { key: "move", label: "→ Move" },
                            { key: "copy", label: "⧉ Copy" },
                        ].map((opt) => (
                            <button
                                key={opt.key}
                                type="button"
                                onClick={() => setMode(opt.key)}
                                data-testid={`move-mode-${opt.key}`}
                                className={`lh-chip ${mode === opt.key ? "selected" : ""}`}
                            >{opt.label}</button>
                        ))}
                    </div>
                    <p className="lh-hint">
                        {mode === "copy"
                            ? "A fresh copy will be added to the target collection. Original stays put. Copy starts unassigned."
                            : "Resource moves out of its current collection into the target."}
                    </p>
                </Field>

                <Field label="Search">
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        autoFocus
                        placeholder="Filter collections…"
                        data-testid="move-target-search"
                        className="lh-form-control"
                    />
                </Field>

                <div style={{ maxHeight: 420, overflowY: "auto", marginTop: 4 }}>
                    {loading ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "8px 0" }}>
                            {[0, 1, 2].map((i) => (
                                <div key={i} style={{ height: 54, borderRadius: 10, background: "#F5F8F7", animation: "lhPulse 1.2s ease-in-out infinite" }} />
                            ))}
                        </div>
                    ) : eligible.length === 0 ? (
                        <div className="empty-state" style={{ marginTop: 8 }}>
                            <div className="empty-icon">◆</div>
                            <p>Nothing to add to yet. You can add to collections you own or any collection in your department.</p>
                        </div>
                    ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                            {mine.length > 0 && (
                                <div>
                                    <div className="section-label">Your collections</div>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                        {mine.map(renderRow)}
                                    </div>
                                </div>
                            )}
                            {departmentPeers.length > 0 && (
                                <div>
                                    <div className="section-label">In your department</div>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                        {departmentPeers.map(renderRow)}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};


const AddResourceDialog = ({ cid, existing, onClose, onAdded, onUpdated, collections: collectionsProp, defaultFolderId }) => {
    const { user } = useAuth();
    const isEdit = !!existing;
    const standaloneAllowed = !cid && !isEdit;
    const [url, setUrl] = useState(existing?.url || "");
    const [name, setName] = useState(existing?.name || "");
    const [description, setDescription] = useState(existing?.description || "");
    const [type, setType] = useState(existing?.type || "");
    const [level, setLevel] = useState(existing?.level || "");
    const [preview, setPreview] = useState(existing?.preview || null);
    const [previewing, setPreviewing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [aiCleaning, setAiCleaning] = useState(false);
    const [autoClean, setAutoClean] = useState(!isEdit);
    const [target, setTarget] = useState(cid ? "existing" : "existing");
    const [targetCid, setTargetCid] = useState(cid || "");
    const targetCollections = (collectionsProp || []).filter((c) => c.can_manage || c.owner_id === user?.user_id);
    // NEW — content mode picker: "url" (default, existing flow) or "file"
    // (uploads to object storage and creates a resource pointing at it).
    const [mode, setMode] = useState(existing?.storage_path ? "file" : "url");
    const [uploadedFile, setUploadedFile] = useState(null);  // {storage_path, original_filename, content_type, size_bytes}
    const [uploading, setUploading] = useState(false);
    const fileInputRef = React.useRef(null);
    // NEW — folder picker: folders available in the currently-selected collection.
    const [folderId, setFolderId] = useState(existing?.folder_id || defaultFolderId || "");
    const [folders, setFolders] = useState([]);
    // Load folders whenever the target collection changes.
    React.useEffect(() => {
        const activeCid = cid || (target === "existing" ? targetCid : "");
        if (!activeCid) { setFolders([]); return; }
        (async () => {
            try {
                const r = await api.get(`/learning/collections/${activeCid}/folders`);
                setFolders(r.data || []);
            } catch { setFolders([]); }
        })();
    }, [cid, target, targetCid]);

    const fetchPreview = async () => {
        const u = url.trim();
        if (!u || !u.startsWith("http")) { toast.error("Enter a valid http(s) URL"); return; }
        setPreviewing(true);
        try {
            const r = await api.post("/learning/preview", { url: u });
            setPreview(r.data);
            if (!name) setName(r.data?.title || u);
            if (!description) setDescription(r.data?.description || "");
            if (!type) setType(r.data?.type || "article");
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not fetch preview"); }
        finally { setPreviewing(false); }
    };

    // Calls the Groq endpoint to normalise the name into the canonical
    // `<Type>: <Topic> — <Source>` format. Used by the manual button AND
    // by the auto-clean flow on submit.
    const aiCleanup = async ({ silent = false } = {}) => {
        const raw = name.trim() || preview?.title || url.trim();
        if (!raw) {
            if (!silent) toast.error("Type or fetch a name first");
            return null;
        }
        setAiCleaning(true);
        try {
            const r = await api.post("/ai/rename-resource", {
                raw_name: raw,
                url: url.trim() || null,
                type: type || preview?.type || null,
                description: description.trim() || null,
            });
            const suggested = (r.data?.suggested_name || "").trim();
            if (suggested) {
                setName(suggested);
                if (!silent) toast.success("Cleaned up with AI");
                return suggested;
            }
            return null;
        } catch (e) {
            if (!silent) toast.error(e?.response?.data?.detail || "AI rename failed");
            return null;
        } finally { setAiCleaning(false); }
    };

    const pickFile = () => fileInputRef.current?.click();

    const onFileChosen = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            // Deliberately NOT setting Content-Type here — axios auto-detects
            // FormData and emits `multipart/form-data; boundary=…`. Setting
            // it manually strips the boundary and the server drops the
            // request (the reason uploads were hanging in production).
            const r = await api.post("/learning/upload", fd);
            setUploadedFile(r.data);
            if (!name) setName(f.name);
            if (!type) setType("file");
            toast.success(`Uploaded ${f.name}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally { setUploading(false); }
    };

    const submit = async () => {
        const u = url.trim();
        const n = name.trim();
        // Name is always mandatory.
        if (!n) {
            toast.error("Name is required.");
            return;
        }
        // Validation depends on the mode — File mode requires an uploaded file.
        if (mode === "file") {
            if (!uploadedFile) { toast.error("Please upload a file first"); return; }
        } else {
            if (u && !u.startsWith("http")) {
                toast.error("URL must start with http:// or https://");
                return;
            }
        }
        setSaving(true);
        try {
            const payload = {
                url: mode === "file" ? "" : u,
                name: n,
                description: description.trim(),
                type: type || (mode === "file" ? "file" : (preview?.type || "")),
                level: level || "",
                folder_id: folderId || "",
            };
            if (mode === "file" && uploadedFile) {
                payload.storage_path = uploadedFile.storage_path;
                payload.file_original_name = uploadedFile.original_filename;
                payload.content_type = uploadedFile.content_type;
                payload.size_bytes = uploadedFile.size_bytes;
            }
            if (isEdit) {
                // Server returns the fresh, fully-decorated resource doc.
                // Pipe it back to the parent for an INSTANT optimistic
                // update so the rename is visible even if the follow-up
                // `load()` refetch hiccups (slow network, transient 5xx,
                // mid-flight cancellation) — used to be a source of
                // "resource vanished from the collection" reports.
                const r = await api.put(`/learning/resources/${existing.id}`, payload);
                toast.success("Resource updated");
                onUpdated?.(r.data);
                onAdded();
                return;
            }
            // Resolve target collection
            let resolvedCid = targetCid;
            if (target === "standalone") {
                // Auto-create / reuse a personal "Quick captures" collection.
                const slug = "Quick captures";
                const existingQuick = (collectionsProp || []).find(
                    (c) => c.owner_id === user?.user_id && c.name === slug
                );
                if (existingQuick) {
                    resolvedCid = existingQuick.id;
                } else {
                    const cr = await api.post("/learning/collections", {
                        name: slug,
                        description: "Standalone resources you saved on the fly.",
                    });
                    resolvedCid = cr.data.id;
                }
            }
            if (!resolvedCid) { toast.error("Pick a collection"); setSaving(false); return; }
            await api.post(`/learning/collections/${resolvedCid}/resources`, { ...payload, preview: preview || null });
            toast.success("Resource added");
            onAdded();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setSaving(false); }
    };

    const canSubmit = name.trim() && (mode === "file" ? (uploadedFile && !saving) : !saving);

    return (
        <Modal title={isEdit ? "Edit resource" : "Add resource"} onClose={onClose}>
            <div>
                {/* Target collection selector — only when adding (not editing)
                    and no collection was pre-selected from the parent. */}
                {!isEdit && !cid && (
                    <Field label="Add to">
                        <div className="lh-chip-select" style={{ marginBottom: 8 }}>
                            {[
                                { key: "existing", label: "Existing collection" },
                                standaloneAllowed ? { key: "standalone", label: "Standalone" } : null,
                            ].filter(Boolean).map((opt) => (
                                <button
                                    key={opt.key}
                                    type="button"
                                    onClick={() => setTarget(opt.key)}
                                    data-testid={`add-target-${opt.key}`}
                                    className={`lh-chip ${target === opt.key ? "selected" : ""}`}
                                >{opt.label}</button>
                            ))}
                        </div>
                        {target === "existing" && (
                            <select
                                data-testid="add-target-cid"
                                value={targetCid}
                                onChange={(e) => setTargetCid(e.target.value)}
                                className="lh-form-control"
                            >
                                <option value="">— Pick a collection you own —</option>
                                {targetCollections.map((c) => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        )}
                        {target === "standalone" && (
                            <p className="lh-hint">
                                Will land in your personal <b>Quick captures</b> collection (auto-created on first use). For a brand-new themed collection, use the <b>New collection</b> button on the Hub header.
                            </p>
                        )}
                    </Field>
                )}

                {/* Mode picker — URL (existing flow) or File upload. */}
                {!isEdit && (
                    <Field label="Source">
                        <div className="lh-chip-select">
                            {[
                                { key: "url",  label: "🔗 URL" },
                                { key: "file", label: "⇧ Upload file" },
                            ].map((opt) => (
                                <button
                                    key={opt.key}
                                    type="button"
                                    onClick={() => setMode(opt.key)}
                                    data-testid={`resource-mode-${opt.key}`}
                                    className={`lh-chip ${mode === opt.key ? "selected" : ""}`}
                                >{opt.label}</button>
                            ))}
                        </div>
                    </Field>
                )}

                {/* Folder picker — only when a collection has been chosen and has folders. */}
                {folders.length > 0 && (
                    <Field label="Folder (optional)">
                        <select
                            value={folderId}
                            onChange={(e) => setFolderId(e.target.value)}
                            data-testid="resource-folder-select"
                            className="lh-form-control"
                        >
                            <option value="">Root of collection</option>
                            {folders.map((f) => (
                                <option key={f.id} value={f.id}>{f.name} ({f.resource_count} items)</option>
                            ))}
                        </select>
                    </Field>
                )}

                {mode === "file" ? (
                    <Field label="File">
                        <input ref={fileInputRef} type="file" onChange={onFileChosen} className="hidden" data-testid="resource-file-input" />
                        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                            <button
                                type="button"
                                onClick={pickFile}
                                disabled={uploading}
                                data-testid="resource-file-picker-btn"
                                className="lh-btn lh-btn-ghost"
                                style={{ padding: "9px 14px" }}
                            >
                                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadIcon className="h-4 w-4" />}
                                {uploadedFile ? "Replace file" : "Choose a file"}
                            </button>
                            {uploadedFile && (
                                <div
                                    style={{ fontSize: 12, background: "#E7F3F1", border: "1px solid #D1E6E2", borderRadius: 9, padding: "6px 10px", color: "#08443C" }}
                                    data-testid="resource-file-preview"
                                >
                                    <span style={{ fontWeight: 700 }}>{uploadedFile.original_filename}</span>
                                    <span style={{ marginLeft: 6, color: "#5B6B70" }}>({Math.max(1, Math.round((uploadedFile.size_bytes || 0) / 1024))} KB)</span>
                                </div>
                            )}
                        </div>
                        <p className="lh-hint">Any format up to 100 MB. Uploaded files are stored securely and served through the app.</p>
                    </Field>
                ) : (
                    <Field label="URL (optional)">
                        <div style={{ display: "flex", gap: 8 }}>
                            <input
                                data-testid="resource-url-input"
                                value={url}
                                onChange={(e) => setUrl(e.target.value)}
                                onBlur={() => url && !preview && fetchPreview()}
                                placeholder="https://… (or leave blank for a note)"
                                className="lh-form-control"
                                style={{ flex: 1 }}
                            />
                            <button
                                onClick={fetchPreview}
                                disabled={previewing || !url}
                                data-testid="fetch-preview-btn"
                                className="lh-btn lh-btn-ghost"
                                style={{ padding: "9px 14px", whiteSpace: "nowrap" }}
                            >
                                {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Fetch preview"}
                            </button>
                        </div>
                    </Field>
                )}

                {preview && (preview.title || preview.image) && (
                    <div
                        style={{ border: "1.5px dashed #0C7C6E", background: "#E7F3F1", borderRadius: 12, padding: 12, display: "flex", gap: 12, marginBottom: 15 }}
                    >
                        {preview.image ? (
                            <img src={preview.image} alt="" style={{ height: 78, width: 120, objectFit: "cover", borderRadius: 8, flexShrink: 0 }} />
                        ) : (
                            <div style={{ height: 78, width: 120, background: "#FFFFFF", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                <Globe className="h-6 w-6" style={{ color: "#0C7C6E" }} />
                            </div>
                        )}
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".06em", fontWeight: 700, color: "#5B6B70" }}>{preview.site || "Preview"}</div>
                            <div style={{ fontFamily: "'Outfit', ui-sans-serif, system-ui, sans-serif", fontWeight: 700, fontSize: 14, color: "#0E2027", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{preview.title || url}</div>
                            {preview.description && <div style={{ fontSize: 12, color: "#5B6B70", lineHeight: 1.4, marginTop: 2, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{preview.description}</div>}
                        </div>
                    </div>
                )}

                <Field label={<>Name <span style={{ color: "#E2604F" }}>*</span></>}>
                    <input
                        data-testid="resource-name-input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="What should learners see on the card?"
                        className="lh-form-control"
                        required
                    />
                </Field>
                <Field label="Description">
                    <textarea
                        data-testid="resource-description-input"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={2}
                        className="lh-form-control"
                        placeholder="What's this about? Why does it matter for the team?"
                    />
                </Field>
                <Field label="Type">
                    <div className="lh-chip-select" data-testid="resource-type-input">
                        {[
                            { key: "",         label: "◆ Auto-detect" },
                            ...TYPES.map((t) => ({ key: t, label: `${{video:"▶",course:"▶",article:"▤",doc:"▤",podcast:"▤",book:"▤",other:"◇"}[t] || "▤"} ${TYPE_LABEL[t]}` })),
                        ].map((opt) => (
                            <button
                                key={opt.key || "auto"}
                                type="button"
                                onClick={() => setType(opt.key)}
                                data-testid={`resource-type-${opt.key || "auto"}`}
                                className={`lh-chip ${type === opt.key ? "selected" : ""}`}
                            >{opt.label}</button>
                        ))}
                    </div>
                </Field>
                <Field label="Level">
                    <div className="lh-chip-select" data-testid="resource-level-input">
                        {[
                            { key: "",             label: "— No level —" },
                            { key: "beginner",     label: "● Beginner"     },
                            { key: "intermediate", label: "●● Intermediate" },
                            { key: "advanced",     label: "●●● Advanced"    },
                        ].map((opt) => (
                            <button
                                key={opt.key || "none"}
                                type="button"
                                onClick={() => setLevel(opt.key)}
                                data-testid={`resource-level-${opt.key || "none"}`}
                                className={`lh-chip ${level === opt.key ? "selected" : ""}`}
                            >{opt.label}</button>
                        ))}
                    </div>
                    <p className="lh-hint">Shown on the resource card so learners know how deep it goes.</p>
                </Field>
            </div>
            <ModalFooter onCancel={onClose} onPrimary={submit} loading={saving} primaryLabel={isEdit ? "Save" : "Add resource"} disabled={!canSubmit} />
        </Modal>
    );
};

const AssignDialog = ({ collection, onClose, onSaved }) => {
    const { user } = useAuth();
    const { users } = useData();
    const [selected, setSelected] = useState(collection.assigned_to || []);
    const [saving, setSaving] = useState(false);

    const candidates = useMemo(() => {
        if (user?.role === "super_admin") return users.filter((u) => u.user_id !== user.user_id);
        return users.filter((u) => u.manager_id === user?.user_id);
    }, [users, user]);

    const toggle = (uid) => setSelected((s) => s.includes(uid) ? s.filter((x) => x !== uid) : [...s, uid]);

    const save = async () => {
        setSaving(true);
        try {
            await api.post(`/learning/collections/${collection.id}/assign`, { user_ids: selected });
            toast.success(`Assigned to ${selected.length} user${selected.length !== 1 ? "s" : ""}`);
            onSaved();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setSaving(false); }
    };

    return (
        <Modal title="Assign to managees" onClose={onClose}>
            {candidates.length === 0 ? (
                <div className="border-2 border-dashed border-teal-300 bg-teal-50/30 rounded-lg p-6 text-center text-sm text-slate-600">
                    You don&apos;t have any managees assigned yet. Ask a Super Admin to assign managees in the Control Panel.
                </div>
            ) : (
                <div className="max-h-80 overflow-y-auto scrollbar-thin space-y-1">
                    {candidates.map((u) => (
                        <label key={u.user_id} className="flex items-center gap-3 p-2 rounded-md hover:bg-slate-50 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={selected.includes(u.user_id)}
                                onChange={() => toggle(u.user_id)}
                                data-testid={`assign-pick-${u.user_id}`}
                                className="h-4 w-4 accent-[#2EB4AC]"
                            />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-slate-900 truncate">{u.name}</div>
                                <div className="text-xs text-slate-500 truncate">{u.email}</div>
                            </div>
                        </label>
                    ))}
                </div>
            )}
            <ModalFooter onCancel={onClose} onPrimary={save} loading={saving} primaryLabel={`Assign (${selected.length})`} disabled={candidates.length === 0} />
        </Modal>
    );
};

const AssignResourceDialog = ({ resource, collection, allUsers, onClose, onSaved }) => {
    const { user: currentUser } = useAuth();
    const initial = resource.assigned_to || [];
    const [selected, setSelected] = useState(initial);
    const [saving, setSaving] = useState(false);
    const [query, setQuery] = useState("");

    // A manager may only assign their direct managees; super admins and the
    // collection owner can target anyone. This mirrors the backend rule.
    const isOwner = currentUser?.user_id === collection.owner_id;
    const isSuper = currentUser?.role === "super_admin";
    const canTargetAnyone = isSuper || isOwner;

    const pool = useMemo(() => {
        let people = allUsers || [];
        if (!canTargetAnyone && currentUser?.role === "manager") {
            people = people.filter((u) => u.manager_id === currentUser.user_id);
        }
        // Exclude inactive users
        people = people.filter((u) => u.active !== false);
        const t = query.trim().toLowerCase();
        if (t) {
            people = people.filter(
                (u) =>
                    (u.name || "").toLowerCase().includes(t) ||
                    (u.email || "").toLowerCase().includes(t) ||
                    (u.position || "").toLowerCase().includes(t) ||
                    (u.sub_department || "").toLowerCase().includes(t)
            );
        }
        return people;
    }, [allUsers, query, canTargetAnyone, currentUser]);

    // Mark who's already assigned to the collection so the assigner sees that context
    const collectionMemberIds = useMemo(() => new Set(collection.assigned_to || []), [collection]);

    const toggle = (uid) => setSelected((s) => s.includes(uid) ? s.filter((x) => x !== uid) : [...s, uid]);
    const everyone = selected.length === 0;

    const save = async () => {
        setSaving(true);
        try {
            await api.post(`/learning/resources/${resource.id}/assign`, { user_ids: selected });
            toast.success(everyone ? "Open to every collection member" : `Assigned to ${selected.length} user${selected.length !== 1 ? "s" : ""}`);
            onSaved();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setSaving(false); }
    };

    return (
        <Modal title={`Assign "${resource.name}"`} onClose={onClose}>
            <div className="space-y-4">
                <p className="text-sm text-slate-600">
                    Pick anyone in the org to give them access to this resource.
                    {" "}<span className="font-semibold">Leaving the list empty</span> keeps it open to every user already assigned to the parent collection.
                    {!canTargetAnyone && currentUser?.role === "manager" && (
                        <span className="block mt-1 text-xs text-slate-500 italic">Managers can only assign to their direct managees.</span>
                    )}
                </p>

                <div className="relative">
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search by name, email, position…"
                        data-testid="assign-resource-search"
                        className="w-full h-10 pl-9 pr-3 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-[#2EB4AC] focus:border-[#2EB4AC] outline-none"
                    />
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z" /></svg>
                </div>

                <div className="flex items-center justify-between text-xs">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-medium ${everyone ? "bg-teal-50 text-[#25908A] border border-teal-100" : "bg-slate-100 text-slate-700"}`}>
                        {everyone ? "Open to every collection member" : `${selected.length} selected`}
                    </span>
                    <button
                        onClick={() => setSelected(everyone ? pool.map((u) => u.user_id) : [])}
                        data-testid="assign-resource-toggle-all"
                        className="text-slate-500 hover:text-slate-900 font-medium"
                    >
                        {everyone ? "Pick all shown" : "Clear selection"}
                    </button>
                </div>

                <div className="max-h-80 overflow-y-auto scrollbar-thin space-y-1 border border-slate-100 rounded-lg p-1">
                    {pool.length === 0 ? (
                        <div className="py-6 text-center text-sm text-slate-500">
                            {query.trim() ? "No people match that search." : "No one to assign."}
                        </div>
                    ) : (
                        pool.map((u) => {
                            const onCollection = collectionMemberIds.has(u.user_id);
                            return (
                                <label key={u.user_id} className="flex items-center gap-3 p-2 rounded-md hover:bg-slate-50 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={selected.includes(u.user_id)}
                                        onChange={() => toggle(u.user_id)}
                                        data-testid={`assign-resource-pick-${u.user_id}`}
                                        className="h-4 w-4 accent-[#2EB4AC]"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-2">
                                            {u.name}
                                            {!onCollection && (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-100" title="Will gain access just to this resource">
                                                    Direct grant
                                                </span>
                                            )}
                                            {u.position && (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-slate-100 text-slate-600">
                                                    {u.position}
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-slate-500 truncate">{u.email}</div>
                                    </div>
                                </label>
                            );
                        })
                    )}
                </div>
            </div>
            <ModalFooter onCancel={onClose} onPrimary={save} loading={saving} primaryLabel="Save" />
        </Modal>
    );
};

// ---------- Shared modal ----------
const Modal = ({ title, onClose, wide, children }) => createPortal(
    <div className="lh-modal-backdrop" onClick={onClose} data-testid="lh-modal-backdrop">
        <div className={`lh-modal ${wide ? "wide" : ""}`} onClick={(e) => e.stopPropagation()}>
            <div className="lh-modal-head">
                <h3 title={title}>{title}</h3>
                <button onClick={onClose} className="lh-modal-close" aria-label="Close">✕</button>
            </div>
            <div className="lh-modal-body">
                {children}
            </div>
        </div>
    </div>,
    document.body
);

const Field = ({ label, children }) => (
    <div className="lh-field">
        <label className="lh-field-label">{label}</label>
        {children}
    </div>
);

const ModalFooter = ({ onCancel, onPrimary, loading, primaryLabel = "Save", disabled }) => (
    <div className="lh-modal-footer">
        <button onClick={onCancel} className="lh-btn lh-btn-ghost">Cancel</button>
        <button
            onClick={onPrimary}
            disabled={loading || disabled}
            data-testid="modal-primary-btn"
            className="lh-btn lh-btn-primary"
        >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {primaryLabel}
        </button>
    </div>
);

// ---------- Refine ✨ — futuristic AI cleanup button ----------
// Bright gradient pill with a soft pulsing aura, conic-gradient sheen on
// hover, and a tiny orbit of sparkles. Same prop surface as a plain
// <button> — drop-in for the old "Clean with AI" trigger.
const RefineButton = ({ onClick, loading, disabled }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        data-testid="ai-refine-btn"
        title="Rewrite as «Type: Topic — Source» using AI"
        className="group relative h-11 px-3 rounded-lg overflow-hidden text-white font-semibold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
        style={{
            background: "linear-gradient(135deg, #6d28d9 0%, #db2777 50%, #f59e0b 100%)",
            boxShadow: "0 0 0 1px rgba(255,255,255,0.12) inset, 0 8px 24px -8px rgba(124,58,237,0.55), 0 0 18px -2px rgba(219,39,119,0.45)",
        }}
    >
        {/* Pulsing aura behind the button */}
        <span
            aria-hidden
            className="absolute inset-0 -z-0 opacity-50 group-hover:opacity-80 transition-opacity pointer-events-none"
            style={{
                background:
                    "radial-gradient(circle at 30% 50%, rgba(255,200,255,0.55) 0, transparent 60%), radial-gradient(circle at 80% 60%, rgba(255,255,255,0.35) 0, transparent 55%)",
                animation: "refine-pulse 2.8s ease-in-out infinite",
            }}
        />
        {/* Conic sheen that rotates on hover */}
        <span
            aria-hidden
            className="absolute -inset-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
            style={{
                background:
                    "conic-gradient(from 0deg, transparent 0deg, rgba(255,255,255,0.55) 40deg, transparent 80deg, transparent 360deg)",
                filter: "blur(8px)",
                animation: "refine-rotate 3.2s linear infinite",
            }}
        />
        <span className="relative z-10 inline-flex items-center gap-1.5">
            {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
                <span className="relative inline-flex">
                    <Sparkles className="h-4 w-4 drop-shadow-[0_0_4px_rgba(255,255,255,0.8)]" />
                    {/* Mini orbiting twinkles */}
                    <span aria-hidden className="absolute -top-1 -right-1 h-1 w-1 rounded-full bg-white animate-ping" />
                </span>
            )}
            <span className="tracking-wide">Refine</span>
            <span className="inline-flex items-center px-1 h-4 rounded text-[9px] font-bold tracking-wider bg-white/20 backdrop-blur-sm">AI</span>
        </span>
        {/* Inline keyframes — scoped to this button via a style tag so we
            don't have to touch the global Tailwind config. */}
        <style>{`
            @keyframes refine-pulse { 0%,100%{transform:scale(1);} 50%{transform:scale(1.05);} }
            @keyframes refine-rotate { to { transform: rotate(360deg); } }
        `}</style>
    </button>
);

export default LearningHub;
